import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { COUNTRIES, validatePostalCode } from '../utils/postalCodeValidation';
import { getCurrencySymbol, formatCurrency } from '../utils/currency';
import { getQuickSelectBudgets, getMinBudget } from '../utils/budgetRanges';
import { markUnsavedChanges, clearUnsavedChanges } from '../utils/unsavedChanges';
import { BACKEND_URL } from '../utils/backendUrl';
import * as authUtils from '../utils/auth';
import ThemeDiscoveryWizard from './ThemeDiscoveryWizard';
import ModalCloseButton from './ui/ModalCloseButton';
import BillingOptionsModal from './BillingOptionsModal';
import {
  fetchBillingPackages,
  formatBillingMoney as formatBillingMoneyExact,
  getCachedBillingPackages,
  getPerCreditCopy,
  getSavingsCopy,
  createPortalSession
} from '../utils/billing';
import { pushHistoryBeforeExternalCheckout } from '../utils/appHistory';
import {
  normalizeFocusSelection,
  getPrimaryPlanningIntent,
  getPlanningIntentConfig
} from '../utils/planningIntent';
import { getConceptLabelForIntent } from '../utils/themePlanKeys';
import ScrollToTopButton from './common/ScrollToTopButton';
import ExpressPlanModal from './ExpressPlanModal';
import { trackEvent } from '../utils/telemetry';

const LAUNCH_COUNTRY = 'US';

const stripInternalThemeDirection = (value = '') => (
  String(value || '').replace(/\n{0,2}Similar-theme direction:[\s\S]*$/i, '').trim()
);

const normalizeArrayField = (value, fallback = []) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return fallback;
};

const deriveThemeModeFromInput = (input) => {
  if (!input) return 'selection';
  if (input.includes('Surprise') || input.includes('Surprise me!')) return 'surprise';
  if (input === 'Theme Mode: Browsing...' || input.startsWith('Theme:')) return 'browse';
  if (input === 'Theme Mode: Custom Concept') return 'custom';
  return 'custom';
};

const GlassModal = ({ isOpen, onClose, title, children, footer }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!isOpen || !mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative w-full max-w-xs sm:max-w-sm bg-white rounded-3xl p-6 pt-14 text-gray-900 shadow-2xl transform transition-all animate-in fade-in zoom-in-95 duration-200">
        <ModalCloseButton onClick={onClose} className="absolute right-4 top-4 h-9 px-3 text-xs" />
        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
          {children}
        </div>
        {footer}
      </div>
    </div>,
    document.body
  );
};

const STATIC_EVENT_TYPES = [
  { id: 'birthday', label: 'Birthday Party', icon: '🎂', color: 'from-pink-500 to-rose-500' },
  { id: 'baby-shower', label: 'Baby Shower', icon: '👶', color: 'from-blue-400 to-cyan-400' },
  { id: 'anniversary', label: 'Anniversary', icon: '💍', color: 'from-purple-500 to-pink-500' },
  { id: 'graduation', label: 'Graduation', icon: '🎓', color: 'from-yellow-400 to-orange-500' },
  { id: 'holiday', label: 'Holiday Party', icon: '🎄', color: 'from-green-500 to-emerald-500' },
  { id: 'dinner', label: 'Dinner Party', icon: '🍽️', color: 'from-amber-500 to-orange-500' },
  { id: 'other', label: 'Other', icon: '✨', color: 'from-gray-400 to-gray-500' }
];

const STATIC_MEAL_OPTIONS = [
  { value: 'breakfast', label: 'Breakfast', icon: '🥐', color: 'from-yellow-300 to-orange-300' },
  { value: 'brunch', label: 'Brunch', icon: '🥞', color: 'from-orange-300 to-pink-300' },
  { value: 'coffee_tea', label: 'Coffee & Tea', icon: '☕', color: 'from-amber-200 to-orange-200' },
  { value: 'lunch', label: 'Lunch', icon: '🥪', color: 'from-green-300 to-emerald-300' },
  { value: 'dinner', label: 'Main Course', icon: '🍽️', color: 'from-purple-300 to-indigo-300' },
  { value: 'appetizers', label: 'Appetizers', icon: '🥟', color: 'from-cyan-300 to-blue-300' },
  { value: 'finger_foods', label: 'Finger Foods', icon: '🍕', color: 'from-orange-200 to-red-200' },
  { value: 'snacks', label: 'Snacks', icon: '🍿', color: 'from-pink-300 to-rose-300' },
  { value: 'desserts', label: 'Desserts', icon: '🍰', color: 'from-rose-300 to-pink-300' },
  { value: 'cocktails', label: 'Drinks / Mocktails', icon: '🍹', color: 'from-blue-300 to-indigo-300' },
  { value: 'late_night_bites', label: 'Late-night bites', icon: '🍟', color: 'from-yellow-300 to-red-300' }
];

// Known standard venue type option values — used for hydration to distinguish
// saved custom strings (e.g. "apartment clubhouse") from standard option values.
const STANDARD_VENUE_TYPES = [
  'House', 'Resort', 'Restaurant', 'Banquet Hall', 'Gardens',
  'Hotel', 'Community Center', 'Park', 'Beach', 'Other'
];

const EventInputScreen = forwardRef(({ onSubmit, initialEventData, onRegisterSwipeHandlers, existingThemes, onNavigateToThemes,
  onRegenerateThemes,
  goToStep16Key,
  onSaveDraft,
  onRequestAuth,
  isAuthenticated = false,
  user = null,
  currentPlanId = null,
  selectedTheme: selectedThemeForReturn = null,
  shoppingItems: shoppingItemsForReturn = [],
  themePlansForReturn = {},
  onDirtyChange,
  sidebarCollapsed = false,
  onSavePlan,
  initialStep = null,
  onFocusChange,
  billingOverlayOnly = false,
  inlineBillingRequest = null,
  onBillingOverlayClose = null,
  saveSuccessToken = 0
}, ref) => {

  const [eventType, setEventType] = useState(() => {
    if (!initialEventData) return '';
    const standardLabels = [
      ...STATIC_EVENT_TYPES.map(t => t.id === 'other' ? 'Other' : t.label),
      'Kids Birthday'
    ];
    if (initialEventData.custom_event_type) {
      return 'Other';
    } else if (initialEventData.event_type && standardLabels.includes(initialEventData.event_type)) {
      return initialEventData.event_type;
    } else if (initialEventData.event_type) {
      return 'Other';
    }
    return '';
  });

  const [customEventType, setCustomEventType] = useState(() => {
    if (!initialEventData) return '';
    const standardLabels = [
      ...STATIC_EVENT_TYPES.map(t => t.id === 'other' ? 'Other' : t.label),
      'Kids Birthday'
    ];
    if (initialEventData.custom_event_type) {
      return initialEventData.custom_event_type;
    } else if (initialEventData.event_type && standardLabels.includes(initialEventData.event_type)) {
      return '';
    } else if (initialEventData.event_type) {
      return initialEventData.event_type;
    }
    return '';
  });
  // Initialize budget with country-specific default (will be adjusted in useEffect)
  const [budget, setBudget] = useState(() => {
    if (
      initialEventData?.budget !== undefined &&
      initialEventData?.budget !== null &&
      initialEventData?.budget !== ''
    ) {
      return Number(initialEventData.budget);
    }
    return null;
  });

  const [guestCount, setGuestCount] = useState(initialEventData?.guest_count ? String(initialEventData.guest_count) : '');
  const [country, setCountry] = useState(LAUNCH_COUNTRY);
  const [zipCode, setZipCode] = useState(initialEventData?.zip_code || '');
  const [date, setDate] = useState(initialEventData?.date || '');
  const [ageRange, setAgeRange] = useState(() => {
    const initial = initialEventData?.age_range;
    if (Array.isArray(initial)) return initial;
    if (typeof initial === 'string' && initial) return [initial];
    return [];
  });
  const [venue, setVenue] = useState(initialEventData?.venue || '');
  const [venueType, setVenueType] = useState(() => {
    const raw = initialEventData?.venue_type || '';
    // If the saved value is not a standard option, it's a custom string — select Other
    if (raw && !STANDARD_VENUE_TYPES.includes(raw)) return 'Other';
    return raw;
  });
  const [customVenueType, setCustomVenueType] = useState(() => {
    const raw = initialEventData?.venue_type || '';
    // Populate the custom input only when the saved value is not a standard option
    if (raw && !STANDARD_VENUE_TYPES.includes(raw)) return raw;
    return '';
  });
  const [focusSelection, setFocusSelection] = useState(() => normalizeFocusSelection(initialEventData?.focus_selection));
  // Demo Save Modal State (REMOVED)
  // const [showSaveExitModal, setShowSaveExitModal] = useState(false);
  const [timeOfDay, setTimeOfDay] = useState(() => {
    const initial = initialEventData?.time_of_day;
    if (Array.isArray(initial)) return initial;
    if (typeof initial === 'string' && initial) return [initial];
    return [];
  });
  const [foodSource, setFoodSource] = useState(() => normalizeArrayField(initialEventData?.food_source));
  const [customCuisineType, setCustomCuisineType] = useState(() => {
    if (!initialEventData?.cuisine_types) return '';
    const standardCuisineIds = ['italian', 'mexican', 'asian', 'american', 'bbq', 'mediterranean', 'indian', 'french', 'seafood', 'vegan'];
    const loaded = normalizeArrayField(initialEventData.cuisine_types);
    const customVal = loaded.find(c => c && c !== 'other' && !standardCuisineIds.includes(c));
    return customVal || '';
  });
  const [cuisineTypes, setCuisineTypes] = useState(() => {
    if (!initialEventData?.cuisine_types) return [];
    const standardCuisineIds = ['italian', 'mexican', 'asian', 'american', 'bbq', 'mediterranean', 'indian', 'french', 'seafood', 'vegan'];
    const loaded = normalizeArrayField(initialEventData.cuisine_types);
    const resolved = [];
    loaded.forEach(c => {
      if (standardCuisineIds.includes(c)) {
        resolved.push(c);
      } else if (c === 'other') {
        resolved.push('other');
      }
    });
    const hasCustom = loaded.some(c => c && c !== 'other' && !standardCuisineIds.includes(c));
    if (hasCustom && !resolved.includes('other')) {
      resolved.push('other');
    }
    return resolved;
  });
  const [eventSpecifics, setEventSpecifics] = useState(initialEventData?.event_specifics || {});
  const [selectedMeals, setSelectedMeals] = useState(() => normalizeArrayField(initialEventData?.meals));
  const [foodMenuStructure, setFoodMenuStructure] = useState(initialEventData?.food_menu_structure || {});
  const [dietaryRestrictions, setDietaryRestrictions] = useState(() => normalizeArrayField(initialEventData?.dietary_restrictions, ['none']));
  const [dietaryInstructions, setDietaryInstructions] = useState(initialEventData?.dietary_instructions || '');
  const [themeInput, setThemeInput] = useState(initialEventData?.theme_input || '');
  const [themeMode, setThemeMode] = useState(() => deriveThemeModeFromInput(initialEventData?.theme_input || ''));
  const themeInputRef = useRef(themeInput);
  useEffect(() => {
    themeInputRef.current = themeInput;
  }, [themeInput]);
  const [additionalDetails, setAdditionalDetails] = useState(stripInternalThemeDirection(initialEventData?.additional_details || ''));
  const [budgetTolerance] = useState(typeof initialEventData?.budget_tolerance_percent === 'number' ? initialEventData.budget_tolerance_percent : 0);
  const [selectedThemeStyles, setSelectedThemeStyles] = useState(() => normalizeArrayField(initialEventData?.theme_styles));
  const [selectedThemeModifiers, setSelectedThemeModifiers] = useState(() => normalizeArrayField(initialEventData?.theme_modifiers));
  const [themeGenerationCount, setThemeGenerationCount] = useState(() => {
    const initialCount = Number(initialEventData?.theme_count);
    return initialCount === 2 || initialCount === 4 ? initialCount : 4;
  });
  const [showExpressModal, setShowExpressModal] = useState(false);

  const foodPreferenceRestoreKey = JSON.stringify({
    food_source: initialEventData?.food_source || [],
    cuisine_types: initialEventData?.cuisine_types || [],
    meals: initialEventData?.meals || [],
    food_menu_structure: initialEventData?.food_menu_structure || {}
  });

  useEffect(() => {
    if (!initialEventData) return;
    setFoodSource(normalizeArrayField(initialEventData.food_source));

    const standardCuisineIds = ['italian', 'mexican', 'asian', 'american', 'bbq', 'mediterranean', 'indian', 'french', 'seafood', 'vegan'];
    const loadedCuisines = normalizeArrayField(initialEventData.cuisine_types);
    const resolvedCuisineTypes = [];
    let foundCustomCuisine = '';

    loadedCuisines.forEach(c => {
      if (standardCuisineIds.includes(c)) {
        resolvedCuisineTypes.push(c);
      } else if (c === 'other') {
        resolvedCuisineTypes.push('other');
      } else if (c) {
        foundCustomCuisine = c;
      }
    });

    if (foundCustomCuisine) {
      resolvedCuisineTypes.push('other');
      setCustomCuisineType(foundCustomCuisine);
    } else if (!resolvedCuisineTypes.includes('other')) {
      setCustomCuisineType('');
    }
    setCuisineTypes(resolvedCuisineTypes);

    setSelectedMeals(normalizeArrayField(initialEventData.meals));
    setFoodMenuStructure(initialEventData.food_menu_structure || {});
  }, [foodPreferenceRestoreKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const getSelectedMealsTotalCount = () => {
    return selectedMeals.reduce((acc, mealSlug) => {
      const count = foodMenuStructure[mealSlug] || 1;
      return acc + count;
    }, 0);
  };

  const handleIncrement = (mealSlug) => {
    const currentCount = foodMenuStructure[mealSlug] || 1;
    const currentTotal = getSelectedMealsTotalCount();
    if (currentTotal < 12 && currentCount < 5) {
      setFoodMenuStructure(prev => ({
        ...prev,
        [mealSlug]: currentCount + 1
      }));
    }
  };

  const handleDecrement = (mealSlug) => {
    const currentCount = foodMenuStructure[mealSlug] || 1;
    if (currentCount > 1) {
      setFoodMenuStructure(prev => ({
        ...prev,
        [mealSlug]: currentCount - 1
      }));
    }
  };

  const toggleMealSelection = (mealSlug) => {
    if (selectedMeals.includes(mealSlug)) {
      setSelectedMeals(prev => prev.filter(m => m !== mealSlug));
      setFoodMenuStructure(prev => {
        const updated = { ...prev };
        delete updated[mealSlug];
        return updated;
      });
    } else {
      setSelectedMeals(prev => [...prev, mealSlug]);
      setFoodMenuStructure(prev => ({
        ...prev,
        [mealSlug]: 1
      }));
      setErrors(prev => {
        const next = { ...prev };
        delete next.meals;
        return next;
      });
    }
  };

  // Mappers for ID to Label (for summary screen)
  const FOOD_SOURCE_LABELS = {
    cooking: 'Cooking Myself',
    delivery: 'Order Delivery',
    takeout: 'Restaurant Takeout',
    catering: 'Professional Catering',
    potluck: 'Potluck',
    snacks: 'Snacks Only'
  };

  const CUISINE_LABELS = {
    italian: 'Italian',
    mexican: 'Mexican',
    asian: 'Asian Fusion',
    american: 'American',
    bbq: 'BBQ',
    mediterranean: 'Mediterranean',
    indian: 'Indian',
    french: 'French',
    seafood: 'Seafood',
    vegan: 'Vegan/Healthy',
    other: 'Other'
  };

  const capitalize = (str) => {
    if (!str) return '';
    return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };

  const [errors, setErrors] = useState({});
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  // 'append' = keep existing themes & add a new set; 'replace' = replace current themes.
  // Defaults to 'append' so the summary-screen fresh-set flow matches the
  // carousel-side fresh-set flow and the user does not lose existing themes silently.
  const [regenerateMode, setRegenerateMode] = useState('append');
  const [showDemoUpgradeModal, setShowDemoUpgradeModal] = useState(false);
  const [showJumpModal, setShowJumpModal] = useState(false);
  const [jumpError, setJumpError] = useState(null);
  const [returnToSummaryMode, setReturnToSummaryMode] = useState(false);
  const [lastEditedMacroId, setLastEditedMacroId] = useState(null);
  const [editSnapshot, setEditSnapshot] = useState(null);

  // Refs for scroll-into-view on "Other" custom inputs
  const cuisineCustomInputRef = useRef(null);
  const cuisineInputFieldRef = useRef(null);
  const dietaryNotesRef = useRef(null);
  const venueCustomInputRef = useRef(null);
  const venueInputFieldRef = useRef(null);

  // Issue B: Reset summary availability when focus selection changes
  const lastFocusSelectionRef = useRef(focusSelection);
  useEffect(() => {
    const prev = JSON.stringify(lastFocusSelectionRef.current || []);
    const curr = JSON.stringify(focusSelection || []);
    if (prev !== curr) {
      setReturnToSummaryMode(false);
      lastFocusSelectionRef.current = focusSelection;
    }
  }, [focusSelection]);

  const [billingPackages, setBillingPackages] = useState(() => {
    const cached = getCachedBillingPackages(LAUNCH_COUNTRY);
    return Array.isArray(cached?.packages) && cached.packages.length > 0 ? cached.packages : [];
  });
  const [showBillingOptionsModal, setShowBillingOptionsModal] = useState(false);
  const [billingModalFlow, setBillingModalFlow] = useState('generation');
  const [billingModalTab, setBillingModalTab] = useState('credits');
  const [billingLoadingPackage, setBillingLoadingPackage] = useState(null);
  const [selectedBillingPackageId, setSelectedBillingPackageId] = useState(null);
  const [billingError, setBillingError] = useState('');
  const [billingCloseTarget, setBillingCloseTarget] = useState({ screen: 'input', returnThemeKey: '' });
  const [devBillingMode, setDevBillingMode] = useState(() => {
    try {
      return localStorage.getItem('craftmyparty_dev_billing_mode') || 'real';
    } catch (_) {
      return 'real';
    }
  });
  const previousStepRef = useRef(1);
  const isBillingLaunching = billingLoadingPackage !== null;
  const isThemeBased = focusSelection.includes('everything') || focusSelection.includes('theme_ideas');

  const defaultBillingPackages = [
    {
      id: 'credit_single',
      kind: 'credit_pack',
      mode: 'payment',
      credits: 1,
      amount: 500,
      currency: 'usd',
      name: '1 credit',
      description: '1 planning credit for one fresh theme set and the included matching event plan.'
    },
    {
      id: 'credit_5',
      kind: 'credit_pack',
      mode: 'payment',
      credits: 5,
      amount: 2250,
      currency: 'usd',
      name: '5 credits',
      description: '5 planning credits for multiple fresh theme sets with included matching plans.'
    },
    {
      id: 'credit_10',
      kind: 'credit_pack',
      mode: 'payment',
      credits: 10,
      amount: 4000,
      currency: 'usd',
      name: '10 credits',
      description: '10 planning credits for frequent fresh theme sets and matching plans.'
    },
    {
      id: 'plus_monthly',
      kind: 'subscription',
      mode: 'subscription',
      credits: 2,
      annual_credits: 24,
      amount: 900,
      currency: 'usd',
      interval: 'month',
      billing_period_months: 1,
      rollover: true,
      name: 'CraftMyParty Plus Monthly',
      description: '2 planning credits every month with rollover.'
    },
    {
      id: 'plus_annual',
      kind: 'subscription',
      mode: 'subscription',
      credits: 2,
      annual_credits: 24,
      amount: 9000,
      currency: 'usd',
      interval: 'year',
      billing_period_months: 12,
      rollover: true,
      name: 'CraftMyParty Plus Annual',
      description: '24 planning credits per year, delivered as 2 credits every month with rollover.'
    }
  ];

  const loadBillingPackages = async () => {
    try {
      const data = await fetchBillingPackages(LAUNCH_COUNTRY, false);
      if (Array.isArray(data?.packages) && data.packages.length > 0) {
        setBillingPackages(data.packages);
      }
    } catch (_) {
      // Fall back to defaultBillingPackages / existing state.
    }
  };


  const withWorkflowFlags = (snapshot) => {
    const flaggedSnapshot = { ...snapshot };
	    [
	      '__appendThemes',
	      '__similarToThemeKey',
	      '__similarToThemeName',
	      '__openFreshSetWorkflow',
	      '__returnToThemesAfterFreshSet',
	      '__recalculateCurrentPlan',
	      '__focusThemeKey',
	      '__returnThemeKey',
	      '__returnThemeIndex',
	      '__freshSetRequestId',
	      '__aiAdditionalDetails'
	    ].forEach((key) => {
	      if (initialEventData?.[key] !== undefined) {
	        flaggedSnapshot[key] = initialEventData[key];
	      }
    });
    return flaggedSnapshot;
  };



  const hasPrimedBillingPackagesRef = useRef(false);

  useEffect(() => {
    if (billingOverlayOnly) return undefined;
    if (hasPrimedBillingPackagesRef.current) return undefined;
    hasPrimedBillingPackagesRef.current = true;

    if (billingPackages.length === 0) {
      setBillingPackages(defaultBillingPackages);
    }

    let cancelled = false;
    loadBillingPackages().finally(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingOverlayOnly]);

  // Focus Selection State - moved to top
  // Dynamic Step Mapping
  // CONSTANT_STEP_ID mapping to manage logical steps vs display steps

  // Dynamic Step Mapping
  // CONSTANT_STEP_ID mapping to manage logical steps vs display steps
  const STEP_IDS = {
    EVENT_TYPE: 'event_type',
    FOCUS: 'focus', // New Step
    EVENT_SPECIFICS_WHO: 'event_specifics_who', // Sub-step 3a
    EVENT_SPECIFICS_GENDER: 'event_specifics_gender', // New Sub-step 3b
    EVENT_SPECIFICS_AGE: 'event_specifics_age', // Sub-step 3c
    EVENT_SPECIFICS_EXTRA: 'event_specifics_extra', // Sub-step 3d
    ESSENTIALS_COUNTRY: 'essentials_country', // Moved up
    ESSENTIALS_DATE: 'essentials_date',
    ESSENTIALS_GUESTS: 'essentials_guests',
    ESSENTIALS_BUDGET: 'essentials_budget',
    ESSENTIALS_ZIP: 'essentials_zip',
    AGE_RANGE: 'age_range', // Added back
    THEME_DEAILS: 'theme_details', // Vibe/Modifiers
    THEME_MODE: 'theme_mode',
    THEME_CONCEPT: 'theme_concept',
    THEME_BROWSE_CATS: 'theme_browse_cats',
    THEME_BROWSE_TEMPLATES: 'theme_browse_templates',
    VENUE: 'venue',
    VENUE_TYPE: 'venue_type', // Added back
    TIME: 'time',
    FOOD_SOURCE: 'food_source', // New Step (UberEats, Catering, etc.)
    CUISINE_TYPES: 'cuisine_types', // New Step: Preferred Cuisines
    FOOD_MEALS: 'food_meals',
    FOOD_DIETARY: 'food_dietary',
    FOOD_DIETARY_INSTRUCTIONS: 'food_dietary_instructions',
    ADDITIONAL: 'additional',
    THEME_STYLES: 'theme_styles',
    SUMMARY: 'summary'
  };

  const MASTER_STEP_ORDER = [
    STEP_IDS.EVENT_TYPE,
    STEP_IDS.FOCUS,
    STEP_IDS.EVENT_SPECIFICS_WHO,
    STEP_IDS.EVENT_SPECIFICS_GENDER,
    STEP_IDS.EVENT_SPECIFICS_AGE,
    STEP_IDS.EVENT_SPECIFICS_EXTRA,
    STEP_IDS.ESSENTIALS_COUNTRY,
    STEP_IDS.ESSENTIALS_DATE,
    STEP_IDS.ESSENTIALS_GUESTS,
    STEP_IDS.ESSENTIALS_BUDGET,
    STEP_IDS.ESSENTIALS_ZIP,
    STEP_IDS.AGE_RANGE,
    STEP_IDS.THEME_MODE,
    STEP_IDS.THEME_CONCEPT,
    STEP_IDS.THEME_BROWSE_CATS,
    STEP_IDS.THEME_BROWSE_TEMPLATES,
    STEP_IDS.THEME_STYLES,
    STEP_IDS.THEME_DEAILS,
    STEP_IDS.VENUE,
    STEP_IDS.VENUE_TYPE,
    STEP_IDS.TIME,
    STEP_IDS.FOOD_SOURCE,
    STEP_IDS.CUISINE_TYPES,
    STEP_IDS.FOOD_MEALS,
    STEP_IDS.FOOD_DIETARY,
    STEP_IDS.ADDITIONAL,
    STEP_IDS.SUMMARY
  ];

  // Group active steps into logical Macro Steps
  const MACRO_GROUPS = [
    { id: 'event_type', title: 'Event Basics', steps: [STEP_IDS.EVENT_TYPE, STEP_IDS.FOCUS] },
    { id: 'event_details', title: 'Event Details', steps: [STEP_IDS.EVENT_SPECIFICS_WHO, STEP_IDS.EVENT_SPECIFICS_GENDER, STEP_IDS.EVENT_SPECIFICS_AGE, STEP_IDS.EVENT_SPECIFICS_EXTRA] },
    { id: 'logistics', title: 'Logistics', steps: [STEP_IDS.ESSENTIALS_COUNTRY, STEP_IDS.ESSENTIALS_DATE, STEP_IDS.ESSENTIALS_GUESTS, STEP_IDS.ESSENTIALS_BUDGET, STEP_IDS.ESSENTIALS_ZIP] },
    { id: 'age_range', title: 'Target Audience', steps: [STEP_IDS.AGE_RANGE] },
    { id: 'theme', title: 'Theme Concept', steps: [STEP_IDS.THEME_MODE, STEP_IDS.THEME_CONCEPT, STEP_IDS.THEME_BROWSE_CATS, STEP_IDS.THEME_BROWSE_TEMPLATES, STEP_IDS.THEME_STYLES, STEP_IDS.THEME_DEAILS] },
    { id: 'venue', title: 'Venue', steps: [STEP_IDS.VENUE, STEP_IDS.VENUE_TYPE] },
    { id: 'time', title: 'Time of Day', steps: [STEP_IDS.TIME] },
    { id: 'food', title: 'Food & Cuisine', steps: [STEP_IDS.FOOD_SOURCE, STEP_IDS.CUISINE_TYPES, STEP_IDS.FOOD_MEALS] },
    { id: 'dietary', title: 'Dietary Needs', steps: [STEP_IDS.FOOD_DIETARY] },
    { id: 'additional', title: 'Extra Details', steps: [STEP_IDS.ADDITIONAL] },
    { id: 'summary', title: 'Review', steps: [STEP_IDS.SUMMARY] }
  ];

  const getActiveMacroSteps = (allActiveStepIds) => {
    const isFoodOnly = focusSelection && focusSelection.length === 1 && focusSelection[0] === 'food';
    return MACRO_GROUPS.map(macro => {
      const activeSubSteps = macro.steps.filter(s => allActiveStepIds.includes(s));
      let title = macro.title;
      if (macro.id === 'theme' && isFoodOnly) {
        title = 'Menu Concept';
      }
      if (macro.id === 'age_range') {
        title = 'Guest Age Groups';
      }
      return { ...macro, title, activeSubSteps };
    }).filter(macro => macro.activeSubSteps.length > 0);
  };

  // Helper to get theme mode from input if it exists
  const getThemeInputMode = (inputOverride) => {
    let currentMode = themeMode;
    if (currentMode === 'browse_categories' || currentMode === 'browse_templates') currentMode = 'browse';

    if (currentMode !== 'selection') return currentMode;
    const input = inputOverride !== undefined ? inputOverride : themeInputRef.current;
    if (!input) return 'selection';
    if (input === 'Theme Mode: Surprise Me' || input.includes('Surprise me!')) return 'surprise';
    if (input === 'Theme Mode: Browsing...' || input.startsWith('Theme:')) return 'browse';
    if (input === 'Theme Mode: Custom Concept') return 'custom';
    return 'custom';
  };


  // Helper to determine active steps based on focus
  const getActiveSteps = (focusInput) => {
    const focus = Array.isArray(focusInput) ? focusInput : (focusInput ? [focusInput] : []);

    // Base steps that are ALWAYS required
    let requiredSteps = [
      STEP_IDS.EVENT_TYPE,
      STEP_IDS.FOCUS,
      STEP_IDS.EVENT_SPECIFICS_WHO
    ];

    if (eventType === 'Birthday Party' || eventType === 'Kids Birthday') {
      requiredSteps.push(STEP_IDS.EVENT_SPECIFICS_GENDER);
    }

    if (['Birthday Party', 'Kids Birthday', 'Anniversary', 'Baby Shower', 'Graduation'].includes(eventType)) {
      requiredSteps.push(STEP_IDS.EVENT_SPECIFICS_AGE);
      requiredSteps.push(STEP_IDS.EVENT_SPECIFICS_EXTRA);
    }

    // Helper to add steps if not present
    const addSteps = (...steps) => requiredSteps.push(...steps);

    // If focus includes "everything" or focus is empty, treat as full plan
    if (focus.includes('everything') || focus.length === 0) {
      addSteps(
        STEP_IDS.ESSENTIALS_COUNTRY, STEP_IDS.ESSENTIALS_DATE, STEP_IDS.ESSENTIALS_GUESTS, STEP_IDS.ESSENTIALS_BUDGET, STEP_IDS.ESSENTIALS_ZIP,
        STEP_IDS.AGE_RANGE, STEP_IDS.THEME_MODE
      );

      const mode = getThemeInputMode();
      if (mode === 'custom') {
        addSteps(STEP_IDS.THEME_CONCEPT);
      } else if (mode === 'browse') {
        addSteps(STEP_IDS.THEME_BROWSE_CATS, STEP_IDS.THEME_BROWSE_TEMPLATES);
      }

      addSteps(STEP_IDS.THEME_STYLES, STEP_IDS.THEME_DEAILS);

      addSteps(
        STEP_IDS.VENUE, STEP_IDS.VENUE_TYPE, STEP_IDS.TIME,
        STEP_IDS.FOOD_SOURCE, STEP_IDS.CUISINE_TYPES, STEP_IDS.FOOD_MEALS, STEP_IDS.FOOD_DIETARY,
        STEP_IDS.ADDITIONAL,
        STEP_IDS.SUMMARY
      );

      // Deduplicate and filter by master order to maintain correct sequence
      const distinctSteps = [...new Set(requiredSteps)];
      return MASTER_STEP_ORDER.filter(stepId => distinctSteps.includes(stepId));
    }

    // Otherwise, build the union of steps needed by each selected intent:
    const hasThemeIdeas = focus.includes('theme_ideas');
    const hasDecor = focus.includes('decor');
    const hasFood = focus.includes('food');
    const hasActivities = focus.includes('activities');
    const hasShopping = focus.includes('shopping');

    // 1. ESSENTIALS & LOGISTICS
    if (hasShopping) {
      addSteps(STEP_IDS.ESSENTIALS_COUNTRY);
    }
    if (hasDecor || hasFood || hasActivities || hasShopping) {
      addSteps(STEP_IDS.ESSENTIALS_GUESTS, STEP_IDS.ESSENTIALS_BUDGET);
    }
    if (hasFood || hasShopping || hasDecor) {
      addSteps(STEP_IDS.ESSENTIALS_ZIP);
    }

    // 2. AGE RANGE (demographics)
    if (hasThemeIdeas || hasFood || hasActivities) {
      addSteps(STEP_IDS.AGE_RANGE);
    }

    // 3. THEME SELECTION & DISCOVERY
    if (hasThemeIdeas || hasDecor) {
      const mode = getThemeInputMode();
      addSteps(STEP_IDS.THEME_MODE);
      if (mode === 'custom') {
        addSteps(STEP_IDS.THEME_CONCEPT);
      } else if (mode === 'browse') {
        addSteps(STEP_IDS.THEME_BROWSE_CATS, STEP_IDS.THEME_BROWSE_TEMPLATES);
      }
      addSteps(STEP_IDS.THEME_STYLES);
    }

    // theme details / value modifiers: all specific focuses!
    addSteps(STEP_IDS.THEME_DEAILS);

    // 4. VENUE
    if (hasDecor || hasActivities) {
      addSteps(STEP_IDS.VENUE, STEP_IDS.VENUE_TYPE);
    }

    // 5. TIME OF DAY
    if (hasFood || hasActivities) {
      addSteps(STEP_IDS.TIME);
    }

    // 6. FOOD SYSTEM
    if (hasFood) {
      addSteps(STEP_IDS.FOOD_SOURCE, STEP_IDS.CUISINE_TYPES, STEP_IDS.FOOD_MEALS, STEP_IDS.FOOD_DIETARY);
    }

    // 7. ADDITIONAL & SUMMARY
    addSteps(STEP_IDS.ADDITIONAL, STEP_IDS.SUMMARY);

    // Deduplicate and filter by master order to maintain correct sequence
    const distinctSteps = [...new Set(requiredSteps)];
    return MASTER_STEP_ORDER.filter(stepId => distinctSteps.includes(stepId));
  };

  const [activeStepIds, setActiveStepIds] = useState(() => getActiveSteps(normalizeFocusSelection(initialEventData?.focus_selection)));
  const [activeMacroSteps, setActiveMacroSteps] = useState(() => getActiveMacroSteps(activeStepIds));

  // Ref to track latest activeStepIds for sync
  const activeStepIdsRef = useRef(activeStepIds);
  const totalSteps = activeStepIds.length;

  const readStepFromUrl = useCallback((steps) => {
    if (typeof window === 'undefined') return null;
    try {
      const requestedStep = new URLSearchParams(window.location.search || '').get('step');
      if (!requestedStep) return null;
      if (requestedStep === 'summary') return steps.length;
      const numericStep = parseInt(requestedStep, 10);
      if (!Number.isNaN(numericStep) && numericStep >= 1 && numericStep <= steps.length) {
        return numericStep;
      }
      const stepIndex = steps.indexOf(requestedStep);
      return stepIndex >= 0 ? stepIndex + 1 : null;
    } catch (_) {
      return null;
    }
  }, []);

  const readStoredStep = useCallback((steps) => {
    try {
      const storagePlanId = currentPlanId || initialEventData?.plan_id || initialEventData?._id || initialEventData?.id;
      const stored = storagePlanId
        ? sessionStorage.getItem(`cmp_active_step_${storagePlanId}`)
        : sessionStorage.getItem('cmp_active_step_new') || sessionStorage.getItem('cmp_active_step_last');
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed >= 1 && parsed <= steps.length) {
          return parsed;
        }
      }
    } catch (e) {
      console.error('Error reading active step from sessionStorage', e);
    }
    return null;
  }, [currentPlanId, initialEventData?.plan_id, initialEventData?._id, initialEventData?.id]);

  const appliedInitialStepRef = useRef(false);

  const [currentStep, setCurrentStep] = useState(() => {
    let resolvedStep = 1;
    if (initialStep === 'last') {
      resolvedStep = activeStepIds.length;
    } else if (initialStep === 'firstIncomplete') {
      const localEventType = initialEventData?.event_type || '';
      const localFocusSelection = normalizeFocusSelection(initialEventData?.focus_selection);
      const localGuestCount = initialEventData?.guest_count ? String(initialEventData.guest_count) : '';
      const localCountry = LAUNCH_COUNTRY;
      const localZipCode = initialEventData?.zip_code || '';
      const localBudget = initialEventData?.budget !== undefined && initialEventData?.budget !== null && initialEventData?.budget !== ''
        ? Number(initialEventData.budget)
        : null;
      const localEventSpecifics = initialEventData?.event_specifics || {};
      const localThemeInput = initialEventData?.theme_input || '';

      const getLocalStepValid = (stepId) => {
        if (stepId === STEP_IDS.EVENT_TYPE) return !!localEventType;
        if (stepId === STEP_IDS.FOCUS) return Array.isArray(localFocusSelection) && localFocusSelection.length > 0;
        if (stepId === STEP_IDS.EVENT_SPECIFICS_GENDER) return !!localEventSpecifics.gender;
        if (stepId === STEP_IDS.EVENT_SPECIFICS_WHO) {
          if (['Birthday Party', 'Kids Birthday', 'Anniversary', 'Baby Shower', 'Graduation'].includes(localEventType)) {
            return !!localEventSpecifics.honoreeRelation;
          }
          return true;
        }
        if (stepId === STEP_IDS.EVENT_SPECIFICS_AGE) {
          if (localEventType === 'Baby Shower') return true;
          if (localEventType === 'Birthday Party' || localEventType === 'Kids Birthday') return !!localEventSpecifics.ageRange;
          if (localEventType === 'Anniversary') return !!localEventSpecifics.milestone;
          if (localEventType === 'Graduation') return !!localEventSpecifics.subType;
          return true;
        }
        if (stepId === STEP_IDS.EVENT_SPECIFICS_EXTRA) return true;
        if (stepId === STEP_IDS.ESSENTIALS_COUNTRY) return !!localCountry;
        if (stepId === STEP_IDS.ESSENTIALS_DATE) return true;
        if (stepId === STEP_IDS.ESSENTIALS_GUESTS) return !!(localGuestCount && !isNaN(localGuestCount) && parseInt(localGuestCount) >= 1);
        if (stepId === STEP_IDS.ESSENTIALS_BUDGET) return !!(localBudget && localBudget >= getMinBudget(localCountry));
        if (stepId === STEP_IDS.ESSENTIALS_ZIP) {
          const postalVal = validatePostalCode(localZipCode, localCountry);
          return !!postalVal.valid;
        }
        if (stepId === STEP_IDS.THEME_CONCEPT) {
          return localThemeInput.trim().length > 5 && !localThemeInput.includes('Theme Mode:');
        }
        return true;
      };

      const getLocalStepOptional = (stepId) => {
        return ![
          STEP_IDS.EVENT_TYPE,
          STEP_IDS.FOCUS,
          STEP_IDS.EVENT_SPECIFICS_WHO,
          STEP_IDS.EVENT_SPECIFICS_AGE,
          STEP_IDS.ESSENTIALS_COUNTRY,
          STEP_IDS.ESSENTIALS_GUESTS,
          STEP_IDS.ESSENTIALS_BUDGET,
          STEP_IDS.ESSENTIALS_ZIP,
          STEP_IDS.THEME_MODE,
          STEP_IDS.THEME_CONCEPT,
          STEP_IDS.FOOD_DIETARY,
          STEP_IDS.SUMMARY
        ].includes(stepId);
      };

      let foundStep = null;
      for (let i = 0; i < activeStepIds.length - 1; i++) {
        const stepId = activeStepIds[i];
        if (!getLocalStepOptional(stepId) && !getLocalStepValid(stepId)) {
          foundStep = i + 1;
          break;
        }
      }
      resolvedStep = foundStep || activeStepIds.length;
    } else if (initialStep) {
      resolvedStep = initialStep;
    } else {
      resolvedStep = readStepFromUrl(activeStepIds) || readStoredStep(activeStepIds) || 1;
    }
    return Math.max(1, Math.min(resolvedStep, activeStepIds.length));
  });

  const [maxReachedStep, setMaxReachedStep] = useState(() => {
    const restoredMaxStep = Number(initialEventData?.draft_max_reached_step);
    return (Number.isFinite(restoredMaxStep) && restoredMaxStep > 0)
      ? Math.max(restoredMaxStep, currentStep)
      : currentStep;
  });

  useEffect(() => {
    setMaxReachedStep(prev => Math.max(prev, currentStep));
  }, [currentStep]);

  useEffect(() => {
    console.debug('[CMP_STEP1_STATE_DEBUG]', {
      currentStep,
      eventType,
      customEventType,
      eventDataEventType: initialEventData?.event_type,
      eventDataCustomEventType: initialEventData?.custom_event_type,
      eventSpecifics: initialEventData?.event_specifics,
      focusSelection
    });
  }, [currentStep, eventType, customEventType, initialEventData, focusSelection]);

  const getNormalizedCuisineTypes = () => {
    const mapped = cuisineTypes.map(c => {
      if (c === 'other') {
        return customCuisineType.trim();
      }
      return c;
    }).filter(Boolean);
    return mapped.length > 0 ? mapped : null;
  };

  const getFormSnapshot = () => {
    const isStepReached = (stepId) => {
      const index = activeStepIds.indexOf(stepId);
      return index !== -1 && index < maxReachedStep;
    };

    return withWorkflowFlags({
      event_type: eventType === 'Other' ? customEventType.trim() : eventType,
      custom_event_type: eventType === 'Other' ? customEventType.trim() : '',
      focus_selection: isStepReached(STEP_IDS.FOCUS) ? normalizeFocusSelection(focusSelection) : null,
      budget: isStepReached(STEP_IDS.ESSENTIALS_BUDGET) ? (budget !== null && budget !== undefined && budget !== '' ? Number(budget) : null) : null,
      budget_tolerance_percent: isStepReached(STEP_IDS.ESSENTIALS_BUDGET) ? budgetTolerance : null,
      guest_count: isStepReached(STEP_IDS.ESSENTIALS_GUESTS) ? (Number.isFinite(parseInt(guestCount, 10)) ? parseInt(guestCount, 10) : null) : null,
      country: isStepReached(STEP_IDS.ESSENTIALS_COUNTRY) ? LAUNCH_COUNTRY : null,
      zip_code: isStepReached(STEP_IDS.ESSENTIALS_ZIP) ? zipCode : null,
      date: isStepReached(STEP_IDS.ESSENTIALS_DATE) ? (date || null) : null,
      age_range: isStepReached(STEP_IDS.AGE_RANGE) ? (Array.isArray(ageRange) ? (ageRange.length > 0 ? ageRange : null) : (ageRange || null)) : null,
      venue: isStepReached(STEP_IDS.VENUE) ? (venue || null) : null,
      venue_type: isStepReached(STEP_IDS.VENUE_TYPE) ? (venueType === 'Other' ? customVenueType.trim() : (venueType || null)) : null,
      time_of_day: isStepReached(STEP_IDS.TIME) ? (Array.isArray(timeOfDay) ? (timeOfDay.length > 0 ? timeOfDay : null) : (timeOfDay || null)) : null,
      meals: isStepReached(STEP_IDS.FOOD_MEALS) ? (selectedMeals.length > 0 ? selectedMeals : null) : null,
      food_menu_structure: isStepReached(STEP_IDS.FOOD_MEALS) ? (Object.keys(foodMenuStructure).length > 0 ? foodMenuStructure : null) : null,
      food_source: isStepReached(STEP_IDS.FOOD_SOURCE) ? (foodSource.length > 0 ? foodSource : null) : null,
      cuisine_types: isStepReached(STEP_IDS.CUISINE_TYPES) ? getNormalizedCuisineTypes() : null,
      dietary_restrictions: isStepReached(STEP_IDS.FOOD_DIETARY) ? (dietaryRestrictions.length > 0 ? dietaryRestrictions : null) : null,
      dietary_instructions: isStepReached(STEP_IDS.FOOD_DIETARY) ? (dietaryInstructions.trim() || null) : null,
      theme_input: (isStepReached(STEP_IDS.THEME_MODE) || isStepReached(STEP_IDS.THEME_CONCEPT) || isStepReached(STEP_IDS.THEME_BROWSE_CATS) || isStepReached(STEP_IDS.THEME_BROWSE_TEMPLATES)) ? (themeInput.trim() || null) : null,
      additional_details: isStepReached(STEP_IDS.ADDITIONAL) ? (additionalDetails.trim() || null) : null,
      theme_styles: isStepReached(STEP_IDS.THEME_STYLES) ? (selectedThemeStyles.length > 0 ? selectedThemeStyles : null) : null,
      theme_modifiers: isStepReached(STEP_IDS.THEME_DEAILS) ? (selectedThemeModifiers.length > 0 ? selectedThemeModifiers : null) : null,
      theme_count: isAuthenticated ? themeGenerationCount : 2,
      event_specifics: (isStepReached(STEP_IDS.EVENT_SPECIFICS_WHO) || isStepReached(STEP_IDS.EVENT_SPECIFICS_GENDER) || isStepReached(STEP_IDS.EVENT_SPECIFICS_AGE) || isStepReached(STEP_IDS.EVENT_SPECIFICS_EXTRA)) ? (Object.keys(eventSpecifics).length > 0 ? eventSpecifics : null) : null,
      draft_step_index: currentStep,
      draft_max_reached_step: maxReachedStep
    });
  };

  // Theme generation count is a session-only preference for the Generate/Regenerate
  // flow and should NOT mark the form as dirty. We strip it (and ephemeral
  // workflow flags, as well as step state indices) from the dirty-check snapshot so toggling 2/4 in the
  // fresh-set / regenerate modal does not flip canSave.
  const getDirtyCheckSnapshot = () => {
    const snapshot = getFormSnapshot();
    const { theme_count, draft_step_index, draft_max_reached_step, ...rest } = snapshot;
    return JSON.stringify(rest);
  };

  // Track unsaved changes against a stable baseline (avoids false positives on summary/dev tools)
  const [isFormDirty, setIsFormDirty] = useState(false);
  const formBaselineRef = useRef(null);
  const mountedAtRef = useRef(Date.now());
  useEffect(() => {
    if (!eventType) {
      formBaselineRef.current = null;
      clearUnsavedChanges();
      setIsFormDirty(false);
      if (onDirtyChange) onDirtyChange(false);
      return;
    }

    if (window.__restoreTime && Date.now() - window.__restoreTime < 1500) {
      return;
    }

    const snapshot = getDirtyCheckSnapshot();
    if (formBaselineRef.current === null || (initialEventData && Date.now() - mountedAtRef.current < 1500)) {
      if (initialEventData) {
        // Restoring a saved draft/plan — treat loaded data as the clean baseline
        formBaselineRef.current = snapshot;
        clearUnsavedChanges();
        setIsFormDirty(false);
        if (onDirtyChange) onDirtyChange(false);
      } else {
        // Brand-new plan — first meaningful user input (e.g. event type selection)
        // is unsaved work; mark dirty so the navigation guard fires correctly
        formBaselineRef.current = '';
        markUnsavedChanges();
        setIsFormDirty(true);
        if (onDirtyChange) onDirtyChange(true);
      }
      return;
    }

    const isDirty = snapshot !== formBaselineRef.current;
    setIsFormDirty(isDirty);
    if (isDirty) {
      markUnsavedChanges();
    } else {
      clearUnsavedChanges();
    }
    if (onDirtyChange) onDirtyChange(isDirty);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStep, activeStepIds, maxReachedStep,
    eventType, customEventType, focusSelection, guestCount, country, zipCode,
    date, ageRange, venue, venueType, customVenueType, timeOfDay,
    cuisineTypes, customCuisineType, foodSource,
    selectedMeals, dietaryRestrictions, dietaryInstructions, themeInput,
    additionalDetails, selectedThemeStyles, selectedThemeModifiers,
    eventSpecifics, budget, budgetTolerance
  ]);



  const [isMobile, setIsMobile] = useState(false);
  const [isSwipeViewport, setIsSwipeViewport] = useState(false);
  const [showMobileActionMenu, setShowMobileActionMenu] = useState(false);
  const [stepDirection, setStepDirection] = useState('forward');
  const inputSwipeStartRef = useRef(null);
  const hydratedInitialDataKeyRef = useRef('');
  const lastPlanIdRef = useRef(initialEventData ? null : 'new');
  const hasHydratedLoadedDataRef = useRef(initialEventData ? false : true);

  useEffect(() => {
    setShowMobileActionMenu(false);
  }, [currentStep]);

  const scrollWizardToTop = useCallback((behavior = 'auto') => {
    if (typeof window === 'undefined') return;
    const scrollNow = () => {
      const appScroller = document.querySelector('[data-app-main-scroll="true"]');
      if (appScroller?.scrollTo) {
        appScroller.scrollTo({ top: 0, left: 0, behavior });
      }
      window.scrollTo({ top: 0, left: 0, behavior });
    };
    scrollNow();
    window.requestAnimationFrame(scrollNow);
    window.setTimeout(scrollNow, 60);
  }, []);

  useEffect(() => {
    if (!initialEventData) return;
    const activePlanId = currentPlanId || initialEventData.plan_id || initialEventData._id || initialEventData.id || 'new';

    const isDifferentPlan = lastPlanIdRef.current !== activePlanId;
    if (isDifferentPlan) {
      hasHydratedLoadedDataRef.current = false;
    }

    const hydrationKey = [
      activePlanId,
      initialEventData.updated_at || initialEventData.updatedAt || initialEventData.created_at || '',
      initialEventData.event_type || '',
      initialEventData.guest_count || '',
      initialEventData.budget || '',
      initialEventData.theme_input || '',
      JSON.stringify(initialEventData.focus_selection || []),
      JSON.stringify(initialEventData.cuisine_types || [])
    ].join('|');

    if (hydratedInitialDataKeyRef.current === hydrationKey) return;

    if (lastPlanIdRef.current === activePlanId && hasHydratedLoadedDataRef.current) {
      hydratedInitialDataKeyRef.current = hydrationKey;
      return;
    }

    hydratedInitialDataKeyRef.current = hydrationKey;
    lastPlanIdRef.current = activePlanId;

    const standardLabels = [
      ...STATIC_EVENT_TYPES.map(t => t.id === 'other' ? 'Other' : t.label),
      'Kids Birthday'
    ];
    if (initialEventData.custom_event_type) {
      setEventType('Other');
      setCustomEventType(initialEventData.custom_event_type);
    } else if (initialEventData.event_type && standardLabels.includes(initialEventData.event_type)) {
      setEventType(initialEventData.event_type);
      setCustomEventType('');
    } else if (initialEventData.event_type) {
      setEventType('Other');
      setCustomEventType(initialEventData.event_type);
    } else {
      setEventType('');
      setCustomEventType('');
    }

    if (initialEventData.event_type || initialEventData.custom_event_type) {
      hasHydratedLoadedDataRef.current = true;
    }
    setBudget(initialEventData.budget !== undefined && initialEventData.budget !== null && initialEventData.budget !== ''
      ? Number(initialEventData.budget)
      : null
    );
    setGuestCount(initialEventData.guest_count ? String(initialEventData.guest_count) : '');
    setCountry(LAUNCH_COUNTRY);
    setZipCode(initialEventData.zip_code || '');
    setDate(initialEventData.date || initialEventData.event_date || '');
    setAgeRange(normalizeArrayField(initialEventData.age_range));
    setVenue(initialEventData.venue || '');
    // Custom venue hydration: if saved venue_type is not a standard option, treat as custom (Other)
    const rawVenueType = initialEventData.venue_type || '';
    if (rawVenueType && !STANDARD_VENUE_TYPES.includes(rawVenueType)) {
      setVenueType('Other');
      setCustomVenueType(rawVenueType);
    } else {
      setVenueType(rawVenueType);
      setCustomVenueType('');
    }
    const hydratedFocusSelection = normalizeFocusSelection(initialEventData.focus_selection);
    const hydratedSteps = getActiveSteps(hydratedFocusSelection);

    setFocusSelection(hydratedFocusSelection);
    setActiveStepIds(hydratedSteps);
    setActiveMacroSteps(getActiveMacroSteps(hydratedSteps));
    activeStepIdsRef.current = hydratedSteps;
    setTimeOfDay(normalizeArrayField(initialEventData.time_of_day));
    setFoodSource(normalizeArrayField(initialEventData.food_source));
    const standardCuisineIds = ['italian', 'mexican', 'asian', 'american', 'bbq', 'mediterranean', 'indian', 'french', 'seafood', 'vegan'];
    const loadedCuisines = normalizeArrayField(initialEventData.cuisine_types);
    const resolvedCuisineTypes = [];
    let foundCustomCuisine = '';

    loadedCuisines.forEach(c => {
      if (standardCuisineIds.includes(c)) {
        resolvedCuisineTypes.push(c);
      } else if (c === 'other') {
        resolvedCuisineTypes.push('other');
      } else if (c) {
        foundCustomCuisine = c;
      }
    });

    if (foundCustomCuisine) {
      resolvedCuisineTypes.push('other');
      setCustomCuisineType(foundCustomCuisine);
    } else {
      setCustomCuisineType('');
    }
    setCuisineTypes(resolvedCuisineTypes);

    setEventSpecifics(initialEventData.event_specifics || {});
    setSelectedMeals(normalizeArrayField(initialEventData.meals));
    setDietaryRestrictions(normalizeArrayField(initialEventData.dietary_restrictions, ['none']));
    setDietaryInstructions(initialEventData.dietary_instructions || '');
    setThemeInput(initialEventData.theme_input || '');
    setThemeMode(deriveThemeModeFromInput(initialEventData.theme_input || ''));
    setAdditionalDetails(stripInternalThemeDirection(initialEventData.additional_details || ''));
    setSelectedThemeStyles(normalizeArrayField(initialEventData.theme_styles));
    setSelectedThemeModifiers(normalizeArrayField(initialEventData.theme_modifiers));

    const initialCount = Number(initialEventData.theme_count);
    setThemeGenerationCount(initialCount === 2 || initialCount === 4 ? initialCount : 4);
    formBaselineRef.current = null;
    if (!appliedInitialStepRef.current) {
      const restoredMaxStep = Number(initialEventData?.draft_max_reached_step);
      const getTargetMaxStep = (targetStep) => {
        return (Number.isFinite(restoredMaxStep) && restoredMaxStep > 0)
          ? Math.max(restoredMaxStep, targetStep)
          : targetStep;
      };

      if (initialStep === 'last') {
        const targetStep = hydratedSteps.length;
        setCurrentStep(targetStep);
        setMaxReachedStep(getTargetMaxStep(targetStep));
      } else if (initialStep === 'firstIncomplete') {
        const localEventType = initialEventData.event_type || '';
        const localFocusSelection = normalizeFocusSelection(initialEventData.focus_selection);
        const localGuestCount = initialEventData.guest_count ? String(initialEventData.guest_count) : '';
        const localCountry = LAUNCH_COUNTRY;
        const localZipCode = initialEventData.zip_code || '';
        const localBudget = initialEventData.budget !== undefined && initialEventData.budget !== null && initialEventData.budget !== ''
          ? Number(initialEventData.budget)
          : null;
        const localEventSpecifics = initialEventData.event_specifics || {};
        const localThemeInput = initialEventData.theme_input || '';

        const getLocalStepValid = (stepId) => {
          if (stepId === STEP_IDS.EVENT_TYPE) return !!localEventType;
          if (stepId === STEP_IDS.FOCUS) return Array.isArray(localFocusSelection) && localFocusSelection.length > 0;
          if (stepId === STEP_IDS.EVENT_SPECIFICS_GENDER) return !!localEventSpecifics.gender;
          if (stepId === STEP_IDS.EVENT_SPECIFICS_WHO) {
            if (['Birthday Party', 'Kids Birthday', 'Anniversary', 'Baby Shower', 'Graduation'].includes(localEventType)) {
              return !!localEventSpecifics.honoreeRelation;
            }
            return true;
          }
          if (stepId === STEP_IDS.EVENT_SPECIFICS_AGE) {
            if (localEventType === 'Baby Shower') return true;
            if (localEventType === 'Birthday Party' || localEventType === 'Kids Birthday') return !!localEventSpecifics.ageRange;
            if (localEventType === 'Anniversary') return !!localEventSpecifics.milestone;
            if (localEventType === 'Graduation') return !!localEventSpecifics.subType;
            return true;
          }
          if (stepId === STEP_IDS.EVENT_SPECIFICS_EXTRA) return true;
          if (stepId === STEP_IDS.ESSENTIALS_COUNTRY) return !!localCountry;
          if (stepId === STEP_IDS.ESSENTIALS_DATE) return true;
          if (stepId === STEP_IDS.ESSENTIALS_GUESTS) return !!(localGuestCount && !isNaN(localGuestCount) && parseInt(localGuestCount) >= 1);
          if (stepId === STEP_IDS.ESSENTIALS_BUDGET) return !!(localBudget && localBudget >= getMinBudget(localCountry));
          if (stepId === STEP_IDS.ESSENTIALS_ZIP) {
            const postalVal = validatePostalCode(localZipCode, localCountry);
            return !!postalVal.valid;
          }
          if (stepId === STEP_IDS.THEME_CONCEPT) {
            return localThemeInput.trim().length > 5 && !localThemeInput.includes('Theme Mode:');
          }
          return true;
        };

        const getLocalStepOptional = (stepId) => {
          return ![
            STEP_IDS.EVENT_TYPE,
            STEP_IDS.FOCUS,
            STEP_IDS.EVENT_SPECIFICS_WHO,
            STEP_IDS.EVENT_SPECIFICS_AGE,
            STEP_IDS.ESSENTIALS_COUNTRY,
            STEP_IDS.ESSENTIALS_GUESTS,
            STEP_IDS.ESSENTIALS_BUDGET,
            STEP_IDS.ESSENTIALS_ZIP,
            STEP_IDS.THEME_MODE,
            STEP_IDS.THEME_CONCEPT,
            STEP_IDS.FOOD_DIETARY,
            STEP_IDS.SUMMARY
          ].includes(stepId);
        };

        let foundStep = null;
        for (let i = 0; i < hydratedSteps.length - 1; i++) {
          const stepId = hydratedSteps[i];
          if (!getLocalStepOptional(stepId) && !getLocalStepValid(stepId)) {
            foundStep = i + 1;
            break;
          }
        }
        const targetStep = foundStep || hydratedSteps.length;
        setCurrentStep(targetStep);
        setMaxReachedStep(getTargetMaxStep(targetStep));
      } else if (initialStep) {
        const targetStep = Number(initialStep) || 1;
        setCurrentStep(targetStep);
        setMaxReachedStep(getTargetMaxStep(targetStep));
      } else {
        const restoredStep = readStepFromUrl(hydratedSteps) || readStoredStep(hydratedSteps);
        const targetStep = restoredStep || 1;
        setCurrentStep(targetStep);
        setMaxReachedStep(getTargetMaxStep(targetStep));
      }
      appliedInitialStepRef.current = true;
    }
    scrollWizardToTop('auto');
    hasHydratedLoadedDataRef.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEventData, currentPlanId, initialStep, readStepFromUrl, readStoredStep, scrollWizardToTop]);

  useEffect(() => {
    try {
      const storagePlanId = currentPlanId || initialEventData?.plan_id || initialEventData?._id || initialEventData?.id || 'new';
      sessionStorage.setItem(`cmp_active_step_${storagePlanId}`, String(currentStep));
      if (storagePlanId === 'new') {
        sessionStorage.setItem('cmp_active_step_last', String(currentStep));
      }
    } catch (e) {
      console.error('Error saving active step to sessionStorage', e);
    }
  }, [currentStep, currentPlanId, initialEventData?.plan_id, initialEventData?._id, initialEventData?.id]);

  useEffect(() => {
    if (typeof window === 'undefined' || billingOverlayOnly) return;
    try {
      const url = new URL(window.location.href);
      if (!url.pathname.startsWith('/wizard/details') && !url.pathname.startsWith('/wizard/detail')) return;
      const nextStepParam = currentStep === activeStepIds.length
        ? 'summary'
        : activeStepIds[currentStep - 1] || String(currentStep);
      if (url.searchParams.get('step') === nextStepParam) return;
      url.searchParams.set('step', nextStepParam);
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    } catch (_) {}
  }, [activeStepIds, billingOverlayOnly, currentStep]);

  useEffect(() => {
    if (appliedInitialStepRef.current) return;
    const restoredMaxStep = Number(initialEventData?.draft_max_reached_step);
    const getTargetMaxStep = (targetStep) => {
      return (Number.isFinite(restoredMaxStep) && restoredMaxStep > 0)
        ? Math.max(restoredMaxStep, targetStep)
        : targetStep;
    };

    if (initialStep === 'last') {
      const targetStep = activeStepIds.length;
      setCurrentStep(targetStep);
      setMaxReachedStep(getTargetMaxStep(targetStep));
      appliedInitialStepRef.current = true;
      return;
    }
    if (initialStep === 'firstIncomplete') {
      let foundStep = null;
      for (let i = 0; i < activeStepIds.length - 1; i++) {
        const stepIndex = i + 1;
        if (!isStepOptional(stepIndex) && !isStepValid(stepIndex)) {
          foundStep = stepIndex;
          break;
        }
      }
      const targetStep = foundStep || activeStepIds.length;
      setCurrentStep(targetStep);
      setMaxReachedStep(getTargetMaxStep(targetStep));
      appliedInitialStepRef.current = true;
      return;
    }
    if (initialStep) {
      const targetStep = Number(initialStep) || 1;
      setCurrentStep(targetStep);
      setMaxReachedStep(getTargetMaxStep(targetStep));
      appliedInitialStepRef.current = true;
    } else {
      const restored = readStepFromUrl(activeStepIds) || readStoredStep(activeStepIds);
      if (restored) {
        setCurrentStep(restored);
        setMaxReachedStep(getTargetMaxStep(restored));
        appliedInitialStepRef.current = true;
      }
    }
  }, [activeStepIds, initialStep, readStepFromUrl, readStoredStep, initialEventData]);

  // Expose method to get current form data (for unsaved changes detection)
  useImperativeHandle(ref, () => ({
    getCurrentFormData: () => getFormSnapshot(),
    getCurrentStep: () => {
      return currentStep;
    },

    hasFormData: () => {
      // Check if any form fields have been filled
      // Simple check: if any required or optional fields have values
      const hasEventType = eventType && eventType.trim() !== '';
      const hasGuestCount = guestCount && guestCount.trim() !== '';
      const hasZipCode = zipCode && zipCode.trim() !== '';
      const hasDate = date && date.trim() !== '';
      const hasAgeRange = Array.isArray(ageRange) ? ageRange.length > 0 : (ageRange && ageRange.trim() !== '');
      const hasVenue = venue && venue.trim() !== '';
      const hasVenueType = venueType && venueType.trim() !== '';
      const hasTimeOfDay = Array.isArray(timeOfDay) ? timeOfDay.length > 0 : (timeOfDay && timeOfDay.trim() !== '');
      const hasMeals = selectedMeals.length > 0;
      const hasDietaryRestrictions = dietaryRestrictions.length > 0;
      const hasDietaryInstructions = dietaryInstructions.trim() !== '';
      const hasThemeInput = themeInput.trim() !== '';
      const hasAdditionalDetails = additionalDetails.trim() !== '';
      const hasThemeStyles = selectedThemeStyles.length > 0;
      const hasThemeModifiers = selectedThemeModifiers.length > 0;

      const result = !!(
        hasEventType ||
        hasGuestCount ||
        hasZipCode ||
        hasDate ||
        hasAgeRange ||
        hasVenue ||
        hasVenueType ||
        hasTimeOfDay ||
        hasMeals ||
        hasDietaryRestrictions ||
        hasDietaryInstructions ||
        hasThemeInput ||
        hasAdditionalDetails ||
        hasThemeStyles ||
        hasThemeModifiers
      );

      console.log('DEBUG hasFormData - result:', result, {
        eventType, guestCount, zipCode, date, ageRange, venue, venueType, timeOfDay,
        selectedMeals: selectedMeals.length, dietaryRestrictions: dietaryRestrictions.length,
        hasEventType, hasGuestCount, hasZipCode, hasTimeOfDay
      });

      return result;
    }
  }));

  useEffect(() => {
    const newSteps = getActiveSteps(normalizeFocusSelection(focusSelection));
    if (JSON.stringify(newSteps) !== JSON.stringify(activeStepIds)) {
      // Find current step ID before update
      const currentId = activeStepIds[currentStep - 1];
      setActiveStepIds(newSteps);
      setActiveMacroSteps(getActiveMacroSteps(newSteps));
      activeStepIdsRef.current = newSteps;

      // Re-sync currentStep to the same ID if its position shifted
      const newIndex = newSteps.indexOf(currentId);
      if (newIndex !== -1) {
        if (newIndex + 1 !== currentStep) {
          console.log(`🔄 Syncing step index from ${currentStep} to ${newIndex + 1} for ID: ${currentId}`);
          setCurrentStep(newIndex + 1);
        }
      } else {
        // Clamp to valid bounds if old step is gone
        const clamped = Math.max(1, Math.min(currentStep, newSteps.length));
        console.log(`🔄 Old step ${currentId} is gone. Clamping index from ${currentStep} to ${clamped}`);
        setCurrentStep(clamped);
      }
    }
  }, [focusSelection, eventType, themeInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle return to summary change detection
  useEffect(() => {
    if (currentStep === totalSteps && editSnapshot) {
      const currentDataStr = JSON.stringify(getFormSnapshot());
      if (currentDataStr === editSnapshot) {
        console.log("No changes detected, clearing highlight");
        setLastEditedMacroId(null);
      } else {
        console.log("Changes detected, keeping highlight");
      }
      setEditSnapshot(null);
    }
  }, [currentStep, totalSteps, editSnapshot]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleThemeStyleToggle = (styleId) => {
    setSelectedThemeStyles(prev => {
      if (prev.includes(styleId)) {
        return prev.filter(id => id !== styleId);
      }
      if (prev.length >= 4) return prev;
      return [...prev, styleId];
    });
  };

  const handleModifierToggle = (modifierId) => {
    setSelectedThemeModifiers(prev => {
      if (prev.includes(modifierId)) {
        return prev.filter(id => id !== modifierId);
      }
      return [...prev, modifierId];
    });
  };

  const validateForm = () => {
    // Validate all active steps
    let isValid = true;
    for (let i = 1; i <= totalSteps; i++) {
      // Skip optional steps if they are valid or empty (validation logic handles mandatory checks)
      // But here we want to ensure *mandatory* steps are filled.
      if (!isStepOptional(i) && !isStepValid(i)) {
        isValid = false;
        // Trigger errors for invalid mandatory steps
        validateStep(i);
      }
    }
    return isValid;
  };

  const isFormValid = () => {
    // Check if critical/mandatory fields are filled based on current focus
    // We can reuse isStepValid logic for all mandatory steps
    const mandatorySteps = activeStepIds.map((id, index) => ({ id, index: index + 1 }))
      .filter(s => !isStepOptional(s.index));

    return mandatorySteps.every(s => isStepValid(s.index));
  };

  const getCtaLabel = () => {
    const focus = Array.isArray(focusSelection) ? focusSelection : [];
    const isTheme = focus.includes('everything') || focus.includes('theme_ideas');
    if (isTheme) {
      const count = isAuthenticated ? themeGenerationCount : 2;
      return `Generate ${count} Themes`;
    }
    return 'Generate custom plan';
  };

  // Restore step from initialStep prop (e.g., after sign-in)
  useEffect(() => {
    if (appliedInitialStepRef.current) return;
    if (initialStep && initialStep !== 'last' && initialStep !== 'firstIncomplete' && Number(initialStep) > 1 && Number(initialStep) <= 17) {
      console.log('DEBUG EventInputScreen - Restoring step from initialStep:', initialStep);
      const targetStep = Number(initialStep);
      setCurrentStep(targetStep);
      const restoredMaxStep = Number(initialEventData?.draft_max_reached_step);
      const targetMaxStep = (Number.isFinite(restoredMaxStep) && restoredMaxStep > 0)
        ? Math.max(restoredMaxStep, targetStep)
        : targetStep;
      setMaxReachedStep(targetMaxStep);
      appliedInitialStepRef.current = true;
    }
  }, [initialStep, initialEventData]);

  // Update baseline after successful draft save from parent/modal
  useEffect(() => {
    if (saveSuccessToken > 0) {
      formBaselineRef.current = getDirtyCheckSnapshot();
      setIsFormDirty(false);
      if (onDirtyChange) onDirtyChange(false);
      clearUnsavedChanges();
    }
  }, [saveSuccessToken]);

  // Detect compact viewports on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
      setIsSwipeViewport(window.innerWidth < 1024); // phones and tablets
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!onRegisterSwipeHandlers) return undefined;

    if (!isSwipeViewport) {
      onRegisterSwipeHandlers({ onBack: null, onNext: null });
      return undefined;
    }

    const canGoNext = isStepOptional(currentStep) || isStepValid(currentStep);
    const hasPrevious = currentStep > 1;

    onRegisterSwipeHandlers({
      onBack: hasPrevious ? () => handleBack() : null,
      onNext: canGoNext ? () => handleNext() : null
    });

    return () => onRegisterSwipeHandlers({ onBack: null, onNext: null });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStep,
    isSwipeViewport,
    onRegisterSwipeHandlers,
    eventType,
    customEventType,
    budget,
    guestCount,
    zipCode,
    eventSpecifics
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Removed transition direction tracking - no longer needed for animations
  useEffect(() => {
    if (!isSwipeViewport) return;
    previousStepRef.current = currentStep;
  }, [currentStep, isSwipeViewport]);

  // Handle goToStep16Key prop - when this changes, go to step 16 (navigating back from themes)
  useEffect(() => {
    if (goToStep16Key > 0 && existingThemes && existingThemes.length > 0) {
      setCurrentStep(totalSteps);
    }
  }, [goToStep16Key, existingThemes, totalSteps]);

  const getStepAnimationClass = (step) => {
    // Removed complex animations - simple fade is handled by key prop
    return '';
  };

  const getFormSpacingClass = () => {
    return 'space-y-5 pt-1.5';
  };

  const getCardOffsetClass = () => {
    return 'mt-1.5';
  };

  const getCardAnimationClass = () => {
    if (!isSwipeViewport) return '';
    return stepDirection === 'backward'
      ? 'motion-reduce:animate-none animate-in fade-in slide-in-from-left-3 duration-200 ease-out'
      : 'motion-reduce:animate-none animate-in fade-in slide-in-from-right-3 duration-200 ease-out';
  };

  const eventTypes = STATIC_EVENT_TYPES;

  const ageRanges = [
    { value: 'Kids (0-12)', label: 'Kids', sublabel: '0-12 years', color: 'from-yellow-400 to-orange-400', icon: '' },
    { value: 'Teens (13-17)', label: 'Teens', sublabel: '13-17 years', color: 'from-blue-400 to-purple-400', icon: '' },
    { value: 'Young Adults (18-29)', label: 'Young Adults', sublabel: '18-29 years', color: 'from-pink-400 to-rose-400', icon: '' },
    { value: 'Adults (30-49)', label: 'Adults', sublabel: '30-49 years', color: 'from-indigo-500 to-blue-500', icon: '' },
    { value: 'Adults (50+)', label: 'Seniors', sublabel: '50+ years', color: 'from-purple-500 to-indigo-500', icon: '' },
    { value: 'Mixed Ages', label: 'Mixed Ages', sublabel: 'All ages', color: 'from-green-400 to-teal-400', icon: '' }
  ];



  const themeStyles = [
    { id: 'classic', label: 'Classic', description: 'Elegant & timeless', icon: '👑', color: 'from-amber-600 to-orange-600' },
    { id: 'modern', label: 'Modern', description: 'Sleek & trendy', icon: '✨', color: 'from-blue-500 to-cyan-500' },
    { id: 'whimsical', label: 'Whimsical', description: 'Fun & playful', icon: '🎪', color: 'from-pink-400 to-rose-400' },
    { id: 'adventure', label: 'Adventure', description: 'Dynamic & bold', icon: '🏔️', color: 'from-green-500 to-emerald-500' },
    { id: 'minimalist', label: 'Minimalist', description: 'Clean & simple', icon: '⚪', color: 'from-gray-400 to-gray-600' },
    { id: 'rustic', label: 'Rustic', description: 'Natural & cozy', icon: '🌾', color: 'from-amber-700 to-orange-700' },
    { id: 'luxury', label: 'Luxury', description: 'Premium & refined', icon: '💎', color: 'from-purple-600 to-indigo-600' },
    { id: 'vintage', label: 'Vintage', description: 'Retro & nostalgic', icon: '📻', color: 'from-rose-600 to-pink-600' }
  ];

  const themeModifiers = useMemo(() => {
    const focus = Array.isArray(focusSelection) ? focusSelection : [];

    // 1. Theme Ideas Focus
    if (focus.includes('theme_ideas')) {
      return [
        { id: 'whimsical', label: 'Whimsical', description: 'Fun & playful vibe', icon: '🎪', color: 'from-pink-100 to-rose-100', text: 'text-rose-700' },
        { id: 'modern', label: 'Modern', description: 'Sleek & trendy look', icon: '✨', color: 'from-blue-100 to-cyan-100', text: 'text-blue-700' },
        { id: 'minimal', label: 'Minimal clean', description: 'Less clutter, more impact', icon: '⚪', color: 'from-gray-100 to-slate-100', text: 'text-slate-700' },
        { id: 'bold-color', label: 'Bold color pop', description: 'High-contrast palettes', icon: '🎨', color: 'from-red-100 to-orange-100', text: 'text-red-700' },
        { id: 'vintage', label: 'Vintage-inspired', description: 'Retro & nostalgic details', icon: '📻', color: 'from-rose-100 to-pink-100', text: 'text-rose-700' },
        { id: 'luxury', label: 'Luxe touches', description: 'Glam accents & finishes', icon: '💎', color: 'from-purple-100 to-fuchsia-100', text: 'text-purple-700' },
        { id: 'nature', label: 'Nature-inspired', description: 'Organic, green & outdoor beauty', icon: '🌿', color: 'from-green-100 to-emerald-100', text: 'text-emerald-700' },
        { id: 'diy', label: 'DIY-friendly', description: 'Handcrafted elements', icon: '🛠️', color: 'from-indigo-100 to-blue-100', text: 'text-indigo-700' }
      ];
    }

    // 2. Plan Everything / Mixed Focus (if focus has everything, or has 0 options)
    if (focus.includes('everything') || focus.length === 0) {
      return [
        { id: 'budget-smart', label: 'Budget-smart', description: 'Prioritize affordable alternatives', icon: '💸', color: 'from-amber-100 to-orange-100', text: 'text-amber-700' },
        { id: 'kid-friendly', label: 'Kid-friendly', description: 'Simple, guest-safe favorites', icon: '🧸', color: 'from-pink-100 to-rose-100', text: 'text-rose-700' },
        { id: 'diy', label: 'DIY-friendly', description: 'Printables, crafts, and simple setup', icon: '🛠️', color: 'from-indigo-100 to-blue-100', text: 'text-indigo-700' },
        { id: 'outdoor-ready', label: 'Outdoor-ready', description: 'Wind, sun, and outdoor-friendly choices', icon: '🌤️', color: 'from-teal-100 to-emerald-100', text: 'text-teal-700' },
        { id: 'photo-backdrop', label: 'Photo-ready backdrop', description: 'A standout spot for guest photos', icon: '📸', color: 'from-purple-100 to-fuchsia-100', text: 'text-purple-700' },
        { id: 'buffet', label: 'Buffet & grazing', description: 'Casual self-serve layout', icon: '🍇', color: 'from-orange-100 to-amber-100', text: 'text-orange-700' },
        { id: 'high-energy', label: 'High-energy activities', description: 'Relays, movement, and active play', icon: '⚡', color: 'from-orange-100 to-red-100', text: 'text-orange-700' },
        { id: 'lighting', label: 'Ambient lighting', description: 'Candles, fairy lights, and warm glow', icon: '✨', color: 'from-sky-100 to-cyan-100', text: 'text-sky-700' },
        { id: 'reusable', label: 'Reusable items', description: 'Multi-use and lower-waste picks', icon: '🌿', color: 'from-teal-100 to-emerald-100', text: 'text-teal-700' },
        { id: 'luxury', label: 'Premium upgrades', description: 'Elevated experience and premium touches', icon: '💎', color: 'from-purple-100 to-fuchsia-100', text: 'text-purple-700' }
      ];
    }

    // 3. Pool definitions for specific intents
    const pools = {
      food: [
        { id: 'budget-smart', label: 'Budget-smart', description: 'Affordable crowd-pleasers', color: 'from-amber-100 to-orange-100', text: 'text-amber-700', icon: '💸' },
        { id: 'kid-friendly', label: 'Kid-friendly menu', description: 'Simple, guest-safe favorites', color: 'from-pink-100 to-rose-100', text: 'text-rose-700', icon: '🧸' },
        { id: 'make-ahead', label: 'Make-ahead dishes', description: 'Prep before the event starts', color: 'from-blue-100 to-indigo-100', text: 'text-indigo-700', icon: '🥣' },
        { id: 'low-mess', label: 'Low-mess finger foods', description: 'Easy to serve and clean up', color: 'from-gray-100 to-slate-100', text: 'text-slate-700', icon: '🥪' },
        { id: 'vegetarian', label: 'Vegetarian-friendly', description: 'Strong plant-forward options', color: 'from-green-100 to-emerald-100', text: 'text-emerald-700', icon: '🥗' },
        { id: 'buffet', label: 'Buffet & grazing', description: 'Casual self-serve layout', color: 'from-orange-100 to-amber-100', text: 'text-orange-700', icon: '🍇' },
        { id: 'luxury', label: 'Premium menu upgrades', description: 'Elevated ingredients and presentation', color: 'from-purple-100 to-fuchsia-100', text: 'text-purple-700', icon: '💎' },
        { id: 'bold-flavors', label: 'Bold flavors', description: 'Rich, memorable flavor profiles', color: 'from-red-100 to-orange-100', text: 'text-red-700', icon: '🌶️' }
      ],
      decor: [
        { id: 'budget-smart', label: 'Budget-smart', description: 'High-impact, low-cost styling', color: 'from-amber-100 to-orange-100', text: 'text-amber-700', icon: '💸' },
        { id: 'diy', label: 'DIY-friendly decor', description: 'Printables, crafts, and simple setup', color: 'from-indigo-100 to-blue-100', text: 'text-indigo-700', icon: '🛠️' },
        { id: 'photo-backdrop', label: 'Photo-ready backdrop', description: 'A standout spot for guest photos', color: 'from-purple-100 to-fuchsia-100', text: 'text-purple-700', icon: '📸' },
        { id: 'table-setup', label: 'Tablescape focus', description: 'Place settings, centerpieces, and table styling', color: 'from-yellow-100 to-amber-100', text: 'text-yellow-700', icon: '🕯️' },
        { id: 'balloons', label: 'Balloon moments', description: 'Arches, garlands, and festive accents', color: 'from-pink-100 to-rose-100', text: 'text-rose-700', icon: '🎈' },
        { id: 'lighting', label: 'Ambient lighting', description: 'Candles, fairy lights, and warm glow', color: 'from-sky-100 to-cyan-100', text: 'text-sky-700', icon: '✨' },
        { id: 'outdoor-ready', label: 'Weather-safe decor', description: 'Wind, sun, and outdoor-friendly choices', color: 'from-teal-100 to-emerald-100', text: 'text-teal-700', icon: '🌤️' },
        { id: 'bold-color', label: 'Bold color pop', description: 'High-contrast, expressive styling', color: 'from-red-100 to-orange-100', text: 'text-red-700', icon: '🎨' }
      ],
      activities: [
        { id: 'kid-friendly', label: 'Kid-safe games', description: 'Fun, safe options for children', color: 'from-pink-100 to-rose-100', text: 'text-rose-700', icon: '🧸' },
        { id: 'high-energy', label: 'High-energy activities', description: 'Relays, movement, and active play', color: 'from-orange-100 to-red-100', text: 'text-orange-700', icon: '⚡' },
        { id: 'calm', label: 'Calm & low-key', description: 'Trivia, conversation, and relaxed games', color: 'from-sky-100 to-blue-100', text: 'text-sky-700', icon: '🧩' },
        { id: 'outdoor-ready', label: 'Outdoor games', description: 'Lawn games and open-space activities', color: 'from-green-100 to-emerald-100', text: 'text-emerald-700', icon: '🌳' },
        { id: 'team-games', label: 'Team challenges', description: 'Group activities and friendly competition', color: 'from-indigo-100 to-blue-100', text: 'text-indigo-700', icon: '🤝' },
        { id: 'crafts', label: 'Arts & crafts', description: 'Creative hands-on projects', color: 'from-yellow-100 to-amber-100', text: 'text-yellow-700', icon: '🎨' },
        { id: 'music-dance', label: 'Music & dance', description: 'Karaoke, dancing, and rhythm-based fun', color: 'from-purple-100 to-indigo-100', text: 'text-purple-700', icon: '🎵' },
        { id: 'all-ages', label: 'All-ages inclusive', description: 'Works for mixed-age groups', color: 'from-teal-100 to-emerald-100', text: 'text-teal-700', icon: '✨' }
      ],
      shopping: [
        { id: 'budget-smart', label: 'Budget-smart', description: 'Prioritize affordable alternatives', color: 'from-amber-100 to-orange-100', text: 'text-amber-700', icon: '💸' },
        { id: 'buy-online', label: 'Online delivery', description: 'Amazon, Etsy, and delivery-friendly picks', color: 'from-sky-100 to-blue-100', text: 'text-sky-700', icon: '📦' },
        { id: 'buy-local', label: 'Shop local', description: 'Nearby stores and local vendors', color: 'from-green-100 to-emerald-100', text: 'text-emerald-700', icon: '🏬' },
        { id: 'rent-items', label: 'Rental focus', description: 'Rent larger or one-time-use items', color: 'from-indigo-100 to-purple-100', text: 'text-indigo-700', icon: '🤝' },
        { id: 'diy', label: 'DIY-friendly sourcing', description: 'Craft materials and make-at-home items', color: 'from-yellow-100 to-amber-100', text: 'text-yellow-700', icon: '🛠️' },
        { id: 'reusable', label: 'Reusable items', description: 'Multi-use and lower-waste picks', color: 'from-teal-100 to-emerald-100', text: 'text-teal-700', icon: '🌿' },
        { id: 'fast-pickup', label: 'Same-day pickup', description: 'Fast local pickup options', color: 'from-red-100 to-orange-100', text: 'text-red-700', icon: '⏱️' },
        { id: 'eco-friendly', label: 'Eco-friendly picks', description: 'Compostable, recyclable, or greener products', color: 'from-emerald-100 to-green-100', text: 'text-emerald-700', icon: '🌿' }
      ]
    };

    // 4. Extract active intents in order
    const activeIntents = ['food', 'decor', 'activities', 'shopping'].filter(intent => focus.includes(intent));

    // 5. If it's a single intent focus, return its pool directly
    if (activeIntents.length === 1) {
      return pools[activeIntents[0]];
    }

    // 6. Mixed Focuses: Merge and deduplicate with balance (max 10 options)
    const merged = [];
    const activePools = activeIntents.map(intent => [...pools[intent]]);

    // Budget-smart gets priority if it exists in any pool
    let hasBudgetSmart = false;
    for (const pool of activePools) {
      const idx = pool.findIndex(item => item.id === 'budget-smart');
      if (idx !== -1) {
        if (!hasBudgetSmart) {
          merged.push(pool[idx]);
          hasBudgetSmart = true;
        }
      }
    }

    // Remove budget-smart from active pools to avoid duplicates
    activePools.forEach(pool => {
      const idx = pool.findIndex(item => item.id === 'budget-smart');
      if (idx !== -1) pool.splice(idx, 1);
    });

    let poolIdx = 0;
    let attempts = 0;
    const maxAttempts = 100;
    while (merged.length < 10 && attempts < maxAttempts) {
      attempts++;
      const currentPool = activePools[poolIdx];
      if (currentPool && currentPool.length > 0) {
        const item = currentPool.shift();
        if (!merged.some(existing => existing.id === item.id)) {
          merged.push(item);
        }
      }
      poolIdx = (poolIdx + 1) % activePools.length;
      if (activePools.every(pool => pool.length === 0)) break;
    }

    return merged.slice(0, 10);
  }, [focusSelection]);

  const venueOptions = [
    { value: 'Indoor', label: 'Indoor', icon: '🏠', color: 'from-blue-400 to-cyan-400', description: 'Inside venue' },
    { value: 'Outdoor', label: 'Outdoor', icon: '🌳', color: 'from-green-400 to-emerald-400', description: 'Outside venue' },
    { value: 'Mixed', label: 'Mixed', icon: '🏡', color: 'from-purple-400 to-pink-400', description: 'Both indoor & outdoor' }
  ];

  const venueTypeOptions = [
    { value: 'House', label: 'House', icon: '🏠', color: 'from-blue-400 to-cyan-400', spaces: ['Indoor', 'Outdoor', 'Mixed'] },
    { value: 'Resort', label: 'Resort', icon: '🏖️', color: 'from-yellow-400 to-orange-400', spaces: ['Indoor', 'Outdoor', 'Mixed'] },
    { value: 'Restaurant', label: 'Restaurant', icon: '🍽️', color: 'from-amber-500 to-orange-500', spaces: ['Indoor', 'Mixed'] },
    { value: 'Banquet Hall', label: 'Banquet Hall', icon: '🏛️', color: 'from-purple-500 to-pink-500', spaces: ['Indoor'] },
    { value: 'Gardens', label: 'Gardens', icon: '🌳', color: 'from-green-500 to-emerald-500', spaces: ['Outdoor', 'Mixed'] },
    { value: 'Hotel', label: 'Hotel', icon: '🏨', color: 'from-indigo-500 to-blue-500', spaces: ['Indoor', 'Mixed'] },
    { value: 'Community Center', label: 'Community Center', icon: '🏢', color: 'from-gray-400 to-gray-600', spaces: ['Indoor', 'Mixed'] },
    { value: 'Park', label: 'Park', icon: '🌲', color: 'from-green-400 to-teal-400', spaces: ['Outdoor'] },
    { value: 'Beach', label: 'Beach', icon: '🏖️', color: 'from-cyan-400 to-blue-400', spaces: ['Outdoor'] },
    { value: 'Other', label: 'Other', icon: '✨', color: 'from-gray-400 to-gray-500', spaces: ['Indoor', 'Outdoor', 'Mixed'] }
  ];

  const timeOfDayOptions = [
    { value: 'Morning', label: 'Morning', icon: '🌅', color: 'from-yellow-400 to-orange-400' },
    { value: 'Afternoon', label: 'Afternoon', icon: '☀️', color: 'from-orange-400 to-red-400' },
    { value: 'Evening', label: 'Evening', icon: '🌆', color: 'from-purple-400 to-indigo-400' },
    { value: 'Night', label: 'Night', icon: '🌙', color: 'from-indigo-500 to-blue-500' }
  ];



  const filteredMealOptions = useMemo(() => {
    if (!timeOfDay || timeOfDay.length === 0) {
      const defaultValues = ['breakfast', 'brunch', 'lunch', 'dinner', 'appetizers', 'snacks', 'desserts', 'cocktails'];
      return STATIC_MEAL_OPTIONS.filter(opt => defaultValues.includes(opt.value));
    }

    const visibleValues = new Set();
    const hasMorning = timeOfDay.includes('Morning');
    const hasAfternoon = timeOfDay.includes('Afternoon');
    const hasEvening = timeOfDay.includes('Evening') || timeOfDay.includes('Night');

    if (hasMorning) {
      ['breakfast', 'brunch', 'coffee_tea', 'snacks', 'desserts', 'cocktails'].forEach(v => visibleValues.add(v));
    }
    if (hasAfternoon) {
      ['lunch', 'appetizers', 'finger_foods', 'snacks', 'desserts', 'cocktails'].forEach(v => visibleValues.add(v));
    }
    if (hasEvening) {
      ['dinner', 'appetizers', 'snacks', 'desserts', 'cocktails', 'late_night_bites'].forEach(v => visibleValues.add(v));
    }

    return STATIC_MEAL_OPTIONS.filter(opt => visibleValues.has(opt.value));
  }, [timeOfDay]);

  const dietaryRestrictionOptions = [
    { value: 'none', label: 'No Restrictions', icon: '✅', color: 'from-gray-300 to-gray-400', description: 'No specific requirements' },
    { value: 'vegetarian', label: 'Vegetarian', icon: '🥗', color: 'from-green-400 to-emerald-400', description: 'No meat or fish' },
    { value: 'vegan', label: 'Vegan', icon: '🌱', color: 'from-green-500 to-teal-500', description: 'No animal products' },
    { value: 'pescatarian', label: 'Pescatarian', icon: '🐟', color: 'from-blue-400 to-cyan-400', description: 'Fish but no meat' },
    { value: 'halal', label: 'Halal', icon: '☪️', color: 'from-indigo-500 to-purple-500', description: 'Islamic dietary laws' },
    { value: 'kosher', label: 'Kosher', icon: '✡️', color: 'from-blue-500 to-indigo-500', description: 'Jewish dietary laws' },
    { value: 'gluten-free', label: 'Gluten-Free', icon: '🌾', color: 'from-amber-400 to-orange-400', description: 'No gluten' },
    { value: 'dairy-free', label: 'Dairy-Free', icon: '🥛', color: 'from-gray-400 to-gray-500', description: 'No dairy products' },
    { value: 'nut-free', label: 'Nut-Free', icon: '🥜', color: 'from-amber-500 to-yellow-500', description: 'No nuts' },
    { value: 'low-carb', label: 'Low-Carb', icon: '🥑', color: 'from-green-500 to-emerald-500', description: 'Reduced carbohydrates' },
    { value: 'keto', label: 'Keto', icon: '🥩', color: 'from-red-400 to-pink-400', description: 'Ketogenic diet' }
  ];

  const focusOptions = [
    { id: 'everything', label: 'Plan Everything', icon: '✨', desc: 'Decor, Food, Venue & More' },
    { id: 'theme_ideas', label: 'Themes & Ideas', icon: '💡', desc: 'Inspiration & Vibe' },
    { id: 'decor', label: 'Decor', icon: '🎈', desc: 'Decor, setup, colors, and styling' },
    { id: 'food', label: 'Food & Drinks', icon: '🍕', desc: 'Menu, drinks, cake, and dietary needs' },
    { id: 'activities', label: 'Activities', icon: '🎯', desc: 'Games, flow, and entertainment' },
    { id: 'shopping', label: 'Shopping List', icon: '🛒', desc: 'Items, quantities, and budget-friendly sourcing' }
  ];

  // Helper to get current Step ID
  const getCurrentStepId = () => activeStepIds[currentStep - 1];

  useEffect(() => {
    const stepId = activeStepIds[currentStep - 1] || 'unknown';
    trackEvent('wizard_step_viewed', {
      step_index: currentStep,
      step_id: stepId,
      total_steps: totalSteps
    });
  }, [currentStep, totalSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateStep = (stepIndex) => { // stepIndex is 1-based
    const newErrors = {};
    const stepId = activeStepIds[stepIndex - 1];

    if (stepId === STEP_IDS.EVENT_TYPE) {
      if (!eventType) newErrors.eventType = 'Please select an event type';
      if (eventType === 'Other' && !customEventType.trim()) newErrors.customEventType = 'Please specify';
    }

    if (stepId === STEP_IDS.FOCUS && (!Array.isArray(focusSelection) || focusSelection.length === 0)) {
      newErrors.focus = 'Please select what you need help with';
    }

    if (stepId === STEP_IDS.ESSENTIALS_COUNTRY && !country) {
      newErrors.country = 'Please select a country';
    }

    if (stepId === STEP_IDS.ESSENTIALS_GUESTS) {
      if (!guestCount || isNaN(guestCount) || parseInt(guestCount) < 1) newErrors.guestCount = 'Enter guests';
    }

    if (stepId === STEP_IDS.ESSENTIALS_BUDGET) {
      if (!budget || budget < getMinBudget(country)) newErrors.budget = 'Budget too low';
    }

    if (stepId === STEP_IDS.ESSENTIALS_ZIP) {
      const postalVal = validatePostalCode(zipCode, country);
      if (!postalVal.valid) newErrors.zipCode = 'Invalid postal code';
    }

    if (stepId === STEP_IDS.ESSENTIALS_DETAILS) {
      if (!guestCount || isNaN(guestCount) || parseInt(guestCount) < 1) newErrors.guestCount = 'Enter guests';
      const postalVal = validatePostalCode(zipCode, country);
      if (!postalVal.valid) newErrors.zipCode = 'Invalid postal code';
      if (budget < getMinBudget(country)) newErrors.budget = 'Budget too low';
    }

    if (stepId === STEP_IDS.FOOD_MEALS) {
      const isFoodActive = focusSelection.includes('food') || focusSelection.includes('everything');
      if (isFoodActive) {
        const visibleValues = filteredMealOptions.map(opt => opt.value);
        if (!Array.isArray(selectedMeals) || !selectedMeals.some(m => visibleValues.includes(m))) {
          newErrors.meals = 'Choose at least one menu focus to continue.';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isStepReachable = (targetStepIndex) => {
    return targetStepIndex <= maxReachedStep;
  };

  const isStepValid = (stepIndex) => {
    // Current implementation relied on hardcoded step numbers.
    // We map back to our ID based logic.
    const stepId = activeStepIds[stepIndex - 1];
    if (stepId === STEP_IDS.EVENT_TYPE) {
      if (eventType === 'Other') {
        return !!customEventType.trim();
      }
      return !!eventType;
    }
    if (stepId === STEP_IDS.FOCUS) return Array.isArray(focusSelection) && focusSelection.length > 0;

    // Sub-step validation
    if (stepId === STEP_IDS.EVENT_SPECIFICS_GENDER) {
      return !!eventSpecifics.gender;
    }

    if (stepId === STEP_IDS.EVENT_SPECIFICS_WHO) {
      if (['Birthday Party', 'Kids Birthday', 'Anniversary', 'Baby Shower', 'Graduation'].includes(eventType)) {
        return !!eventSpecifics.honoreeRelation;
      }
      return true; // Occasion details text area is optional for other event types
    }

    if (stepId === STEP_IDS.EVENT_SPECIFICS_AGE) {
      if (eventType === 'Baby Shower') {
        return true;
      }
      if (eventType === 'Birthday Party' || eventType === 'Kids Birthday') return !!eventSpecifics.ageRange;
      if (eventType === 'Anniversary') return !!eventSpecifics.milestone;
      if (eventType === 'Graduation') return !!eventSpecifics.subType;
      return true; // No required second step for Other
    }

    if (stepId === STEP_IDS.EVENT_SPECIFICS_EXTRA) {
      return true; // Optional extra info
    }

    if (stepId === STEP_IDS.ESSENTIALS_COUNTRY) return !!country;
    if (stepId === STEP_IDS.ESSENTIALS_DATE) return true; // Optional
    if (stepId === STEP_IDS.ESSENTIALS_GUESTS) return !!(guestCount && !isNaN(guestCount) && parseInt(guestCount) >= 1);
    if (stepId === STEP_IDS.ESSENTIALS_BUDGET) return !!(budget && budget >= getMinBudget(country));
    if (stepId === STEP_IDS.ESSENTIALS_ZIP) {
      const postalVal = validatePostalCode(zipCode, country);
      return !!postalVal.valid;
    }
    if (stepId === STEP_IDS.THEME_MODE) {
      const isThemeModeRequired = focusSelection.includes('everything') || focusSelection.includes('theme_ideas');
      if (isThemeModeRequired) {
        return themeMode && themeMode !== 'selection';
      }
      return true;
    }
    if (stepId === STEP_IDS.THEME_CONCEPT) {
      return themeInput.trim().length > 5 && !themeInput.includes('Theme Mode:');
    }
    if (stepId === STEP_IDS.FOOD_MEALS) {
      const isFoodActive = focusSelection.includes('food') || focusSelection.includes('everything');
      if (isFoodActive) {
        const visibleValues = filteredMealOptions.map(opt => opt.value);
        return Array.isArray(selectedMeals) && selectedMeals.some(m => visibleValues.includes(m));
      }
      return true;
    }
    if (stepId === STEP_IDS.FOOD_DIETARY) {
      const isFoodActive = focusSelection.includes('food') || focusSelection.includes('everything');
      if (isFoodActive) {
        return Array.isArray(dietaryRestrictions) && dietaryRestrictions.length > 0;
      }
      return true;
    }
    return true; // Optional steps
  };

  const isStepIdOptional = (stepId) => {
    if (!stepId) return true;

    // Special custom rules
    if (stepId === STEP_IDS.EVENT_SPECIFICS_WHO) {
      return !['Birthday Party', 'Kids Birthday', 'Anniversary', 'Baby Shower', 'Graduation'].includes(eventType);
    }
    if (stepId === STEP_IDS.THEME_MODE) {
      const isThemeModeRequired = focusSelection.includes('everything') || focusSelection.includes('theme_ideas');
      return !isThemeModeRequired;
    }
    if (stepId === STEP_IDS.FOOD_MEALS) {
      const isFoodActive = focusSelection.includes('food') || focusSelection.includes('everything');
      return !isFoodActive;
    }
    if (stepId === STEP_IDS.FOOD_DIETARY) {
      const isFoodActive = focusSelection.includes('food') || focusSelection.includes('everything');
      return !isFoodActive;
    }

    // Dynamic config checks for single & mixed intents
    const config = getPlanningIntentConfig(focusSelection);
    const fieldMapping = {
      [STEP_IDS.EVENT_TYPE]: 'event_type',
      [STEP_IDS.FOCUS]: 'focus',
      [STEP_IDS.ESSENTIALS_COUNTRY]: 'country',
      [STEP_IDS.ESSENTIALS_GUESTS]: 'guest_count',
      [STEP_IDS.ESSENTIALS_BUDGET]: 'budget',
      [STEP_IDS.ESSENTIALS_ZIP]: 'zip',
      [STEP_IDS.THEME_CONCEPT]: 'theme',
      [STEP_IDS.FOOD_DIETARY]: 'food',
    };

    const fieldName = fieldMapping[stepId];
    if (fieldName) {
      const isRequired = config.requiredFields.includes(fieldName);
      return !isRequired;
    }

    // Default fallback list
    return ![
      STEP_IDS.EVENT_TYPE,
      STEP_IDS.FOCUS,
      STEP_IDS.EVENT_SPECIFICS_WHO,
      STEP_IDS.EVENT_SPECIFICS_AGE,
      STEP_IDS.ESSENTIALS_COUNTRY,
      STEP_IDS.ESSENTIALS_GUESTS,
      STEP_IDS.ESSENTIALS_BUDGET,
      STEP_IDS.ESSENTIALS_ZIP,
      STEP_IDS.THEME_MODE,
      STEP_IDS.THEME_CONCEPT,
      STEP_IDS.FOOD_DIETARY,
      STEP_IDS.SUMMARY
    ].includes(stepId);
  };

  const isStepOptional = (stepIndex) => {
    const stepId = activeStepIds[stepIndex - 1];
    return isStepIdOptional(stepId);
  };

  const isFieldRequired = (stepId) => {
    const idx = activeStepIds.indexOf(stepId);
    if (idx === -1) return false;
    return !isStepIdOptional(stepId);
  };


  // Check if optional step has a selection
  const hasOptionalSelection = (stepIndex) => {
    const stepId = activeStepIds[stepIndex - 1];

    switch (stepId) {
      case STEP_IDS.ESSENTIALS_BUDGET:
        return !!budget;
      case STEP_IDS.EVENT_SPECIFICS_WHO:
        return !!eventSpecifics.honoreeRelation || (!!eventSpecifics.occasionDetails && eventSpecifics.occasionDetails.trim() !== '');
      case STEP_IDS.AGE_RANGE:
        return Array.isArray(ageRange) ? ageRange.length > 0 : !!ageRange;
      case STEP_IDS.VENUE:
        return !!venue;
      case STEP_IDS.VENUE_TYPE:
        return !!venueType && (venueType !== 'Other' || customVenueType.trim() !== '');
      case STEP_IDS.TIME:
        return Array.isArray(timeOfDay) ? timeOfDay.length > 0 : !!timeOfDay;
      case STEP_IDS.FOOD_SOURCE:
        return foodSource.length > 0;
      case STEP_IDS.CUISINE_TYPES:
        return cuisineTypes.length > 0;
      case STEP_IDS.FOOD_MEALS:
        return selectedMeals.length > 0;
      case STEP_IDS.FOOD_DIETARY:
        return dietaryRestrictions.length > 0 || dietaryInstructions.trim() !== '';
      case STEP_IDS.THEME_DEAILS:
        return selectedThemeModifiers.length > 0;
      case STEP_IDS.EVENT_SPECIFICS_GENDER:
        return !!eventSpecifics.gender;
      case STEP_IDS.EVENT_SPECIFICS_EXTRA:
        return !!eventSpecifics.interests || !!eventSpecifics.occasionDetails;
      case STEP_IDS.THEME_STYLES:
        return selectedThemeStyles.length > 0;
      case STEP_IDS.THEME_MODE:
        return themeInput.trim() !== '';
      case STEP_IDS.ADDITIONAL:
        return additionalDetails.trim() !== '';
      // Mandatory steps don't rely on 'optional selection' logic for next button text,
      // but if we ever treated them as optional, here's their check:
      case STEP_IDS.EVENT_TYPE: return !!eventType;
      case STEP_IDS.FOCUS: return Array.isArray(focusSelection) && focusSelection.length > 0;
      case STEP_IDS.ESSENTIALS_COUNTRY: return !!country;
      case STEP_IDS.ESSENTIALS_ZIP: return !!(zipCode && zipCode.trim() !== '');
      case STEP_IDS.ESSENTIALS_GUESTS: return !!guestCount;
      case STEP_IDS.ESSENTIALS_DATE: return !!date;
      case STEP_IDS.ESSENTIALS_DETAILS: return !!(guestCount && budget && zipCode);
      default: return false;
    }
  };

  const buildDraftEventData = () => {
    const parsedGuestCount = parseInt(guestCount, 10);
    const isStepReached = (stepId) => {
      const index = activeStepIds.indexOf(stepId);
      return index !== -1 && index < maxReachedStep;
    };

    return {
      event_type: eventType === 'Other' ? customEventType.trim() : eventType,
      custom_event_type: eventType === 'Other' ? customEventType.trim() : '',
      budget: isStepReached(STEP_IDS.ESSENTIALS_BUDGET) ? (budget !== null && budget !== undefined && budget !== '' ? Number(budget) : null) : null,
      budget_tolerance_percent: isStepReached(STEP_IDS.ESSENTIALS_BUDGET) ? budgetTolerance : null,
      guest_count: isStepReached(STEP_IDS.ESSENTIALS_GUESTS) ? (Number.isFinite(parsedGuestCount) ? parsedGuestCount : null) : null,
      country: isStepReached(STEP_IDS.ESSENTIALS_COUNTRY) ? LAUNCH_COUNTRY : null,
      zip_code: isStepReached(STEP_IDS.ESSENTIALS_ZIP) ? zipCode : null,
      date: isStepReached(STEP_IDS.ESSENTIALS_DATE) ? (date || null) : null,
      age_range: isStepReached(STEP_IDS.AGE_RANGE) ? (Array.isArray(ageRange) ? (ageRange.length > 0 ? ageRange : null) : (ageRange || null)) : null,
      venue: isStepReached(STEP_IDS.VENUE) ? (venue || null) : null,
      venue_type: isStepReached(STEP_IDS.VENUE_TYPE) ? (venueType === 'Other' ? customVenueType.trim() : (venueType || null)) : null,
      time_of_day: isStepReached(STEP_IDS.TIME) ? (Array.isArray(timeOfDay) ? (timeOfDay.length > 0 ? timeOfDay : null) : (timeOfDay || null)) : null,
      meals: isStepReached(STEP_IDS.FOOD_MEALS) ? (selectedMeals.length > 0 ? selectedMeals : null) : null,
      food_menu_structure: isStepReached(STEP_IDS.FOOD_MEALS) ? (Object.keys(foodMenuStructure).length > 0 ? foodMenuStructure : null) : null,
      cuisine_types: isStepReached(STEP_IDS.CUISINE_TYPES) ? getNormalizedCuisineTypes() : null,
      dietary_restrictions: isStepReached(STEP_IDS.FOOD_DIETARY) ? (dietaryRestrictions.length > 0 ? dietaryRestrictions : null) : null,
      dietary_instructions: isStepReached(STEP_IDS.FOOD_DIETARY) ? (dietaryInstructions.trim() || null) : null,
      theme_input: (isStepReached(STEP_IDS.THEME_MODE) || isStepReached(STEP_IDS.THEME_CONCEPT) || isStepReached(STEP_IDS.THEME_BROWSE_CATS) || isStepReached(STEP_IDS.THEME_BROWSE_TEMPLATES)) ? (themeInput.trim() || null) : null,
      additional_details: isStepReached(STEP_IDS.ADDITIONAL) ? (additionalDetails.trim() || null) : null,
      theme_styles: isStepReached(STEP_IDS.THEME_STYLES) ? (selectedThemeStyles.length > 0 ? selectedThemeStyles : null) : null,
      theme_modifiers: isStepReached(STEP_IDS.THEME_DEAILS) ? (selectedThemeModifiers.length > 0 ? selectedThemeModifiers : null) : null,
      focus_selection: isStepReached(STEP_IDS.FOCUS) ? (focusSelection || []) : null,
      theme_count: isAuthenticated ? themeGenerationCount : 2,
      event_specifics: (isStepReached(STEP_IDS.EVENT_SPECIFICS_WHO) || isStepReached(STEP_IDS.EVENT_SPECIFICS_GENDER) || isStepReached(STEP_IDS.EVENT_SPECIFICS_AGE) || isStepReached(STEP_IDS.EVENT_SPECIFICS_EXTRA)) ? (Object.keys(eventSpecifics).length > 0 ? eventSpecifics : null) : null,
      draft_step_index: maxReachedStep,
      draft_max_reached_step: maxReachedStep
    };
  };

  // Warn if currentPlanId is set on what appears to be a brand new flow (no initial event type)
  useEffect(() => {
    if (currentPlanId && (!initialEventData || !initialEventData.event_type)) {
      console.warn("EventInputScreen: currentPlanId is set but initialEventData has no event_type (possible dirty session state). ID:", currentPlanId);
    }
  }, [currentPlanId, initialEventData]);

  const isPersistedDraft = !!currentPlanId;
  const canSaveDraft = !!eventType && (eventType !== 'Other' || !!customEventType.trim()) && (!isPersistedDraft || isFormDirty);
  const draftSaveLabel = isPersistedDraft && !isFormDirty ? 'Saved' : 'Save Draft';

  const saveDraftButtonClass = canSaveDraft
    ? 'h-11 px-4 border border-zinc-300 bg-white text-zinc-700 font-semibold rounded-xl hover:bg-stone-50 transition text-sm whitespace-nowrap active:scale-[0.98]'
    : 'h-11 px-4 bg-gray-100 text-gray-400 font-semibold rounded-xl border border-gray-200 cursor-not-allowed text-sm whitespace-nowrap';

  const isSavingDraftRef = useRef(false);
  const handleSaveDraftClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSavingDraftRef.current || !isAuthenticated || !onSaveDraft || !eventType || !canSaveDraft) return;
    isSavingDraftRef.current = true;
    try {
      await onSaveDraft(buildDraftEventData());
    } catch (err) {
      console.error("Failed to save draft:", err);
    } finally {
      isSavingDraftRef.current = false;
    }
  };

  const minBudget = getMinBudget(country);

  // Adjust budget if it's outside the country's range when country changes
  const prevCountryRef = useRef(country);
  useEffect(() => {
    if (budget && budget < minBudget) {
      setBudget(minBudget);
    }

    // Only reset Zip Code if the country ACTUALLY changed from a previous user interaction, not on initial mount
    if (prevCountryRef.current !== country) {
      setZipCode('');
      prevCountryRef.current = country;
    }
  }, [country, minBudget]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync Venue Type with Venue Preference
  useEffect(() => {
    if (venue && venueType) {
      const selectedOpt = venueTypeOptions.find(o => o.value === venueType);
      if (selectedOpt && !selectedOpt.spaces.includes(venue)) {
        console.log(`🔄 Resetting venueType because it's incompatible with ${venue}`);
        setVenueType('');
      }
    }
  }, [venue, venueType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const activeSteps = activeStepIdsRef.current;
    const totalCount = activeSteps.length;

    // Only proceed if not on last step
    if (currentStep >= totalCount) {
      console.warn(`Already on last step (${currentStep}), cannot go next`);
      return;
    }

    // Validate step if it's mandatory
    if (!isStepOptional(currentStep) && !isStepValid(currentStep)) {
      console.warn(`Step ${currentStep} validation failed`);
      validateStep(currentStep);
      return;
    }

    let nextStep = currentStep + 1;

    // Logic to skip sub-steps
    if (getCurrentStepId() === STEP_IDS.EVENT_SPECIFICS_WHO) {
      if (eventType === 'Other') {
        // Skip Age step for Other
        while (nextStep <= totalSteps && activeStepIds[nextStep - 1] === STEP_IDS.EVENT_SPECIFICS_AGE) {
          nextStep++;
        }
      }
    } else if (getCurrentStepId() === STEP_IDS.THEME_MODE) {
       // All theme navigation is now handled by dynamic activeStepIds!
    } else if (getCurrentStepId() === STEP_IDS.THEME_CONCEPT) {
       // Done with custom
    } else if (getCurrentStepId() === STEP_IDS.THEME_BROWSE_CATS) {
       // If browse, next is templates. Handled by default order.
    }

    nextStep = Math.min(nextStep, totalSteps);
    console.log(`✅ Moving from step ${currentStep} to step ${nextStep} (total: ${totalSteps})`);
    trackEvent('wizard_step_completed', {
      step_index: currentStep,
      step_id: getCurrentStepId() || 'unknown',
      event_type: eventType,
      guest_count: guestCount
    });
    setStepDirection('forward');
    setCurrentStep(nextStep);
    // Scroll to top immediately and ensure it stays there
    scrollWizardToTop('auto');
  };

  const handleSkip = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const activeSteps = activeStepIdsRef.current;
    const totalCount = activeSteps.length;
    // Only proceed if not on last step
    if (currentStep >= totalCount) {
      console.warn(`Already on last step (${currentStep}), cannot skip`);
      return;
    }
    const nextStep = Math.min(currentStep + 1, totalCount);
    console.log(`⏭️  Skipping from step ${currentStep} to step ${nextStep} (total: ${totalCount})`);
    setStepDirection('forward');
    setCurrentStep(nextStep);
    // Scroll to top immediately
    scrollWizardToTop('auto');
  };

  const handleBack = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentStep === 1 && returnToSummaryMode) {
      setReturnToSummaryMode(false);
      setCurrentStep(totalSteps);
      scrollWizardToTop('auto');
      return;
    }
    let prevStep = currentStep - 1;

    // Logic to skip sub-steps in reverse
    if (getCurrentStepId() === STEP_IDS.EVENT_SPECIFICS_EXTRA) {
      if (eventType === 'Other') {
        // Skip Age step for Other
        while (prevStep > 0 && activeStepIds[prevStep - 1] === STEP_IDS.EVENT_SPECIFICS_AGE) {
          prevStep--;
        }
      }
    } else if (getCurrentStepId() === STEP_IDS.VENUE) {
      // Logic for venue skips if needed
    } else if (getCurrentStepId() === STEP_IDS.THEME_BROWSE_CATS) {
        // Mode is already correct
    } else if (getCurrentStepId() === STEP_IDS.THEME_CONCEPT) {
        // Mode is already correct
    }

    prevStep = Math.max(prevStep, 1);
    console.log(`Moving back from step ${currentStep} to step ${prevStep}`);
    setStepDirection('backward');
    setCurrentStep(prevStep);
    scrollWizardToTop('auto');
  };

  const shouldIgnoreWizardSwipe = (target) => {
    return !!target?.closest?.('button, input, textarea, select, a, [role="button"], [data-no-swipe="true"]');
  };

  const handleWizardTouchStart = (e) => {
    if (!isSwipeViewport || shouldIgnoreWizardSwipe(e.target)) return;
    const touch = e.touches?.[0];
    if (!touch) return;
    inputSwipeStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
  };

  const handleWizardTouchEnd = (e) => {
    const start = inputSwipeStartRef.current;
    inputSwipeStartRef.current = null;
    if (!isSwipeViewport || !start || shouldIgnoreWizardSwipe(e.target)) return;
    const touch = e.changedTouches?.[0];
    if (!touch) return;

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const elapsed = Date.now() - start.time;
    const isHorizontalSwipe = Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.35 && elapsed < 700;
    if (!isHorizontalSwipe) return;

    if (dx > 0 && currentStep > 1) {
      handleBack();
      return;
    }

    if (dx < 0 && currentStep < totalSteps) {
      if (isStepOptional(currentStep) && !hasOptionalSelection(currentStep)) {
        handleSkip();
      } else {
        handleNext();
      }
    }
  };

  const handleSubmit = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Only allow submission on the last step (single-step view on all screen sizes)
    if (currentStep !== totalSteps) {
      // Navigate to last step instead
      setStepDirection('forward');
      setCurrentStep(totalSteps);
      scrollWizardToTop('smooth');
      return;
    }

    // If themes already exist, navigate to them instead of regenerating,
    // unless the user intentionally came here from the plan screen to
    // re-estimate the active plan with new details.
    if (!shouldRecalculateCurrentPlan && existingThemes && existingThemes.length > 0 && onNavigateToThemes) {
      onNavigateToThemes();
      return;
    }

    const wantsRecalculate = shouldRecalculateCurrentPlan || forceRecalculate;
    if (hasCorePlanInputChanges && !wantsRecalculate) {
      setShowRecalculatePrompt(true);
      return;
    }

    if (!isFormValid()) {
      validateForm(); // Set errors for display
      
      // Find the first invalid step ID and navigate to it dynamically
      let targetIndex = -1;
      for (let i = 0; i < activeStepIds.length; i++) {
        const stepId = activeStepIds[i];
        const stepNum = i + 1;
        if (!isStepOptional(stepNum) && !isStepValid(stepNum)) {
          targetIndex = stepNum;
          break;
        }
      }
      
      if (targetIndex !== -1) {
        setCurrentStep(targetIndex);
      } else {
        // Fallback checks mapped dynamically to activeStepIds
        if (!eventType || (eventType === 'Other' && !customEventType.trim())) {
          const idx = activeStepIds.indexOf(STEP_IDS.EVENT_TYPE);
          if (idx !== -1) setCurrentStep(idx + 1);
        } else if (!country) {
          const idx = activeStepIds.indexOf(STEP_IDS.ESSENTIALS_COUNTRY);
          if (idx !== -1) setCurrentStep(idx + 1);
        } else if (!zipCode || !validatePostalCode(zipCode, country).valid) {
          const idx = activeStepIds.indexOf(STEP_IDS.ESSENTIALS_ZIP);
          if (idx !== -1) setCurrentStep(idx + 1);
        } else if (budget < 100) {
          const idx = activeStepIds.indexOf(STEP_IDS.ESSENTIALS_BUDGET);
          if (idx !== -1) setCurrentStep(idx + 1);
        } else if (!guestCount) {
          const idx = activeStepIds.indexOf(STEP_IDS.ESSENTIALS_GUESTS);
          if (idx !== -1) setCurrentStep(idx + 1);
        }
      }
      return;
    }

    const parsedGuestCount = parseInt(guestCount, 10);
    const eventData = withWorkflowFlags({
      event_type: eventType === 'Other' ? customEventType.trim() : eventType,
      custom_event_type: eventType === 'Other' ? customEventType.trim() : '',
      budget: budget !== null && budget !== undefined && budget !== '' ? Number(budget) : null,
      budget_tolerance_percent: budgetTolerance,
      guest_count: Number.isFinite(parsedGuestCount) ? parsedGuestCount : null,
      country: LAUNCH_COUNTRY,
      zip_code: zipCode,
      date: date || null,
      age_range: Array.isArray(ageRange) ? (ageRange.length > 0 ? ageRange : null) : (ageRange || null),
      venue: venue || null,
      venue_type: venueType === 'Other' ? customVenueType.trim() : (venueType || null),
      time_of_day: Array.isArray(timeOfDay) ? (timeOfDay.length > 0 ? timeOfDay : null) : (timeOfDay || null),
      meals: selectedMeals.length > 0 ? selectedMeals : null,
      food_menu_structure: Object.keys(foodMenuStructure).length > 0 ? foodMenuStructure : null,
      food_source: foodSource.length > 0 ? foodSource : null,
      cuisine_types: getNormalizedCuisineTypes(),
      dietary_restrictions: dietaryRestrictions.length > 0 ? dietaryRestrictions : null,
      dietary_instructions: dietaryInstructions.trim() || null,
      theme_input: themeInput.trim() || null,
      additional_details: additionalDetails.trim() || null,
      theme_styles: selectedThemeStyles.length > 0 ? selectedThemeStyles : null,
      theme_modifiers: selectedThemeModifiers.length > 0 ? selectedThemeModifiers : null,
      focus_selection: normalizeFocusSelection(focusSelection),
      theme_count: isAuthenticated ? themeGenerationCount : 2,
      event_specifics: Object.keys(eventSpecifics).length > 0 ? eventSpecifics : null,
      __recalculateCurrentPlan: wantsRecalculate && Boolean(selectedThemeForReturn)
    });

    setForceRecalculate(false);
    onSubmit(eventData);
  };

  const hasExistingThemes = !!(existingThemes && existingThemes.length > 0);
  const shouldRecalculateCurrentPlan = Boolean(initialEventData?.__recalculateCurrentPlan && selectedThemeForReturn);
  const [showRecalculatePrompt, setShowRecalculatePrompt] = useState(false);
  const [forceRecalculate, setForceRecalculate] = useState(false);
  const planBaselineGuestCount = Number(initialEventData?.__planEstimateCore?.guest_count ?? initialEventData?.guest_count ?? 0);
  const planBaselineBudget = Number(initialEventData?.__planEstimateCore?.budget ?? initialEventData?.budget ?? 0);
  const planBaselineDate = initialEventData?.__planEstimateCore?.date ?? initialEventData?.date ?? initialEventData?.event_date ?? '';
  const planInputBaselineRef = useRef({
    guest_count: planBaselineGuestCount,
    budget: planBaselineBudget,
    date: planBaselineDate
  });

  useEffect(() => {
    planInputBaselineRef.current = {
      guest_count: planBaselineGuestCount,
      budget: planBaselineBudget,
      date: planBaselineDate
    };
  }, [planBaselineGuestCount, planBaselineBudget, planBaselineDate]);
  const hasCorePlanInputChanges = Boolean(selectedThemeForReturn) && (
    parseInt(guestCount, 10) !== planInputBaselineRef.current.guest_count
    || Number(budget) !== planInputBaselineRef.current.budget
    || (date || '') !== (planInputBaselineRef.current.date || '')
  );
  const isUnrestrictedUser = !!(user?.is_unrestricted || ["sandheepr89@gmail.com", "sandheep.r.krishnan@gmail.com"].includes(user?.email));
  const showDevBillingSimulator = isAuthenticated && isUnrestrictedUser;
  const planningCredits = Number(user?.billing?.planning_credits || 0);
  const usedFreeGeneration = Number(user?.usage_stats?.theme_generations || 0) >= 1;
  const effectivePlanningCredits = devBillingMode === 'has_credit'
    ? 1
    : devBillingMode === 'free_used_no_credits'
      ? 0
      : planningCredits;
  const effectiveUsedFreeGeneration = devBillingMode === 'new_free'
    ? false
    : devBillingMode === 'free_used_no_credits' || devBillingMode === 'has_credit'
      ? true
      : usedFreeGeneration;
  const effectiveIsUnrestrictedUser = isUnrestrictedUser && devBillingMode === 'real';
  const effectiveSubscriptionActive = devBillingMode === 'real' && user?.billing?.subscription_status === 'active';
  const needsPaidGeneration = isAuthenticated && !effectiveIsUnrestrictedUser && effectiveUsedFreeGeneration && effectivePlanningCredits <= 0 && !hasExistingThemes;
  const needsPaidFreshSet = isAuthenticated && !effectiveIsUnrestrictedUser && effectiveUsedFreeGeneration && effectivePlanningCredits <= 0 && hasExistingThemes;
  const resolvedBillingPackages = billingPackages.length > 0 ? billingPackages : defaultBillingPackages;
  const creditPackOffers = resolvedBillingPackages.filter((pkg) => pkg.kind === 'credit_pack');
  const subscriptionOffers = resolvedBillingPackages.filter((pkg) => pkg.kind === 'subscription');
  const visibleSubscriptionOffers = effectiveSubscriptionActive ? [] : subscriptionOffers;
  const activeBillingOffers = billingModalTab === 'credits' ? creditPackOffers : visibleSubscriptionOffers;
  const selectedBillingOffer = activeBillingOffers.find((pkg) => pkg.id === selectedBillingPackageId) || null;
  const [portalMounted, setPortalMounted] = useState(false);

  useEffect(() => {
    setPortalMounted(true);
    return () => setPortalMounted(false);
  }, []);

  const formatBillingMoney = (amountMinor, currencyCode, options = {}) => (
    formatBillingMoneyExact(amountMinor, currencyCode, country || 'US', options)
  );

  const getBillingOfferTitle = (pkg) => {
    if (pkg.kind === 'credit_pack') {
      return `${pkg.credits} credit${pkg.credits === 1 ? '' : 's'}`;
    }
    if (pkg.id === 'plus_monthly') return 'Plus monthly';
    if (pkg.id === 'plus_annual') return 'Plus annual';
    return pkg.name;
  };

  const formatBillingOfferPrice = (pkg) => {
    const formattedAmount = formatBillingMoney(pkg.amount || 0, pkg.currency || 'usd');
    if (pkg.mode === 'subscription' && pkg.interval) {
      return `${formattedAmount}/${pkg.interval === 'year' ? 'yr' : 'mo'}`;
    }
    return formattedAmount;
  };

  const getBillingOfferBadge = (pkg) => {
    if (pkg.kind === 'subscription') {
      if (pkg.interval === 'year') {
        return `${pkg.annual_credits || 24} credits/year · 2 added monthly`;
      }
      return `${pkg.credits || 2} credits/month · rollover included`;
    }
    if (pkg.credits === 1) {
      return 'Unlocks 1 fresh set';
    }
    return `Unlocks ${pkg.credits} fresh sets`;
  };

  const getBillingOfferSavings = (pkg) => {
    let savingsAmount = Number(pkg?.savings_amount || 0);
    let savingsPercent = Number(pkg?.savings_percent || 0);

    if (!savingsAmount) {
      if (pkg.kind === 'credit_pack') {
        const singleOffer = creditPackOffers.find((offer) => offer.id === 'credit_single');
        const baseline = Number(singleOffer?.amount || 0) * Number(pkg.credits || 0);
        savingsAmount = Math.max(0, baseline - Number(pkg.amount || 0));
        savingsPercent = baseline > 0 ? Number(((savingsAmount / baseline) * 100).toFixed(1)) : 0;
      } else if (pkg.id === 'plus_annual') {
        const monthlyOffer = subscriptionOffers.find((offer) => offer.id === 'plus_monthly');
        const baseline = Number(monthlyOffer?.amount || 0) * 12;
        savingsAmount = Math.max(0, baseline - Number(pkg.amount || 0));
        savingsPercent = baseline > 0 ? Number(((savingsAmount / baseline) * 100).toFixed(1)) : 0;
      }
    }

    if (!savingsAmount) return '';
    return getSavingsCopy({ ...pkg, savings_amount: savingsAmount, savings_percent: savingsPercent }, country || 'US');
  };

  const getBillingOfferFinePrint = (pkg) => {
    if (pkg.kind === 'credit_pack' && pkg.price_source === 'stripe_price') {
      return 'Final local amount is shown in Stripe checkout';
    }
    if (pkg.kind === 'credit_pack') {
      return pkg.credits > 1 ? getPerCreditCopy(pkg, country || 'US') : 'Extra generation + included plan';
    }
    if (pkg.kind === 'subscription') {
      if (pkg.interval === 'year') {
        const monthlyEquivalent = pkg.monthly_equivalent_amount
          ? formatBillingMoney(pkg.monthly_equivalent_amount || 0, pkg.currency || 'usd')
          : null;
        return monthlyEquivalent
          ? `${monthlyEquivalent}/mo effective · unused credits roll over`
          : 'Unused credits roll over';
      }
      return 'Unused credits roll over month to month';
    }
    return '';
  };

  const getBillingModalTitle = () => (
    billingModalFlow === 'fresh_set' ? 'Choose a plan for a fresh set' : 'Add credits or manage subscription'
  );

  const getBillingModalDescription = () => (
    billingModalFlow === 'fresh_set'
      ? 'Pick the option that unlocks your next refined theme set and matching plan access.'
      : 'Choose a one-time credit pack or review subscription options.'
  );

  const handleManageSubscription = async () => {
    setBillingError('');
    setBillingLoadingPackage('portal');
    try {
      sessionStorage.setItem('craftmyparty_billing_return', JSON.stringify({
        source: 'event_input',
        screen: billingCloseTarget.screen === 'carousel' ? 'carousel' : 'input',
        restoreStep: 'last',
        eventData: {
          ...getFormSnapshot(),
          ...(billingCloseTarget.screen === 'carousel' && billingCloseTarget.returnThemeKey
            ? {
                __focusThemeKey: billingCloseTarget.returnThemeKey,
                __returnThemeKey: billingCloseTarget.returnThemeKey
              }
            : {})
        },
        themes: existingThemes || [],
        selectedTheme: selectedThemeForReturn || null,
        themePlans: themePlansForReturn && typeof themePlansForReturn === 'object' ? themePlansForReturn : {},
        shoppingItems: Array.isArray(shoppingItemsForReturn) ? shoppingItemsForReturn : [],
        planId: currentPlanId || null
      }));
      const origin = window.location.origin;
      const data = await createPortalSession(`${origin}/?billing=portal`);
      pushHistoryBeforeExternalCheckout();
      window.location.assign(data.portal_url);
    } catch (error) {
      setBillingError(error.message || 'Could not open subscription management.');
      setBillingLoadingPackage(null);
    }
  };

  const pickDefaultBillingOfferId = useCallback((offers, tab = billingModalTab) => {
    if (!Array.isArray(offers) || offers.length === 0) return null;
    if (tab === 'subscriptions') {
      return offers.find((pkg) => pkg.id === 'plus_annual')?.id || offers[0].id;
    }
    return offers.find((pkg) => pkg.id === 'credit_5')?.id || offers[0].id;
  }, [billingModalTab]);

  const isRecommendedBillingOffer = (pkg) => (
    pkg.id === 'credit_10' || pkg.id === 'plus_annual'
  );

  const handleBillingTabChange = (tab) => {
    setBillingModalTab(tab);
    const nextOffers = tab === 'credits' ? creditPackOffers : visibleSubscriptionOffers;
    setSelectedBillingPackageId(pickDefaultBillingOfferId(nextOffers, tab));
  };

  useEffect(() => {
    if (!showBillingOptionsModal) return;
    setSelectedBillingPackageId((currentId) => {
      if (currentId && activeBillingOffers.some((pkg) => pkg.id === currentId)) {
        return currentId;
      }
      return pickDefaultBillingOfferId(activeBillingOffers);
    });
  }, [showBillingOptionsModal, billingModalTab, activeBillingOffers, pickDefaultBillingOfferId]);

  const updateDevBillingMode = (mode) => {
    setDevBillingMode(mode);
    try {
      localStorage.setItem('craftmyparty_dev_billing_mode', mode);
      window.dispatchEvent(new CustomEvent('craftmyparty:dev-billing-mode-changed', { detail: mode }));
    } catch (_) {}
  };

  const openBillingOptionsModal = (flow = 'generation', options = {}) => {
    const closeTarget = options?.closeTarget
      ? {
          screen: options.closeTarget.screen === 'carousel' ? 'carousel' : 'input',
          returnThemeKey: String(options.closeTarget.returnThemeKey || '').trim()
        }
      : {
          screen: options?.returnToThemesOnClose ? 'carousel' : 'input',
          returnThemeKey: String(options?.returnThemeKey || '').trim()
        };

    setBillingError('');
    setBillingModalFlow(flow);
    const reqTab = options?.defaultTab || inlineBillingRequest?.tab || 'credits';
    setBillingModalTab(reqTab);
    const initialOffers = reqTab === 'credits' ? creditPackOffers : visibleSubscriptionOffers;
    setSelectedBillingPackageId(pickDefaultBillingOfferId(initialOffers, reqTab));
    setBillingCloseTarget(closeTarget);
    if (billingPackages.length === 0) {
      setBillingPackages(defaultBillingPackages);
    }
    loadBillingPackages();
    setShowBillingOptionsModal(true);
  };

  // closeFreshSetModal is invoked when the user cancels / dismisses the
  // fresh-set modal — NOT when they actually generate. Cancelling should
  // leave the user on the summary screen (it's where they were), regardless
  // of the legacy __returnToThemesAfterFreshSet flag. The post-generation
  // navigation flow in App.js already takes successful generations back to
  // the carousel automatically.
  const closeFreshSetModal = () => {
    setShowRegenerateModal(false);
    setRegenerateMode('append');
  };

  const closeBillingOptionsModal = () => {
    setShowBillingOptionsModal(false);
    const closeTarget = billingCloseTarget;
    setBillingCloseTarget({ screen: 'input', returnThemeKey: '' });
    if (billingOverlayOnly) {
      onBillingOverlayClose?.();
      return;
    }
    if (closeTarget.screen === 'carousel' && onNavigateToThemes) {
      onNavigateToThemes();
    }
  };

  const openedInlineBillingRef = useRef(false);

  useEffect(() => {
    if (!billingOverlayOnly || !inlineBillingRequest) return;
    if (openedInlineBillingRef.current) return;
    openedInlineBillingRef.current = true;
    openBillingOptionsModal(inlineBillingRequest.flow || 'generation', {
      closeTarget: {
        screen: inlineBillingRequest.returnToThemesOnClose ? 'carousel' : (inlineBillingRequest.screen === 'plan' ? 'plan' : 'input'),
        returnThemeKey: String(inlineBillingRequest.returnThemeKey || '').trim()
      }
    });
  }, [billingOverlayOnly, inlineBillingRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasExistingThemes || !window.__openFreshSetWorkflow) return;

    window.__openFreshSetWorkflow = false;
    setCurrentStep(totalSteps);

    window.setTimeout(() => {
      if (needsPaidFreshSet) {
        openBillingOptionsModal('fresh_set', {
          closeTarget: {
            screen: 'carousel',
            returnThemeKey: String(initialEventData?.__returnThemeKey || initialEventData?.__focusThemeKey || '').trim()
          }
        });
      } else {
        setShowRegenerateModal(true);
      }
    }, 0);
  }, [hasExistingThemes, needsPaidFreshSet, totalSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (typeof window === 'undefined' || !window.__openBillingWorkflow) return;

    const workflow = window.__openBillingWorkflow;
    window.__openBillingWorkflow = null;
    setCurrentStep(totalSteps);

    window.setTimeout(() => {
      openBillingOptionsModal(workflow?.flow || 'generation', {
        closeTarget: {
          screen: workflow?.returnToThemesOnClose ? 'carousel' : 'input',
          returnThemeKey: String(workflow?.returnThemeKey || '').trim()
        }
      });
    }, 0);
  }, [totalSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  const startCheckout = async (packageId) => {
    setBillingError('');
    setBillingLoadingPackage(packageId);
    try {
      sessionStorage.setItem('craftmyparty_billing_return', JSON.stringify({
        source: 'event_input',
        screen: billingCloseTarget.screen === 'carousel' ? 'carousel' : 'input',
        restoreStep: 'last',
        eventData: {
          ...getFormSnapshot(),
          ...(billingCloseTarget.screen === 'carousel' && billingCloseTarget.returnThemeKey
            ? {
                __focusThemeKey: billingCloseTarget.returnThemeKey,
                __returnThemeKey: billingCloseTarget.returnThemeKey
              }
            : {})
        },
        themes: existingThemes || [],
        selectedTheme: selectedThemeForReturn || null,
        themePlans: themePlansForReturn && typeof themePlansForReturn === 'object' ? themePlansForReturn : {},
        shoppingItems: Array.isArray(shoppingItemsForReturn) ? shoppingItemsForReturn : [],
        planId: currentPlanId || null
      }));
      const response = await fetch(`${BACKEND_URL}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await authUtils.getFreshAuthHeader())
        },
        body: JSON.stringify({
          package_id: packageId,
          country: LAUNCH_COUNTRY,
          success_url: `${window.location.origin}/?billing=success`,
          cancel_url: `${window.location.origin}/?billing=cancelled`
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = data?.detail;
        throw new Error(typeof detail === 'string' ? detail : detail?.message || 'Could not start checkout.');
      }
      if (data.checkout_url) {
        pushHistoryBeforeExternalCheckout();
        window.location.assign(data.checkout_url);
        return;
      }
      throw new Error('Stripe did not return a checkout link.');
    } catch (error) {
      const message = error.message || 'Could not start checkout.';
      setBillingError(message);
      console.error('Checkout start failed:', error);
    } finally {
      setBillingLoadingPackage(null);
    }
  };

  // Helper to render field titles with trailing asterisk or optional note bound to the last word
  const renderFieldLabel = (title, isRequired, optionalText = null) => {
    if (!title) return null;
    const parts = title.trim().split(' ');
    const lastWord = parts.pop();
    const leadingText = parts.join(' ');

    return (
      <span>
        {leadingText ? `${leadingText} ` : ''}
        <span className="whitespace-nowrap">
          {lastWord}
          {isRequired ? (
            <span className="text-red-500 select-none ml-1">*</span>
          ) : optionalText ? (
            <span className="text-gray-400 font-normal text-sm ml-1">({optionalText})</span>
          ) : null}
        </span>
      </span>
    );
  };

  const handleOpenExpressModal = () => {
    if (!isAuthenticated) {
      if (onRequestAuth) {
        onRequestAuth('signup');
      }
      return;
    }
    setShowExpressModal(true);
  };

  const renderProgressBar = ({ customSpacingClass } = {}) => {
    const currentStepId = getCurrentStepId();
    let currentMacroIndex = activeMacroSteps.findIndex(macro => macro.activeSubSteps.includes(currentStepId));
    if (currentMacroIndex === -1) currentMacroIndex = 0;

    const currentMacro = activeMacroSteps[currentMacroIndex];
    const totalMacroSteps = activeMacroSteps.length;
    const currentSubStepIndex = currentMacro?.activeSubSteps.indexOf(currentStepId) || 0;
    const totalSubSteps = currentMacro?.activeSubSteps.length || 1;

    return (
      <div className={`w-full ${customSpacingClass || ''}`}>
        {/* Compact Header Layout */}
        <div className="flex flex-col gap-3">
          {/* Top Row: Step Info & Save Button */}
          <div className="flex items-center justify-between h-8 relative">
            {/* Left: Step Indicator */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-bold text-gray-900 tabular-nums shrink-0">
                Step {currentMacroIndex + 1}
                {!(currentMacroIndex === 0 && currentSubStepIndex === 0) && (
                  <span className="text-gray-400 font-normal"> / {totalMacroSteps}</span>
                )}
              </span>
              {currentMacro?.title && (
                <span className="text-xs sm:text-sm text-gray-500 font-medium border-l border-gray-200 pl-2 ml-1 truncate max-w-[140px] sm:max-w-none">
                  {currentMacro.title}
                </span>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button
                type="button"
                onClick={handleOpenExpressModal}
                className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-bold text-stone-700 hover:text-stone-900 px-2 sm:px-2.5 py-1 rounded-full bg-stone-100 hover:bg-stone-200 border border-stone-200 transition shadow-xs shrink-0"
                title={!isAuthenticated ? "Sign in to unlock Fast-Track" : "Fast-track plan with AI"}
              >
                <span>⚡</span> Fast-Track {!isAuthenticated && <span className="opacity-60 text-[9px]">🔒</span>}
              </button>
              <button
                type="button"
                onClick={() => {
                  setJumpError(null);
                  setShowJumpModal(true);
                }}
                className="text-xs font-semibold text-gray-500 hover:text-gray-900 px-2 py-1 rounded bg-gray-50 hover:bg-gray-100 transition-colors shrink-0"
                title="Jump to a specific step"
              >
                Jump to...
              </button>
            </div>
          </div>

          <div className="flex gap-1.5 w-full h-2">
            {activeMacroSteps.map((macro, idx) => {
              const isPast = idx < currentMacroIndex;
              const isCurrent = idx === currentMacroIndex;
              const pct = isPast ? 100 : isCurrent ? Math.round(((currentSubStepIndex + 1) / totalSubSteps) * 100) : 0;
              const firstSubStepId = macro.activeSubSteps[0];
              const targetStepNum = activeStepIds.indexOf(firstSubStepId) + 1;
              const isReachable = isStepReachable(targetStepNum);

              return (
                <div
                  key={idx}
                  className={`flex-1 bg-stone-100 rounded-full overflow-hidden relative ${isReachable ? 'cursor-pointer' : 'cursor-default'} group/segment transition-all`}
                  onClick={() => {
                    if (isReachable && targetStepNum !== currentStep) {
                      setCurrentStep(targetStepNum);
                      scrollWizardToTop('smooth');
                    }
                  }}
                  title={`${macro.title} (Step ${idx + 1} of ${totalMacroSteps})`}
                >
                  <div
                    className="absolute top-0 left-0 h-full bg-stone-900 transition-all duration-300 ease-out rounded-full"
                    style={{ width: `${pct}%` }}
                  />
                  {isReachable && (
                    <div className="absolute inset-0 bg-stone-900/0 group-hover/segment:bg-stone-900/10 transition-colors" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Jump Modal */}
        <GlassModal
          isOpen={showJumpModal}
          onClose={() => setShowJumpModal(false)}
          title="Jump to Step"
          footer={
            <button
              type="button"
              onClick={() => setShowJumpModal(false)}
              className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors"
            >
              Cancel
            </button>
          }
        >
          {jumpError && (
            <div className="mb-4 p-3 bg-rose-50 border border-rose-100 text-rose-600 rounded-xl text-xs font-bold text-center flex items-center justify-center gap-1.5 animate-fade-in">
              <span>⚠️</span> {jumpError}
            </div>
          )}
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-3 mb-4">
            {activeMacroSteps.map((macro, idx) => {
              const stepNum = idx + 1;
              const firstSubStepId = macro.activeSubSteps[0];
              const targetStepIndex = activeStepIds.indexOf(firstSubStepId) + 1;
              const isReachable = isStepReachable(targetStepIndex);

              // Determine macro status
              let status = 'locked';
              if (idx === currentMacroIndex) {
                status = 'current';
              } else if (idx < currentMacroIndex) {
                status = 'completed';
              } else {
                status = isReachable ? 'available' : 'locked';
              }

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    if (!isReachable) {
                      // Find which macro step the user is currently on
                      const currentMacro = activeMacroSteps[currentMacroIndex];
                      const macroTitle = currentMacro ? currentMacro.title : 'current step';
                      setJumpError(`Complete ${macroTitle} first.`);
                      return;
                    }
                    setJumpError(null);
                    setCurrentStep(targetStepIndex);
                    setShowJumpModal(false);
                    scrollWizardToTop('smooth');
                  }}
                  className={`aspect-square flex flex-col items-center justify-center rounded-xl border-2 transition-all duration-200 ${
                    status === 'current'
                      ? 'border-primary bg-primary text-white shadow-lg scale-110 z-10'
                      : status === 'completed' || status === 'available'
                      ? 'border-green-200 bg-green-50 text-green-700 hover:border-primary/50 hover:scale-105'
                      : 'border-gray-100 bg-gray-50 text-gray-400 opacity-60'
                  }`}
                >
                  <span className="text-lg font-black leading-none">{stepNum}</span>
                  <span className="text-[8px] uppercase font-bold mt-1 text-center px-1 whitespace-normal break-words w-full">
                    {macro.title.split(' ')[0]}
                  </span>
                </button>
              );
            })}
          </div>
        </GlassModal>
      </div>
    );
  };
  const renderNavigationButtons = () => {
    const currentStepId = getCurrentStepId();
    const isOptional = isStepOptional(currentStep);
    const hasSelection = hasOptionalSelection(currentStep);
    const isValid = isStepValid(currentStep);
    const isLastStep = currentStep === totalSteps;
    const isThemeConcept = currentStepId === STEP_IDS.THEME_CONCEPT;
    
    // Optional + empty = Skip. Otherwise (Required or Optional + has input) = Next.
    const showSkip = isOptional && !hasSelection && !isThemeConcept;
    const showNext = !showSkip;
    const isDisabled = showNext && !isValid;

    const previousButton = (currentStep > 1 || returnToSummaryMode) ? (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleBack(e);
        }}
        className="h-11 px-4 sm:px-5 bg-gray-50 text-gray-800 font-semibold rounded-xl border border-gray-200 hover:bg-gray-100 active:bg-gray-200 transition text-sm flex items-center justify-center gap-1.5 shadow-sm whitespace-nowrap"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>
    ) : <div />;

    const goBackFromDock = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setShowMobileActionMenu(false);
      handleBack(e);
    };



    const mobilePrimaryAction = (() => {
      if (!isLastStep) {
        if (showSkip) {
          return {
            label: 'Skip',
            disabled: false,
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowMobileActionMenu(false);
              handleSkip(e);
            }
          };
        }
        return {
          label: currentStep === totalSteps - 1 ? 'Review' : 'Next',
          disabled: isDisabled,
          onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowMobileActionMenu(false);
            handleNext(e);
          }
        };
      }

      if (hasExistingThemes) {
        const isFoodOnly = focusSelection && focusSelection.length === 1 && focusSelection[0] === 'food';
        const isDecorOnly = focusSelection && focusSelection.length === 1 && focusSelection[0] === 'decor';
        return {
          label: isFoodOnly
            ? 'Menu Concepts'
            : isDecorOnly
              ? 'Decor Concepts'
              : isThemeBased ? 'View Theme Options' : 'View Plan',
          disabled: false,
          onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowMobileActionMenu(false);
            if (onNavigateToThemes) onNavigateToThemes();
          }
        };
      }

      if (needsPaidGeneration) {
        return {
          label: 'Buy credits',
          disabled: billingLoadingPackage !== null,
          onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowMobileActionMenu(false);
            openBillingOptionsModal('generation', {
              closeTarget: { screen: 'input', returnThemeKey: '' }
            });
          }
        };
      }

      return {
        label: getCtaLabel(),
        disabled: !isFormValid(),
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowMobileActionMenu(false);
          if (currentStep !== totalSteps) {
            setCurrentStep(totalSteps);
            scrollWizardToTop('auto');
            return;
          }
          if (!isFormValid()) {
            validateForm();
            if (!eventType || (eventType === 'Other' && !customEventType.trim())) {
              setCurrentStep(1);
            } else if (!guestCount) {
              setCurrentStep(11);
            } else if (!zipCode) {
              setCurrentStep(12);
            }
            return;
          }
          handleSubmit(e);
        }
      };
    })();

    const mobileOverflowActions = [];
    if (isLastStep && hasExistingThemes) {
      mobileOverflowActions.push({
        label: needsPaidFreshSet
          ? (isThemeBased ? 'Buy credit for fresh set' : 'Buy credit to regenerate')
          : (isThemeBased ? 'Generate fresh set' : 'Regenerate plan'),
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        ),
        disabled: billingLoadingPackage !== null,
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowMobileActionMenu(false);
          if (needsPaidFreshSet) {
            openBillingOptionsModal('fresh_set', {
              closeTarget: { screen: 'input', returnThemeKey: '' }
            });
          } else {
            setShowRegenerateModal(true);
          }
        }
      });
    }
    if (isLastStep && !hasExistingThemes && !needsPaidGeneration && isAuthenticated && onSaveDraft) {
      mobileOverflowActions.push({
        label: 'Save draft',
        icon: (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        ),
        onClick: async (e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowMobileActionMenu(false);
          await onSaveDraft(getFormSnapshot());
        }
      });
    }

    if (isMobile) {
      return (
        <div
          className="pointer-events-none fixed left-0 right-0 px-3 pb-3 pt-6 bg-gradient-to-t from-white via-white/85 to-transparent"
          style={{ bottom: 0, paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))', zIndex: 55 }}
        >
          {/* Issue D: Floating compact Summary button on mobile right above the dock */}
          {!isLastStep && returnToSummaryMode && (
            <div className="mx-auto w-full max-w-md flex justify-end mb-2 pr-1.5 animate-fade-in">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setReturnToSummaryMode(false);
                  setCurrentStep(totalSteps);
                  scrollWizardToTop('auto');
                }}
                title="Return to Summary"
                className="pointer-events-auto bg-white/95 text-gray-700 hover:text-primary border border-gray-200/80 shadow-[0_4px_12px_rgba(15,23,42,0.16)] backdrop-blur-md px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 flex items-center gap-1.5 hover:scale-105 active:scale-95 group"
              >
                <svg className="w-3.5 h-3.5 text-gray-500 group-hover:text-primary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>Summary</span>
              </button>
            </div>
          )}

          {showMobileActionMenu && mobileOverflowActions.length > 0 && (
            <div className="pointer-events-auto absolute bottom-[calc(100%+0.5rem)] right-3 w-[min(18rem,calc(100vw-1.5rem))] rounded-2xl border border-gray-200 bg-white p-2 shadow-2xl">
              {mobileOverflowActions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  disabled={action.disabled}
                  onClick={action.onClick}
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-bold text-gray-800 transition hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50"
                >
                  <svg className="h-5 w-5 shrink-0 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {action.icon}
                  </svg>
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          )}
          <div className={`pointer-events-auto mx-auto grid w-full max-w-md items-center gap-1.5 rounded-[1.35rem] border border-gray-200 bg-white/95 p-1.5 shadow-[0_12px_34px_rgba(15,23,42,0.16)] backdrop-blur-xl ${
            (currentStep > 1 || returnToSummaryMode)
              ? mobileOverflowActions.length > 0
                ? 'grid-cols-[1fr_1fr_2.75rem]'
                : 'grid-cols-2'
              : mobileOverflowActions.length > 0
                ? 'grid-cols-[1fr_2.75rem]'
                : 'grid-cols-1'
          }`}>
            {(currentStep > 1 || returnToSummaryMode) ? (
              <button
                type="button"
                onClick={goBackFromDock}
                className="h-12 rounded-2xl border border-gray-200 bg-gray-50 text-xs sm:text-sm font-semibold text-gray-800 transition hover:bg-gray-100 active:bg-gray-100"
              >
                <span className="inline-flex items-center justify-center gap-1">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </span>
              </button>
            ) : null}

            <button
              type="button"
              onClick={mobilePrimaryAction.onClick}
              disabled={mobilePrimaryAction.disabled}
              className={`min-h-12 py-2 min-w-0 rounded-2xl px-3 text-xs sm:text-sm font-semibold shadow-md transition flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${
                showSkip
                  ? 'bg-stone-100 border border-stone-200/80 text-stone-700 hover:bg-stone-200 shadow-sm'
                  : 'bg-stone-900 text-white hover:bg-stone-800 active:bg-stone-950'
              }`}
            >
              <span className="inline-flex min-w-0 items-center justify-center gap-1.5">
                <span className="whitespace-normal break-words leading-tight text-center">{mobilePrimaryAction.label}</span>
                {!showSkip && (
                  <svg className="h-4 w-4 shrink-0 hidden min-[360px]:inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </span>
            </button>

            {mobileOverflowActions.length > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowMobileActionMenu((value) => !value);
                }}
                className={`h-12 rounded-2xl border text-lg font-black shadow-sm transition ${
                  showMobileActionMenu
                    ? 'border-primary/30 bg-primary/10 text-primary'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                aria-expanded={showMobileActionMenu}
                aria-label="More workflow actions"
              >
                ⋯
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        className={`fixed right-0 bg-white/95 backdrop-blur border-t border-gray-200 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] min-h-[72px] md:h-20 py-2 md:py-0 flex items-center transition-all duration-300 left-0 md:bottom-0 ${sidebarCollapsed ? 'md:left-16' : 'md:left-64'}`}
        style={{ bottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : undefined, zIndex: 50 }}
      >
        <div className="relative w-full max-w-2xl mx-auto px-4 sm:px-6 md:px-8 flex items-center justify-between gap-4">
          {/* Issue D: Small floating Summary button right above bottom dock on desktop */}
          {!isMobile && !isLastStep && returnToSummaryMode && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setReturnToSummaryMode(false);
                setCurrentStep(totalSteps);
                scrollWizardToTop('auto');
              }}
              title="Return to Summary"
              className="absolute -top-16 right-4 sm:right-6 md:right-8 bg-white/90 hover:bg-white text-gray-700 hover:text-primary border border-gray-200/80 shadow-[0_4px_12px_rgba(15,23,42,0.12)] backdrop-blur-md px-3.5 py-2 rounded-full text-xs font-bold transition-all duration-300 flex items-center gap-1.5 z-50 hover:scale-105 active:scale-95 group animate-fade-in"
            >
              <svg className="w-3.5 h-3.5 text-gray-500 group-hover:text-primary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span>Return to Summary</span>
            </button>
          )}

          <div className="flex-shrink-0">{previousButton}</div>
          <div className="flex min-w-0 flex-1 items-center justify-end gap-2.5">
            {isDisabled && (currentStepId === STEP_IDS.FOOD_MEALS || currentStepId === STEP_IDS.FOOD_DIETARY) && (
              <span className="text-xs font-semibold text-rose-500 hidden sm:inline mr-2 animate-pulse">
                {currentStepId === STEP_IDS.FOOD_MEALS ? 'Choose at least one menu focus' : 'Select dietary restrictions'}
              </span>
            )}
            {!isLastStep ? (
            <>
              {isAuthenticated && onSaveDraft && eventType && (
                <button
                  type="button"
                  onClick={handleSaveDraftClick}
                  disabled={!canSaveDraft}
                  className={`${saveDraftButtonClass} animate-fade-in`}
                >
                  {draftSaveLabel}
                </button>
              )}
              {showSkip ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log(`Skip clicked on step ${currentStep}, moving to step ${currentStep + 1}`);
                    handleSkip(e);
                  }}
                  className="h-12 w-full sm:w-auto min-w-0 sm:min-w-[180px] px-6 bg-stone-100 border border-stone-200/80 text-stone-700 font-bold rounded-xl hover:bg-stone-200/70 active:bg-stone-200 transition text-sm shadow-xs justify-self-end"
                >
                  Skip
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log(`Next clicked on step ${currentStep}, moving to step ${currentStep + 1}`);
                    handleNext(e);
                  }}
                  disabled={isDisabled}
                  className="h-12 w-full sm:w-auto min-w-0 sm:min-w-[180px] px-6 bg-stone-900 hover:bg-stone-800 active:bg-stone-950 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition text-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2 justify-self-end active:scale-[0.99]"
                >
                  <span>{currentStep === totalSteps - 1 ? 'Review Summary' : 'Next'}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </>
          ) : (
            // Last step - show different options if themes already exist
            hasExistingThemes ? (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2.5 sm:gap-3 min-w-0 w-full">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (onNavigateToThemes) {
                        onNavigateToThemes();
                      }
                    }}
                    className="h-11 px-4 sm:px-5 bg-primary text-white font-semibold rounded-xl shadow-sm hover:shadow-md active:shadow-sm transition text-sm flex items-center justify-center gap-2 w-full sm:w-auto min-w-0 sm:min-w-[180px]"
                  >
                    <span>{focusSelection && focusSelection.length === 1 && focusSelection[0] === 'food' ? 'View Menu Concepts' : (focusSelection && focusSelection.length === 1 && focusSelection[0] === 'decor' ? 'View Decor Concepts' : (isThemeBased ? 'View Theme Options' : 'View Plan'))}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5-5 5M6 7l5 5-5 5" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (needsPaidFreshSet) {
                        openBillingOptionsModal('fresh_set', {
                          closeTarget: { screen: 'input', returnThemeKey: '' }
                        });
                      } else {
                        setShowRegenerateModal(true);
                      }
                    }}
                    disabled={billingLoadingPackage !== null}
                    className="h-11 px-4 sm:px-5 bg-orange-50 text-orange-700 border border-orange-200 rounded-xl shadow-sm hover:bg-orange-100 hover:border-orange-300 active:bg-orange-100 transition flex items-center justify-center gap-2 flex-shrink-0 text-sm font-semibold disabled:opacity-60 w-full sm:w-auto min-w-0 sm:min-w-[160px]"
                    title={needsPaidFreshSet ? `Buy a planning credit to generate fresh ${focusSelection.length === 1 && focusSelection[0] === 'food' ? 'menu concepts' : (isThemeBased ? 'theme ideas' : 'plan options')}` : `Generate fresh ${focusSelection.length === 1 && focusSelection[0] === 'food' ? 'menu concepts' : (isThemeBased ? 'theme ideas' : 'plan options')}`}
                    aria-label={needsPaidFreshSet ? `Buy a planning credit to generate fresh ${focusSelection.length === 1 && focusSelection[0] === 'food' ? 'menu concepts' : (isThemeBased ? 'theme ideas' : 'plan options')}` : `Generate fresh ${focusSelection.length === 1 && focusSelection[0] === 'food' ? 'menu concepts' : (isThemeBased ? 'theme ideas' : 'plan options')}`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>{needsPaidFreshSet ? 'Buy credit' : (isThemeBased ? 'Fresh set' : 'Regenerate')}</span>
                  </button>
              </div>
            ) : needsPaidGeneration ? (
              <div className="flex items-center justify-end gap-2 min-w-0 flex-wrap">
                <button
                  type="button"
                  onClick={() => openBillingOptionsModal('generation', {
                    closeTarget: { screen: 'input', returnThemeKey: '' }
                  })}
                  disabled={billingLoadingPackage !== null}
                  className="h-10 px-4 sm:px-5 bg-primary text-white font-semibold rounded-xl shadow-sm hover:shadow-md transition text-sm disabled:opacity-60"
                >
                  Choose credits or subscription
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-end gap-2 min-w-0 flex-wrap">
                {isAuthenticated && onSaveDraft && eventType && (
                  <button
                    type="button"
                    onClick={handleSaveDraftClick}
                    disabled={!canSaveDraft}
                    className={saveDraftButtonClass}
                  >
                    {draftSaveLabel}
                  </button>
                )}
                <button
                  type="submit"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log(`Submit clicked on step ${currentStep} (last step: ${totalSteps})`);
                    // Double-check we're on the last step and form is valid
                    if (currentStep !== totalSteps) {
                      console.warn(`Not on last step! Current: ${currentStep}, Total: ${totalSteps}`);
                      setCurrentStep(totalSteps);
                      return;
                    }
                    if (!isFormValid()) {
                      console.warn('Form validation failed');
                      validateForm(); // Set errors for display
                      // Navigate to step with error
                      if (!eventType || (eventType === 'Other' && !customEventType.trim())) {
                        setCurrentStep(1);
                      } else if (!guestCount) {
                        setCurrentStep(11);
                      } else if (!zipCode) {
                        setCurrentStep(12);
                      }
                      return;
                    }
                    // Form is valid, proceed with submission
                    console.log('Form is valid, submitting...');
                    handleSubmit(e);
                  }}
                  disabled={!isFormValid()}
                  className="min-h-10 py-2 px-6 bg-primary text-white font-semibold rounded-lg shadow-md hover:shadow-lg active:shadow-sm transition text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none whitespace-normal break-words text-center leading-tight"
                >
                  {getCtaLabel()}
                </button>
              </div>
            )
          )}
          </div>
        </div>
      </div >
    );
  };

  // Bottom padding for fixed bottom navigation on all screen sizes
  const mobileBottomPadding = 'pb-44 sm:pb-36 md:pb-28';
  // Top padding for sticky stepper on all screen sizes
  const mobileTopPadding = 'pt-1.5 sm:pt-4 md:pt-10';

  const hasActivities = focusSelection.includes('activities');

  return (
    <>
    {!billingOverlayOnly && (
    <div className={`max-w-2xl mx-auto px-4 sm:px-6 md:px-8 ${mobileBottomPadding} ${mobileTopPadding}`}>
      <div
        className={`bg-white rounded-3xl border border-stone-200/90 shadow-[0_4px_24px_rgba(0,0,0,0.06)] ${getCardOffsetClass()} ${getCardAnimationClass()} relative overflow-visible touch-pan-y`}
        onTouchStart={handleWizardTouchStart}
        onTouchEnd={handleWizardTouchEnd}
        key={currentStep}
      >
        {/* Progress bar at top edge of card */}
        <div>
          <div className="px-4 sm:px-6 pt-3 pb-3">
            {renderProgressBar({ customSpacingClass: 'mt-0' })}
          </div>
          <div className="border-b border-stone-100"></div>
        </div>
        <div className="p-3.5 sm:p-6 md:p-8">

          <form
            onSubmit={handleSubmit}
            onKeyDown={(e) => {
              // Prevent form submission on Enter key unless it's the submit button
              if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA' && e.target.type !== 'submit') {
                e.preventDefault();
              }
            }}
            className={getFormSpacingClass()}
          >
            {/* Step: Event Type */}
            {getCurrentStepId() === STEP_IDS.EVENT_TYPE && (
              <div className={getStepAnimationClass(1)}>
                {/* Fast-Track Banner */}
                <div
                  onClick={handleOpenExpressModal}
                  className="mb-5 p-3.5 sm:p-4 rounded-xl bg-stone-50 border border-stone-200 hover:border-stone-300 hover:shadow-xs transition cursor-pointer flex items-center justify-between gap-3 group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-stone-900 text-white flex items-center justify-center text-xs font-bold shrink-0">
                      ⚡
                    </div>
                    <div className="min-w-0">
                      <h4 className="text-xs sm:text-sm font-bold text-stone-900 flex items-center gap-1.5 flex-wrap">
                        <span>Have an idea or Pinterest photo?</span>
                        {!isAuthenticated && (
                          <span className="text-[10px] uppercase font-bold tracking-wider bg-stone-200 text-stone-700 px-2 py-0.5 rounded-full">
                            Free Sign-in
                          </span>
                        )}
                      </h4>
                      <p className="text-[11px] sm:text-xs text-stone-500 font-medium leading-tight truncate sm:whitespace-normal">
                        Type notes or drop a photo to auto-fill the full event plan in seconds.
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-stone-800 bg-white px-3 py-1.5 rounded-lg border border-stone-200 shadow-xs shrink-0 group-hover:bg-stone-100 transition">
                    {!isAuthenticated ? 'Sign In →' : 'Fast-Track →'}
                  </span>
                </div>

                <label className="block text-base font-bold text-gray-900 mb-4">
                  {renderFieldLabel("What kind of event are you planning?", isFieldRequired(STEP_IDS.EVENT_TYPE))}
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  {eventTypes.map((type) => {
                    const typeValue = type.id === 'other' ? 'Other' : type.label;
                    const isSelected = eventType === typeValue;

                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            setEventType('');
                            setCustomEventType('');
                          } else {
                            setEventType(typeValue);
                            if (type.id !== 'other') setCustomEventType('');
                          }
                        }}
                        className={`relative group p-4 sm:p-5 rounded-2xl border transition-all duration-200 ${isSelected
                          ? 'border-stone-900 bg-stone-900 text-white shadow-md scale-[1.02]'
                          : 'border-stone-200/90 bg-stone-50/50 hover:bg-stone-50 hover:border-stone-400/80 hover:-translate-y-0.5 shadow-xs'
                          }`}
                      >
                        <div className="text-3xl mb-2">
                          {type.icon}
                        </div>
                        <div className={`text-sm font-bold ${isSelected ? 'text-white' : 'text-stone-900'}`}>{type.label}</div>
                        {isSelected && (
                          <div className="absolute top-2.5 right-2.5 w-5 h-5 bg-white text-stone-900 rounded-full flex items-center justify-center shadow-xs">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {eventType === 'Other' && (
                  <div className="mt-4">
                    <input
                      type="text"
                      value={customEventType}
                      onChange={(e) => setCustomEventType(e.target.value)}
                      placeholder="Enter event type (e.g., Corporate Event, Wedding Reception)"
                      className={`w-full sm:max-w-md h-12 px-4 bg-white border-2 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none transition ${errors.customEventType ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {errors.customEventType && (
                      <p className="mt-2 text-sm text-red-500">{errors.customEventType}</p>
                    )}
                  </div>
                )}
                {errors.eventType && (
                  <p className="mt-2 text-sm text-red-500">{errors.eventType}</p>
                )}
              </div>
            )}

            {/* Step: Focus Selection */}
            {getCurrentStepId() === STEP_IDS.FOCUS && (
              <div className={getStepAnimationClass(2)}>
                <label className="block text-base font-bold text-gray-900 mb-4">
                  {renderFieldLabel("How can we help you plan today?", isFieldRequired(STEP_IDS.FOCUS))}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {focusOptions.map((option) => {
                    const isSelected = focusSelection.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          let newSelection;
                          if (option.id === 'everything') {
                            // Clicking Plan Everything → reset to just everything
                            newSelection = ['everything'];
                          } else {
                            // If currently 'everything', clear it and start fresh with this one
                            let current = focusSelection.includes('everything') ? [] : [...focusSelection];

                            if (current.includes(option.id)) {
                              newSelection = current.filter(i => i !== option.id);
                            } else {
                              newSelection = [...current, option.id];
                            }

                            // Collapse all 5 focused options → Plan Everything
                            const FOCUSED_OPTIONS = ['theme_ideas', 'decor', 'food', 'activities', 'shopping'];
                            const allFiveSelected = FOCUSED_OPTIONS.every(id => newSelection.includes(id));
                            if (allFiveSelected) {
                              newSelection = ['everything'];
                            }
                          }
                          // Allow empty selection — validation blocks Next.
                          setFocusSelection(newSelection);
                          if (onFocusChange) {
                            onFocusChange(newSelection);
                          }
                        }}
                        className={`relative p-4 rounded-2xl border-2 transition-all duration-200 shadow-sm hover:shadow-md flex flex-col items-center text-center gap-1 ${isSelected
                          ? 'border-primary bg-primary/5 scale-[1.02] z-10'
                          : 'border-gray-100 bg-gray-50 hover:border-primary/30 hover:-translate-y-0.5'
                          }`}
                      >
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center shadow-sm">
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                          </div>
                        )}
                        <span className="text-3xl mb-2 transform group-hover:scale-110 transition-transform duration-300">{option.icon}</span>
                        <span className={`font-semibold text-sm mb-0.5 ${isSelected ? 'text-primary' : 'text-gray-900'}`}>{option.label}</span>
                        <span className="text-xs text-gray-500 font-medium leading-tight">{option.desc}</span>
                      </button>
                    )
                  })}
                </div>
                {errors.focus && (
                  <p className="mt-2 text-sm text-red-500">{errors.focus}</p>
                )}
              </div>
            )}

            {/* Step: Event Specifics - Who */}
            {getCurrentStepId() === STEP_IDS.EVENT_SPECIFICS_WHO && (
              <div className={getStepAnimationClass(3)}>
                <label className="block text-base font-bold text-gray-900 mb-4">
                  Tell us a bit more about the {eventType || 'event'}!
                </label>

                <div className="space-y-6">
                  {(eventType === 'Birthday Party' || eventType === 'Kids Birthday') ? (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        {renderFieldLabel("Who are we celebrating?", isFieldRequired(STEP_IDS.EVENT_SPECIFICS_WHO))}
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {[
                          { id: 'My Child', label: 'My Child', sub: 'Kid/Teen' },
                          { id: 'Relative (Kid)', label: 'Niece/Nephew', sub: 'Grandchild' },
                          { id: 'Myself', label: 'Myself', sub: '' },
                          { id: 'Spouse/Partner', label: 'Spouse/Partner', sub: '' },
                          { id: 'Adult Child', label: 'Adult Child', sub: '' },
                          { id: 'Parent', label: 'My Parent', sub: '' },
                          { id: 'Friend', label: 'A Friend', sub: '' },
                          { id: 'Other', label: 'Other', sub: '' }
                        ].map((option) => {
                          const isSelected = eventSpecifics.honoreeRelation === option.id;
                          return (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => {
                                setEventSpecifics(prev => ({
                                  ...prev,
                                  honoreeRelation: option.id,
                                  ageRange: null,
                                  isAdultContext: !['My Child', 'Relative (Kid)'].includes(option.id)
                                }));
                              }}
                              className={`relative p-3 rounded-xl border-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium flex flex-col items-center justify-center text-center h-full ${isSelected
                                ? 'border-primary bg-primary/5 text-primary scale-[1.02] z-10'
                                : 'border-gray-100 bg-gray-50 text-gray-700 hover:border-primary/30 hover:-translate-y-0.5'
                                }`}
                            >
                              <span className="text-sm font-bold">{option.label}</span>
                              {option.sub && <span className="text-[10px] opacity-60 leading-tight">{option.sub}</span>}
                              {isSelected && (
                                <div className="absolute top-1.5 right-1.5 w-4 h-4 bg-primary rounded-full flex items-center justify-center shadow-sm">
                                  <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (eventType === 'Anniversary') ? (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        {renderFieldLabel("Who is celebrating?", isFieldRequired(STEP_IDS.EVENT_SPECIFICS_WHO))}
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {['Myself & Partner', 'My Parents', 'Friends', 'Other'].map((option) => {
                          const isSelected = eventSpecifics.honoreeRelation === option;
                          return (
                            <button
                              key={option}
                              type="button"
                              onClick={() => setEventSpecifics({ ...eventSpecifics, honoreeRelation: option })}
                              className={`relative p-4 rounded-xl border-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-sm flex items-center justify-center text-center h-full ${isSelected
                                ? 'border-primary bg-primary/5 text-primary scale-[1.02] z-10'
                                : 'border-gray-100 bg-gray-50 text-gray-700 hover:border-primary/30 hover:-translate-y-0.5'
                                }`}
                            >
                              {isSelected && (
                                <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center shadow-sm">
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                </div>
                              )}
                              {option}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (eventType === 'Baby Shower') ? (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        {renderFieldLabel("Who are you planning this baby shower for?", isFieldRequired(STEP_IDS.EVENT_SPECIFICS_WHO))}
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {['Myself (Mom-to-be)', 'My Partner', 'Myself & Partner', 'A Friend', 'A Family Member', 'Other'].map(opt => {
                          const isSelected = eventSpecifics.honoreeRelation === opt;
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => setEventSpecifics({ ...eventSpecifics, honoreeRelation: opt })}
                              className={`relative p-4 rounded-xl border-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-sm flex items-center justify-center text-center h-full ${isSelected
                                ? 'border-primary bg-primary/5 text-primary scale-[1.02] z-10'
                                : 'border-gray-100 bg-gray-50 text-gray-700 hover:border-primary/30 hover:-translate-y-0.5'}`}
                            >
                              {isSelected && (
                                <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center shadow-sm">
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                </div>
                              )}
                              {opt}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ) : (eventType === 'Graduation') ? (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        {renderFieldLabel("Who is graduating?", isFieldRequired(STEP_IDS.EVENT_SPECIFICS_WHO))}
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {['My Child', 'Myself', 'Friend', 'Relative'].map((option) => {
                          const isSelected = eventSpecifics.honoreeRelation === option;
                          return (
                            <button
                              key={option}
                              type="button"
                              onClick={() => setEventSpecifics({ ...eventSpecifics, honoreeRelation: option })}
                              className={`relative p-4 rounded-xl border-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-sm flex items-center justify-center text-center h-full ${isSelected
                                ? 'border-primary bg-primary/5 text-primary scale-[1.02] z-10'
                                : 'border-gray-100 bg-gray-50 text-gray-700 hover:border-primary/30 hover:-translate-y-0.5'
                                }`}
                            >
                              {isSelected && (
                                <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center shadow-sm">
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                </div>
                              )}
                              {option}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">What is the occasion specifically? <span className="text-gray-400 font-normal text-sm">(optional)</span></label>
                      <textarea
                        value={eventSpecifics.occasionDetails || ''}
                        onChange={(e) => setEventSpecifics(prev => ({ ...prev, occasionDetails: e.target.value }))}
                        placeholder="Tell us a bit more about what we are celebrating..."
                        rows={3}
                        className="w-full p-4 bg-white border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none resize-none"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step: Event Specifics - Gender */}
            {getCurrentStepId() === STEP_IDS.EVENT_SPECIFICS_GENDER && (
              <div className={getStepAnimationClass(currentStep)}>
                <label className="block text-base font-bold text-gray-900 mb-4">
                  {renderFieldLabel("Who are we celebrating?", isFieldRequired(STEP_IDS.EVENT_SPECIFICS_GENDER))}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { id: 'boy', label: eventSpecifics.isAdultContext ? 'A Man' : 'A Boy', icon: eventSpecifics.isAdultContext ? '👨' : '👦', color: 'from-blue-400 to-blue-600' },
                    { id: 'girl', label: eventSpecifics.isAdultContext ? 'A Woman' : 'A Girl', icon: eventSpecifics.isAdultContext ? '👩' : '👧', color: 'from-pink-400 to-pink-600' },
                    { id: 'neutral', label: 'Gender Neutral', icon: '🎈', color: 'from-purple-400 to-purple-600' },
                    { id: 'other', label: 'Other / Private', icon: '✨', color: 'from-gray-400 to-gray-600' }
                  ].map((option) => {
                    const isSelected = eventSpecifics.gender === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setEventSpecifics(prev => ({ ...prev, gender: option.id }))}
                        className={`relative group p-6 rounded-2xl border-2 transition-all duration-200 text-center flex flex-col items-center justify-center gap-3 ${isSelected
                          ? 'border-primary bg-primary/5 shadow-md scale-[1.02]'
                          : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'}`}
                      >
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${option.color} flex items-center justify-center text-2xl shadow-sm group-hover:scale-110 transition-transform`}>
                          {option.icon}
                        </div>
                        <div className="text-sm font-bold text-gray-900">{option.label}</div>
                        {isSelected && (
                          <div className="absolute top-3 right-3 w-6 h-6 bg-primary rounded-full flex items-center justify-center shadow-lg animate-in zoom-in duration-200">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step: Event Specifics - Age/Details */}
            {getCurrentStepId() === STEP_IDS.EVENT_SPECIFICS_AGE && (
              <div className={getStepAnimationClass(3)}>
                <label className="block text-base font-bold text-gray-900 mb-4">
                  A few more details about the {eventType || 'event'}!
                </label>

                <div className="space-y-6">
                  {(eventType === 'Birthday Party' || eventType === 'Kids Birthday') ? (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-sm font-semibold text-gray-700">
                          {renderFieldLabel(eventSpecifics.isAdultContext ? 'Age Range of Honoree?' : 'Approximate Age Range?', isFieldRequired(STEP_IDS.EVENT_SPECIFICS_AGE))}
                        </label>
                        <button
                          type="button"
                          onClick={() => setEventSpecifics(prev => ({ ...prev, isAdultContext: !prev.isAdultContext, ageRange: null }))}
                          className="text-[10px] font-bold text-primary hover:underline bg-primary/5 px-2 py-1 rounded"
                        >
                          {eventSpecifics.isAdultContext ? 'Plan for a Child?' : 'Plan for an Adult?'}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                        {(!eventSpecifics.isAdultContext ? [
                          { id: '0-2', label: '0-2 (Toddler)', icon: '👶' },
                          { id: '3-5', label: '3-5 (Preschool)', icon: '🎨' },
                          { id: '6-9', label: '6-9 (Child)', icon: '🧩' },
                          { id: '10-12', label: '10-12 (Pre-teen)', icon: '🎮' },
                          { id: '13-17', label: '13-17 (Teen)', icon: '🛹' }
                        ] : [
                          { id: '18-24', label: '18-24', icon: '🎓' },
                          { id: '25-34', label: '25-34', icon: '💼' },
                          { id: '35-44', label: '35-44', icon: '🏡' },
                          { id: '45-54', label: '45-54', icon: '🍷' },
                          { id: '55-64', label: '55-64', icon: '🌅' },
                          { id: '65+', label: '65+', icon: '⛳' }
                        ]).concat([{ id: 'Other', label: 'Other', icon: '✨' }]).map((range) => {
                          const isSelected = eventSpecifics.ageRange === range.id;
                          return (
                            <button
                              key={range.id}
                              type="button"
                              onClick={() => setEventSpecifics(prev => ({ ...prev, ageRange: range.id }))}
                              className={`relative p-3 rounded-xl border-2 transition-all duration-200 text-center flex flex-col items-center justify-center gap-1 ${isSelected
                                ? 'border-primary bg-primary/5 text-primary scale-105 z-10'
                                : 'border-gray-100 bg-gray-50 text-gray-600 hover:border-primary/30'}`}
                            >
                              <span className="text-xl">{range.icon}</span>
                              <span className="text-xs font-bold leading-tight">{range.label}</span>
                              {isSelected && (
                                <div className="absolute top-1 right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center shadow-sm">
                                  <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Optional Exact Age Toggle */}
                      <div className="flex items-center gap-2 mb-3">
                        <input
                          type="checkbox"
                          id="provideExactAge"
                          checked={eventSpecifics.showExactAge}
                          onChange={(e) => setEventSpecifics(prev => ({ ...prev, showExactAge: e.target.checked }))}
                          className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                        />
                        <label htmlFor="provideExactAge" className="text-xs font-medium text-gray-600 cursor-pointer">
                          I'd like to provide the exact age for better suggestions
                        </label>
                      </div>

                      {eventSpecifics.showExactAge && (
                        <div className="animate-in slide-in-from-top-2 duration-200">
                          <label className="block text-xs font-bold text-gray-500 mb-2">Turning Age?</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={eventSpecifics.age || ''}
                            onChange={(e) => {
                              const numeric = e.target.value.replace(/[^0-9]/g, '');
                              setEventSpecifics(prev => ({ ...prev, age: numeric }));
                            }}
                            placeholder="e.g. 5"
                            className="w-full sm:w-1/3 h-11 px-4 bg-white border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none text-sm"
                          />
                        </div>
                      )}
                    </div>
                  ) : (eventType === 'Anniversary') ? (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        {renderFieldLabel("Anniversary Milestone?", isFieldRequired(STEP_IDS.EVENT_SPECIFICS_EXTRA))}
                      </label>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-4">
                        {[
                          { id: '1', label: '1st', icon: '' },
                          { id: '5', label: '5th', icon: '' },
                          { id: '10', label: '10th', icon: '' },
                          { id: '25', label: '25th (Silver)', icon: '' },
                          { id: '50', label: '50th (Gold)', icon: '' },
                          { id: 'Other', label: 'Other', icon: '' }
                        ].map((milestone) => {
                          const isSelected = eventSpecifics.milestone === milestone.id;
                          return (
                            <button
                              key={milestone.id}
                              type="button"
                              onClick={() => setEventSpecifics(prev => ({ ...prev, milestone: milestone.id }))}
                              className={`relative p-3 rounded-xl border-2 transition-all duration-200 text-center flex flex-col items-center justify-center gap-1 ${isSelected
                                ? 'border-primary bg-primary/5 text-primary scale-105 z-10'
                                : 'border-gray-100 bg-gray-50 text-gray-600 hover:border-primary/30'}`}
                            >
                              <span className="text-xl">{milestone.icon}</span>
                              <span className="text-xs font-bold leading-tight">{milestone.label}</span>
                              {isSelected && (
                                <div className="absolute top-1 right-1 w-4 h-4 bg-primary rounded-full flex items-center justify-center shadow-sm">
                                  <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Optional Exact Year Toggle */}
                      <div className="flex items-center gap-2 mb-3">
                        <input
                          type="checkbox"
                          id="provideExactYear"
                          checked={eventSpecifics.showExactAge}
                          onChange={(e) => setEventSpecifics(prev => ({ ...prev, showExactAge: e.target.checked }))}
                          className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary"
                        />
                        <label htmlFor="provideExactYear" className="text-xs font-medium text-gray-600 cursor-pointer">
                          I'd like to provide the exact year for better suggestions
                        </label>
                      </div>

                      {eventSpecifics.showExactAge && (
                        <div className="animate-in slide-in-from-top-2 duration-200">
                          <label className="block text-xs font-bold text-gray-500 mb-2">Years Celebrated?</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={eventSpecifics.age || ''}
                            onChange={(e) => {
                              const numeric = e.target.value.replace(/[^0-9]/g, '');
                              setEventSpecifics(prev => ({ ...prev, age: numeric }));
                            }}
                            placeholder="e.g. 25"
                            className="w-full sm:w-1/3 h-11 px-4 bg-white border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none text-sm"
                          />
                        </div>
                      )}
                    </div>
                  ) : (eventType === 'Baby Shower') ? (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Baby details <span className="text-gray-400 font-normal text-sm">(optional)</span></label>
                      <p className="mb-3 text-xs leading-relaxed text-gray-500">
                        Choose Boy, Girl, or Surprise only if it helps the theme. You can leave this unknown and we will avoid assumptions.
                      </p>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { id: 'Boy', color: 'border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 placeholder-blue-300' },
                          { id: 'Girl', color: 'border-pink-200 bg-pink-50 text-pink-500 hover:bg-pink-100 placeholder-pink-300' },
                          { id: 'Surprise', color: 'border-green-200 bg-green-50 text-green-600 hover:bg-green-100 placeholder-green-300' }
                        ].map(opt => {
                          const genders = eventSpecifics.genders || {};
                          const count = genders[opt.id] || 0;
                          const isSelected = count > 0;

                          const isSurpriseSelected = (genders['Surprise'] || 0) > 0;
                          const isGenderSelected = (genders['Boy'] || 0) > 0 || (genders['Girl'] || 0) > 0;
                          const isDisabled = (opt.id === 'Surprise' && isGenderSelected) || (opt.id !== 'Surprise' && isSurpriseSelected);

                          return (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => {
                                setEventSpecifics(prev => {
                                  const currentGenders = { ...prev.genders };
                                  if (opt.id === 'Surprise') {
                                    const isCurrentlySelected = (currentGenders['Surprise'] || 0) > 0;
                                    currentGenders['Surprise'] = isCurrentlySelected ? 0 : 1;
                                    currentGenders['Boy'] = 0;
                                    currentGenders['Girl'] = 0;
                                  } else {
                                    currentGenders['Surprise'] = 0;
                                    const currentCount = currentGenders[opt.id] || 0;
                                    currentGenders[opt.id] = currentCount >= 5 ? 0 : currentCount + 1;
                                  }
                                  return { ...prev, genders: currentGenders };
                                });
                              }}
                              className={`relative py-4 rounded-xl border-2 font-bold transition-all duration-200 flex flex-col items-center justify-center shadow-sm hover:shadow-md hover:-translate-y-0.5 h-full ${isSelected
                                ? `border-current ${opt.color.split(' ')[0]} ${opt.color.split(' ')[1]} ${opt.color.split(' ')[2]}`
                                : 'border-gray-100 bg-gray-50 text-gray-500 hover:bg-gray-100'} ${isDisabled && !isSelected ? 'opacity-40 grayscale' : ''}`}
                            >
                              {isSelected && (
                                <div
                                  className="absolute top-1 right-1 w-5 h-5 bg-white/80 backdrop-blur rounded-full flex items-center justify-center text-[10px] font-bold border border-current cursor-pointer hover:bg-red-100 hover:text-red-500 transition-colors z-20"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEventSpecifics(prev => ({ ...prev, genders: { ...prev.genders, [opt.id]: 0 } }));
                                  }}
                                >
                                  ✕
                                </div>
                              )}
                              <span className="text-sm">{opt.id}</span>
                              <div className="h-4 flex items-center justify-center mt-1">
                                {count > 1 && (
                                  <span className="text-[10px] font-extrabold px-1.5 py-0.5 bg-white/60 rounded-full border border-current">
                                    x{count}
                                  </span>
                                )}
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  ) : (eventType === 'Graduation') ? (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {renderFieldLabel("School or Degree?", isFieldRequired(STEP_IDS.EVENT_SPECIFICS_EXTRA))}
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {['High School', 'College', 'Masters', 'PhD', 'Med School', 'Bootcamp'].map((option) => {
                          const isSelected = eventSpecifics.subType === option;
                          return (
                            <button
                              key={option}
                              type="button"
                              onClick={() => setEventSpecifics({ ...eventSpecifics, subType: option })}
                              className={`relative p-4 rounded-xl border-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-sm flex items-center justify-center text-center h-full ${isSelected
                                ? 'border-primary bg-primary/5 text-primary scale-[1.02] z-10'
                                : 'border-gray-100 bg-gray-50 text-gray-700 hover:border-primary/30 hover:-translate-y-0.5'
                                }`}
                            >
                              {isSelected && (
                                <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center shadow-sm">
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                                </div>
                              )}
                              {option}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {/* Step: Event Specifics - Extra */}
            {getCurrentStepId() === STEP_IDS.EVENT_SPECIFICS_EXTRA && (
              <div className={getStepAnimationClass(3)}>
                <label className="block text-base font-bold text-gray-900 mb-4">
                  Any additional details? <span className="text-gray-400 font-normal text-sm">(optional)</span>
                </label>

                <div className="space-y-6">
                  {(eventType === 'Birthday Party' || eventType === 'Kids Birthday') ? (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Top Interests/Hobbies</label>
                      <textarea
                        value={eventSpecifics.interests || ''}
                        onChange={(e) => setEventSpecifics(prev => ({ ...prev, interests: e.target.value }))}
                        placeholder="e.g. Space, Dinosaurs, Soccer (Tell us what they love!)"
                        rows={2}
                        className="w-full p-4 bg-white border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none resize-none"
                      />
                    </div>
                  ) : (eventType === 'Baby Shower') ? (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Anything else about the parents?</label>
                      <textarea
                        value={eventSpecifics.interests || ''}
                        onChange={(e) => setEventSpecifics(prev => ({ ...prev, interests: e.target.value }))}
                        placeholder="Likes, dislikes, nursery theme ideas..."
                        rows={2}
                        className="w-full p-4 bg-white border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none resize-none shadow-sm hover:shadow-md transition-shadow"
                      />
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Any other details we should know?</label>
                      <textarea
                        value={eventSpecifics.occasionDetails || ''}
                        onChange={(e) => setEventSpecifics(prev => ({ ...prev, occasionDetails: e.target.value }))}
                        placeholder="Tell us a bit more about what we are celebrating..."
                        rows={3}
                        className="w-full p-4 bg-white border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none resize-none"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
{/* Step: Essentials - Country */}
            {getCurrentStepId() === STEP_IDS.ESSENTIALS_COUNTRY && (
              <div className={getStepAnimationClass(3)}>
                <label className="block text-base font-bold text-gray-900 mb-4">
                  {renderFieldLabel("Where is the event taking place?", isFieldRequired(STEP_IDS.ESSENTIALS_COUNTRY))}
                </label>
                <div className="relative w-full sm:max-w-xs">
                  <select
                    value={country} // country state should be the CODE (e.g., 'US')
                    onChange={() => setCountry(LAUNCH_COUNTRY)}
                    className="w-full h-12 pl-4 pr-10 bg-white border-2 border-gray-200 rounded-xl text-gray-900 focus:border-primary focus:outline-none transition appearance-none cursor-pointer"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  United States only for launch. ZIP codes are checked using the US format.
                </p>
              </div>
            )}

            {/* Step: Essentials - Date */}
            {getCurrentStepId() === STEP_IDS.ESSENTIALS_DATE && (() => {
              const getLocalTodayISO = () => {
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              };
              const todayISO = getLocalTodayISO();
              return (
                <div className={getStepAnimationClass(4)}>
                  <label className="block text-base font-bold text-gray-900 mb-4">
                    When is the event? <span className="text-gray-400 font-normal text-sm">(optional)</span>
                  </label>
                  <input
                    type="date"
                    value={date}
                    min={todayISO}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      if (selectedDate && selectedDate < todayISO) return;
                      setDate(selectedDate);
                    }}
                    className="w-full sm:max-w-xs h-12 px-4 bg-white border-2 border-gray-200 rounded-xl text-gray-900 focus:border-primary focus:outline-none transition"
                  />
                </div>
              );
            })()}

            {/* Step: Essentials - Guests */}
            {getCurrentStepId() === STEP_IDS.ESSENTIALS_GUESTS && (
              <div className={getStepAnimationClass(4)}>
                <label className="block text-base font-bold text-gray-900 mb-1">
                  {renderFieldLabel("How many guests are you expecting?", isFieldRequired(STEP_IDS.ESSENTIALS_GUESTS))}
                </label>
                <p className="text-xs text-gray-500 mb-4 font-medium leading-relaxed">
                  Used to estimate quantities, portions, supplies, and budget.
                </p>
                <div className="relative w-full sm:max-w-xs">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl">👥</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={guestCount}
                    onChange={(e) => {
                      const numeric = e.target.value.replace(/[^0-9]/g, '');
                      setGuestCount(numeric);
                    }}
                    placeholder="e.g., 50"
                    className="w-full h-12 pl-12 pr-4 bg-white border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none transition"
                  />
                </div>
              </div>
            )}

            {/* Step: Essentials - Budget */}
            {getCurrentStepId() === STEP_IDS.ESSENTIALS_BUDGET && (
              <div className={getStepAnimationClass(4)}>
                <label className="block text-base font-bold text-gray-900 mb-1">
                  {renderFieldLabel("What is your approximate budget?", isFieldRequired(STEP_IDS.ESSENTIALS_BUDGET))}
                </label>
                <p className="text-xs text-gray-500 mb-3 font-medium leading-relaxed">
                  A rough range is fine. We’ll use it to keep suggestions realistic.
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[300, 500, 1000, 2500, 5000].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setBudget(preset)}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                        budget === preset
                          ? 'bg-primary text-white border-primary shadow-sm'
                          : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      ${preset.toLocaleString()}
                    </button>
                  ))}
                </div>
                <div className="relative w-full sm:max-w-xs">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-base select-none">
                    {getCurrencySymbol(country)}
                  </span>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={budget !== null && budget !== undefined ? budget : ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '') {
                        setBudget(null);
                      } else {
                        const parsed = Number(val);
                        if (!isNaN(parsed)) {
                          setBudget(parsed);
                        }
                      }
                    }}
                    onBlur={() => {
                      if (budget !== null && budget < minBudget) setBudget(minBudget);
                    }}
                    placeholder="e.g. 1000"
                    className="w-full h-12 pl-9 pr-4 bg-white border-2 border-gray-200 rounded-xl text-gray-900 font-semibold placeholder-gray-400 focus:border-primary focus:outline-none transition shadow-sm"
                  />
                </div>
              </div>
            )}

            {/* Step: Essentials - Zip */}
            {getCurrentStepId() === STEP_IDS.ESSENTIALS_ZIP && (() => {
              const isFoodOnly = focusSelection && focusSelection.length === 1 && focusSelection[0] === 'food';
              const isDecorOnly = focusSelection && focusSelection.length === 1 && focusSelection[0] === 'decor';
              const isShoppingOnly = focusSelection && focusSelection.length === 1 && focusSelection[0] === 'shopping';
              
              let helperText = 'Used to suggest local vendors, stores, rentals, and delivery options near you.';
              if (isFoodOnly) {
                helperText = 'Used to suggest nearby restaurants, catering, grocery, and delivery options.';
              } else if (isDecorOnly) {
                helperText = 'Used to suggest nearby party stores, rentals, pickup, and delivery options.';
              } else if (isShoppingOnly) {
                helperText = 'Used to suggest local stores, pickup options, and delivery-friendly sourcing.';
              }

              return (
                <div className={getStepAnimationClass(4)}>
                  <label className="block text-base font-bold text-gray-900 mb-1">
                    {renderFieldLabel(country === 'US' ? "What is the zip code for the event?" : "What is your postal code or city?", isFieldRequired(STEP_IDS.ESSENTIALS_ZIP))}
                  </label>
                  <p className="text-xs text-gray-500 mb-4 font-medium leading-relaxed">
                    {helperText}
                  </p>
                  <div className="relative w-full sm:max-w-xs">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      inputMode={country === 'US' ? 'numeric' : 'text'}
                      pattern={country === 'US' ? '[0-9]*' : undefined}
                      autoComplete="postal-code"
                      value={zipCode}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (country === 'US') {
                          const numeric = val.replace(/[^0-9]/g, '');
                          if (numeric.length <= 5) setZipCode(numeric);
                        } else {
                          if (val.length <= 15) setZipCode(val);
                        }
                      }}
                      placeholder={country === 'US' ? 'e.g. 90210' : 'Postal code or city (e.g. SW1A 1AA)'}
                      className="w-full h-12 pl-11 pr-4 bg-white border-2 border-gray-200 rounded-xl text-gray-900 font-semibold placeholder-gray-400 focus:border-primary focus:outline-none transition shadow-sm"
                    />
                  </div>
                </div>
              );
            })()}

            {/* Step: Age Range */}
            {getCurrentStepId() === STEP_IDS.AGE_RANGE && (
              <div className={getStepAnimationClass(2)}>
                <label className="block text-base font-bold text-gray-900 mb-1">
                  Who’s attending? {hasActivities ? (
                    <span className="text-amber-600 font-bold text-sm">(strongly recommended for activities)</span>
                  ) : (
                    <span className="text-gray-400 font-normal text-sm">(optional)</span>
                  )}
                </label>
                <p className="text-xs text-gray-500 mb-4 font-medium leading-relaxed">
                  {hasActivities
                    ? "Select the guest age groups so we can tailor activities and keep suggestions age-safe and age-appropriate."
                    : "Select the guest age groups so we can tailor food, decor, and activities. This is different from the guest of honor’s age."}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  {ageRanges.map((age) => {
                    const isSelected = Array.isArray(ageRange) ? ageRange.includes(age.value) : ageRange === age.value;
                    return (
                      <button
                        key={age.value}
                        type="button"
                        onClick={() => {
                          if (Array.isArray(ageRange)) {
                            if (age.value === 'Mixed Ages') {
                              // If selecting Mixed Ages, clear everything else
                              setAgeRange(ageRange.includes('Mixed Ages') ? [] : ['Mixed Ages']);
                            } else {
                              // If selecting something else, toggle it and remove Mixed Ages
                              let newRange = ageRange.includes(age.value)
                                ? ageRange.filter(a => a !== age.value)
                                : [...ageRange, age.value];
                              newRange = newRange.filter(a => a !== 'Mixed Ages');
                              setAgeRange(newRange);
                            }
                          } else {
                            // Fallback if it's a string
                            if (age.value === 'Mixed Ages') {
                              setAgeRange(ageRange === 'Mixed Ages' ? [] : ['Mixed Ages']);
                            } else {
                              setAgeRange(ageRange === age.value ? [] : [age.value]);
                            }
                          }
                        }}
                        className={`relative group p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 overflow-hidden ${isSelected
                          ? 'border-primary shadow-lg scale-105'
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                          }`}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${age.color} opacity-10 ${isSelected ? 'opacity-20' : ''}`} />
                        <div className="relative z-10">
                          <div className="text-2xl mb-1">{age.icon}</div>
                          <div className="text-sm font-bold text-gray-900">{age.label}</div>
                          <div className="text-xs text-gray-600 mt-0.5">{age.sublabel}</div>
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center z-10">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step: Venue */}
            {getCurrentStepId() === STEP_IDS.VENUE && (
              <div className={getStepAnimationClass(3)}>
                <label className="block text-base font-bold text-gray-900 mb-1">
                  Do you have a venue in mind? {hasActivities ? (
                    <span className="text-amber-600 font-bold text-sm">(strongly recommended for activities)</span>
                  ) : (
                    <span className="text-gray-400 font-normal text-sm">(optional)</span>
                  )}
                </label>
                <p className="text-xs text-gray-500 mb-4 font-medium leading-relaxed">
                  {hasActivities
                    ? "Venue context helps suggest activities that fit the space and setup."
                    : "Venue helps us recommend layouts, decor, activities, and setup ideas that fit the space."}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  {venueOptions.map((venueOption) => (
                    <button
                      key={venueOption.value}
                      type="button"
                      onClick={() => setVenue(venue === venueOption.value ? '' : venueOption.value)}
                      className={`relative group p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 overflow-hidden ${venue === venueOption.value
                        ? 'border-primary shadow-lg scale-105'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                        }`}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${venueOption.color} opacity-10 ${venue === venueOption.value ? 'opacity-20' : ''}`} />
                      <div className="relative z-10">
                        <div className="text-3xl mb-2">{venueOption.icon}</div>
                        <div className="text-sm font-bold text-gray-900">{venueOption.label}</div>
                        <div className="text-xs text-gray-600 mt-1">{venueOption.description}</div>
                      </div>
                      {venue === venueOption.value && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center z-10">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step: Venue Type */}
            {getCurrentStepId() === STEP_IDS.VENUE_TYPE && (() => {
              let helperText;
              const isDecorOnly = focusSelection.includes('decor') && !focusSelection.includes('activities');
              const isActivitiesOnly = focusSelection.includes('activities') && !focusSelection.includes('decor');
              if (venue === 'Outdoor') {
                helperText = 'Outdoor spaces may need weather, lighting, seating, and backup-plan suggestions.';
              } else if (venue === 'Indoor') {
                helperText = 'Indoor spaces help us tailor layouts, table setup, activity space, and decor placement.';
              } else if (isDecorOnly) {
                helperText = 'Venue context helps tailor decor ideas to the actual space.';
              } else if (isActivitiesOnly) {
                helperText = 'Venue context helps suggest activities that fit the space and setup.';
              } else {
                helperText = 'Venue helps us recommend layouts, decor, activities, and setup ideas that fit the space.';
              }
              return (
                <div className={getStepAnimationClass(4)}>
                  <label className="block text-base font-bold text-gray-900 mb-1">
                    What kind of space are you looking for? <span className="text-gray-400 font-normal text-sm">(optional)</span>
                  </label>
                  <p className="text-xs text-gray-500 mb-4 font-medium leading-relaxed">
                    {helperText}
                  </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  {venueTypeOptions.filter(opt => !venue || opt.spaces.includes(venue)).map((type) => {
                    const typeValue = type.value === 'Other' ? 'Other' : type.value;
                    const isSelected = venueType === typeValue;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            setVenueType('');
                            setCustomVenueType('');
                          } else {
                            setVenueType(typeValue);
                            if (type.value !== 'Other') setCustomVenueType('');
                            // Scroll custom venue input into view when Other is newly selected
                            if (type.value === 'Other') {
                              setTimeout(() => {
                                requestAnimationFrame(() => {
                                  requestAnimationFrame(() => {
                                    const appScroller = document.querySelector('[data-app-main-scroll="true"]');
                                    const targetEl = venueCustomInputRef.current;
                                    if (appScroller && targetEl) {
                                      const containerRect = appScroller.getBoundingClientRect();
                                      const elemRect = targetEl.getBoundingClientRect();
                                      const relativeTop = elemRect.top - containerRect.top + appScroller.scrollTop;
                                      const targetScrollTop = relativeTop - (containerRect.height / 2) + (elemRect.height / 2);
                                      appScroller.scrollTo({
                                        top: Math.max(0, targetScrollTop),
                                        behavior: 'auto'
                                      });
                                    } else if (targetEl) {
                                      targetEl.scrollIntoView({ behavior: 'auto', block: 'center' });
                                    }
                                    
                                    setTimeout(() => {
                                      venueInputFieldRef.current?.focus();
                                    }, 50);
                                  });
                                });
                              }, 80);
                            }
                          }
                        }}
                        className={`relative group p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 ${isSelected
                          ? 'border-primary bg-primary/5 shadow-md scale-105'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                          }`}
                      >
                        <div className={`text-3xl mb-2 bg-gradient-to-br ${type.color} bg-clip-text text-transparent`}>
                          {type.icon}
                        </div>
                        <div className="text-sm font-semibold text-gray-900">{type.label}</div>
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {venueType === 'Other' && (
                  <div className="mt-4 scroll-mb-40" ref={venueCustomInputRef}>
                    <input
                      ref={venueInputFieldRef}
                      type="text"
                      value={customVenueType}
                      onChange={(e) => setCustomVenueType(e.target.value)}
                      placeholder="Describe the venue (e.g., apartment clubhouse, backyard with pool, temple hall)"
                      className="w-full sm:max-w-md h-12 px-4 bg-white border-2 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none transition border-gray-300 focus:border-primary text-sm"
                    />
                  </div>
                )}
              </div>
              );
            })()}

            {/* Step: Time of Day */}
            {getCurrentStepId() === STEP_IDS.TIME && (
              <div className={getStepAnimationClass(5)}>
                <label className={`block text-base font-bold text-gray-900 ${hasActivities ? 'mb-1' : 'mb-4'}`}>
                  What time of day will it be? {hasActivities ? (
                    <span className="text-amber-600 font-bold text-sm">(recommended for activities)</span>
                  ) : (
                    <span className="text-gray-400 font-normal text-sm">(optional)</span>
                  )}
                </label>
                {hasActivities && (
                  <p className="text-xs text-gray-500 mb-4 font-medium leading-relaxed">
                    Time of day helps tune pacing, setup, and activity energy.
                  </p>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  {timeOfDayOptions.map((time) => {
                    const isSelected = Array.isArray(timeOfDay) ? timeOfDay.includes(time.value) : timeOfDay === time.value;
                    return (
                      <button
                        key={time.value}
                        type="button"
                        onClick={() => {
                          if (Array.isArray(timeOfDay)) {
                            setTimeOfDay(prev => prev.includes(time.value) ? prev.filter(t => t !== time.value) : [...prev, time.value]);
                          } else {
                            setTimeOfDay(timeOfDay === time.value ? [] : [time.value]);
                          }
                        }}
                        className={`relative group p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 overflow-hidden ${isSelected
                          ? 'border-primary shadow-lg scale-105'
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                          }`}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${time.color} opacity-10 ${isSelected ? 'opacity-20' : ''}`} />
                        <div className="relative z-10">
                          <div className="text-3xl mb-2">{time.icon}</div>
                          <div className="text-sm font-bold text-gray-900">{time.label}</div>
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center z-10">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step: Meals */}
            {getCurrentStepId() === STEP_IDS.FOOD_MEALS && (
              <div className={getStepAnimationClass(6)}>
                <label className="block text-base font-bold text-gray-900 mb-4">
                  {renderFieldLabel("What kind of meals will be served?", isFieldRequired(STEP_IDS.FOOD_MEALS), "optional")}
                </label>

                {(!Array.isArray(selectedMeals) || !selectedMeals.some(m => filteredMealOptions.map(o => o.value).includes(m))) && isFieldRequired(STEP_IDS.FOOD_MEALS) && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600 font-medium flex items-center gap-1.5 max-w-xl animate-fade-in">
                    <span>⚠️</span> Choose at least one menu focus to continue.
                  </div>
                )}

                {isFieldRequired(STEP_IDS.FOOD_MEALS) && (
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-gray-500">Quick Select:</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const visibleValues = filteredMealOptions.map(opt => opt.value);
                        let meal = '';
                        if (visibleValues.includes('dinner')) meal = 'dinner';
                        else if (visibleValues.includes('lunch')) meal = 'lunch';
                        else if (visibleValues.includes('brunch')) meal = 'brunch';
                        else if (visibleValues.includes('breakfast')) meal = 'breakfast';
                        else if (visibleValues.length > 0) meal = visibleValues[0];

                        if (meal) {
                          setSelectedMeals([meal]);
                          setFoodMenuStructure({ [meal]: 1 });
                          setErrors(prev => {
                            const next = { ...prev };
                            delete next.meals;
                            return next;
                          });
                        }
                      }}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 transition shadow-sm flex items-center gap-1"
                    >
                      🍽️ Main Course Only
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const visibleValues = filteredMealOptions.map(opt => opt.value);
                        const selected = [];

                        // Primary meal
                        let main = '';
                        if (visibleValues.includes('dinner')) main = 'dinner';
                        else if (visibleValues.includes('lunch')) main = 'lunch';
                        else if (visibleValues.includes('brunch')) main = 'brunch';
                        else if (visibleValues.includes('breakfast')) main = 'breakfast';
                        if (main) selected.push(main);

                        // Appetizers
                        if (visibleValues.includes('appetizers')) selected.push('appetizers');
                        else if (visibleValues.includes('finger_foods')) selected.push('finger_foods');
                        else if (visibleValues.includes('snacks')) selected.push('snacks');

                        // Desserts
                        if (visibleValues.includes('desserts')) selected.push('desserts');
                        else if (visibleValues.includes('late_night_bites')) selected.push('late_night_bites');
                        else if (visibleValues.includes('coffee_tea')) selected.push('coffee_tea');

                        if (selected.length === 0 && visibleValues.length > 0) {
                          selected.push(visibleValues[0]);
                        }

                        const deduped = [...new Set(selected)].filter(v => visibleValues.includes(v));
                        if (deduped.length > 0) {
                          setSelectedMeals(deduped);
                          const newStructure = {};
                          deduped.forEach(m => {
                            newStructure[m] = 1;
                          });
                          setFoodMenuStructure(newStructure);
                          setErrors(prev => {
                            const next = { ...prev };
                            delete next.meals;
                            return next;
                          });
                        }
                      }}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 transition shadow-sm flex items-center gap-1"
                    >
                      ✨ Full Menu
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  {filteredMealOptions.map((meal) => {
                    const isSelected = selectedMeals.includes(meal.value);
                    const count = foodMenuStructure[meal.value] || 1;
                    return (
                      <div
                        key={meal.value}
                        onClick={() => toggleMealSelection(meal.value)}
                        className={`relative group p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 overflow-hidden cursor-pointer select-none ${isSelected
                          ? 'border-primary shadow-lg scale-105'
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                          }`}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${meal.color} opacity-10 ${isSelected ? 'opacity-20' : ''}`} />
                        <div className="relative z-10 flex flex-col items-center justify-between h-full">
                          <div className="text-3xl mb-2 text-center">{meal.icon}</div>
                          <div className="text-sm font-bold text-gray-900 text-center mb-1">{meal.label}</div>
                          {isSelected && (
                            <div className="flex items-center justify-center gap-2 mt-2 relative z-20 w-full">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDecrement(meal.value);
                                }}
                                className="w-7 h-7 flex items-center justify-center rounded-lg bg-white hover:bg-gray-100 text-gray-700 font-bold transition-colors border border-gray-300 shadow-sm"
                              >
                                -
                              </button>
                              <span className="font-bold text-gray-900 text-sm min-w-[16px] text-center">
                                {count}
                              </span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleIncrement(meal.value);
                                }}
                                disabled={getSelectedMealsTotalCount() >= 12 || count >= 5}
                                className={`w-7 h-7 flex items-center justify-center rounded-lg font-bold transition-colors border shadow-sm ${
                                  (getSelectedMealsTotalCount() >= 12 || count >= 5)
                                    ? 'bg-gray-50 text-gray-300 border-gray-200 cursor-not-allowed'
                                    : 'bg-white hover:bg-gray-100 text-gray-700 border-gray-300'
                                }`}
                              >
                                +
                              </button>
                            </div>
                          )}
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center z-10">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {getSelectedMealsTotalCount() >= 12 && (
                  <div className="mt-4 text-sm font-semibold text-amber-600 flex items-center gap-1.5 justify-center">
                    <span>⚠️</span> Maximum of 12 total food items reached
                  </div>
                )}
              </div>
            )}

            {/* Step: Food Source (Delivery/Catering) */}
            {getCurrentStepId() === STEP_IDS.FOOD_SOURCE && (
              <div className={getStepAnimationClass(5)}>
                <label className="block text-base font-bold text-gray-900 mb-4">
                  How will you handle the food? <span className="text-gray-400 font-normal text-sm">(select all that apply)</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  {[
                    { id: 'cooking', label: 'Cooking Myself', icon: '👨‍🍳', desc: 'Home cooked meals' },
                    { id: 'delivery', label: 'Order Delivery', icon: '🛵', desc: 'UberEats, DoorDash, etc.' },
                    { id: 'takeout', label: 'Restaurant Takeout', icon: '🥡', desc: 'Pickup from favorite spots' },
                    { id: 'catering', label: 'Professional Catering', icon: '🥂', desc: 'Full service catering' },
                    { id: 'potluck', label: 'Potluck', icon: '🥘', desc: 'Guests bring dishes' },
                    { id: 'snacks', label: 'Snacks Only', icon: '🍿', desc: 'Keep it light' }
                  ].map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setFoodSource(prev => prev.includes(option.id) ? prev.filter(i => i !== option.id) : [...prev, option.id])}
                      className={`relative group p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 text-center flex flex-col items-center justify-center ${foodSource.includes(option.id)
                        ? 'border-primary bg-primary/5 shadow-md scale-[1.02]'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                        }`}
                    >
                      <div className="text-3xl mb-2">{option.icon}</div>
                      <div className="text-sm font-bold text-gray-900">{option.label}</div>
                      <div className="text-xs text-gray-500">{option.desc}</div>
                      {foodSource.includes(option.id) && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" /></svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step: Preferred Cuisines */}
            {getCurrentStepId() === STEP_IDS.CUISINE_TYPES && (
              <div className={getStepAnimationClass(5)}>
                <label className="block text-base font-bold text-gray-900 mb-4">
                  Any preferred cuisines? <span className="text-gray-400 font-normal text-sm">(optional)</span>
                </label>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {[
                    { id: 'italian', label: 'Italian', icon: '🍕', color: 'bg-orange-50 text-orange-600 border-orange-100', desc: 'Pasta, pizza & classic flavors' },
                    { id: 'mexican', label: 'Mexican', icon: '🌮', color: 'bg-red-50 text-red-600 border-red-100', desc: 'Bold, spicy & vibrant dishes' },
                    { id: 'asian', label: 'Asian Fusion', icon: '🥢', color: 'bg-blue-50 text-blue-600 border-blue-100', desc: 'Eastern inspired blends' },
                    { id: 'american', label: 'American', icon: '🍔', color: 'bg-amber-50 text-amber-600 border-amber-100', desc: 'Burgers, fries & classic comfort' },
                    { id: 'bbq', label: 'BBQ', icon: '🍖', color: 'bg-red-50 text-red-700 border-red-200', desc: 'Smoky grilled favorites' },
                    { id: 'mediterranean', label: 'Mediterranean', icon: '🥙', color: 'bg-green-50 text-green-600 border-green-100', desc: 'Fresh, healthy & coastal' },
                    { id: 'indian', label: 'Indian', icon: '🍛', color: 'bg-yellow-50 text-yellow-700 border-yellow-200', desc: 'Rich spices & traditional curries' },
                    { id: 'french', label: 'French', icon: '🥐', color: 'bg-indigo-50 text-indigo-600 border-indigo-100', desc: 'Sophisticated & refined cuisine' },
                    { id: 'seafood', label: 'Seafood', icon: '🦞', color: 'bg-cyan-50 text-cyan-600 border-cyan-100', desc: 'Fresh catches & ocean flavors' },
                    { id: 'vegan', label: 'Vegan/Healthy', icon: '🥗', color: 'bg-emerald-50 text-emerald-600 border-emerald-100', desc: 'Plant-based & nutritious' },
                    { id: 'other', label: 'Other', icon: '🍽️', color: 'bg-gray-50 text-gray-600 border-gray-100', desc: 'Something else in mind' }
                  ].map((cuisine) => (
                    <button
                      key={cuisine.id}
                      type="button"
                      onClick={() => {
                        const isNowSelected = !cuisineTypes.includes(cuisine.id);
                        setCuisineTypes(prev => prev.includes(cuisine.id) ? prev.filter(c => c !== cuisine.id) : [...prev, cuisine.id]);
                        // Scroll custom input fully into view when Other is newly selected
                        if (cuisine.id === 'other' && isNowSelected) {
                          setTimeout(() => {
                            requestAnimationFrame(() => {
                              cuisineCustomInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                              cuisineInputFieldRef.current?.focus();
                            });
                          }, 60);
                        }
                      }}
                      className={`relative group p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 text-center flex flex-col items-center justify-center ${cuisineTypes.includes(cuisine.id)
                        ? 'border-primary bg-primary/5 shadow-md scale-[1.02]'
                        : `border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm`
                        }`}
                    >
                      <div className={`w-12 h-12 rounded-full ${cuisine.color} border flex items-center justify-center text-2xl mb-2 transition-transform duration-300 group-hover:scale-110`}>
                        {cuisine.icon}
                      </div>
                      <div className="text-sm font-bold text-gray-900">{cuisine.label}</div>
                      <div className="text-[10px] sm:text-xs text-gray-500 mt-1 leading-tight break-words w-full px-1">{cuisine.desc}</div>

                      {cuisineTypes.includes(cuisine.id) && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center shadow-md">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" /></svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                {cuisineTypes.includes('other') && (
                  <div className="mt-4 scroll-mb-40" ref={cuisineCustomInputRef}>
                    <input
                      ref={cuisineInputFieldRef}
                      type="text"
                      value={customCuisineType}
                      onChange={(e) => setCustomCuisineType(e.target.value)}
                      placeholder="Add cuisine or food style (e.g., Tibetan Fusion, Kerala sadya)"
                      className="w-full sm:max-w-md h-12 px-4 bg-white border-2 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none transition border-gray-300 focus:border-primary text-sm"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step: Dietary Restrictions */}
            {getCurrentStepId() === STEP_IDS.FOOD_DIETARY && (
              <div className={getStepAnimationClass(7)}>
                <label className="block text-base font-bold text-gray-900 mb-4">
                  Are there any dietary restrictions to consider?{isFieldRequired(STEP_IDS.FOOD_DIETARY) ? (
                    <span className="text-red-500"> *</span>
                  ) : (
                    <span className="text-gray-400 font-normal text-sm"> (optional)</span>
                  )}
                </label>

                {dietaryRestrictions.length === 0 && isFieldRequired(STEP_IDS.FOOD_DIETARY) && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600 font-medium flex items-center gap-1.5 max-w-xl animate-fade-in">
                    <span>⚠️</span> Select "No dietary restrictions" if none apply.
                  </div>
                )}

                {isFieldRequired(STEP_IDS.FOOD_DIETARY) && dietaryRestrictions.length === 0 && (
                  <div className="mb-4 flex items-center gap-2 animate-fade-in">
                    <span className="text-xs font-semibold text-gray-500">Quick Select:</span>
                    <button
                      type="button"
                      onClick={() => setDietaryRestrictions(['none'])}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary/10 border border-primary/20 text-primary hover:bg-primary/20 transition flex items-center gap-1 shadow-sm"
                    >
                      ✅ No Restrictions
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
                  {dietaryRestrictionOptions.map((restriction) => (
                    <button
                      key={restriction.value}
                      type="button"
                      onClick={() => setDietaryRestrictions(prev => {
                        if (restriction.value === 'none') {
                          return prev.includes('none') ? [] : ['none'];
                        }
                        const filtered = prev.filter(r => r !== 'none');
                        return filtered.includes(restriction.value)
                          ? filtered.filter(r => r !== restriction.value)
                          : [...filtered, restriction.value];
                      })}
                      className={`relative group p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 ${dietaryRestrictions.includes(restriction.value)
                        ? 'border-primary bg-primary/5 shadow-md scale-105'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                        }`}
                    >
                      <div className="flex flex-col items-center justify-center gap-2 text-center h-full">
                        <div className={`text-2xl bg-gradient-to-br ${restriction.color} bg-clip-text text-transparent`}>
                          {restriction.icon}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{restriction.label}</div>
                          <div className="text-xs text-gray-500">{restriction.description}</div>
                        </div>
                      </div>
                      {dietaryRestrictions.includes(restriction.value) && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <div className="mt-6 border-t border-gray-100 pt-6">
                  <label className="block text-sm font-bold text-gray-900 mb-2">
                    Any allergies or dietary notes? <span className="text-gray-400 font-normal text-xs">(optional)</span>
                  </label>
                  <textarea
                    ref={dietaryNotesRef}
                    rows={3}
                    value={dietaryInstructions}
                    onChange={(e) => setDietaryInstructions(e.target.value)}
                    onFocus={() => {
                      setTimeout(() => {
                        requestAnimationFrame(() => {
                          dietaryNotesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        });
                      }, 60);
                    }}
                    placeholder="Severe peanut allergy, no mushrooms, Jain food, egg-free desserts"
                    className="w-full sm:max-w-xl p-3 bg-white border-2 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none transition border-gray-300 focus:border-primary text-sm leading-relaxed"
                  />
                </div>
              </div>
            )}



            {/* Step: Theme Styles (Renamed to Top Priorities) */}
            {getCurrentStepId() === STEP_IDS.THEME_STYLES && (
              <div className={getStepAnimationClass(currentStep)}>
                <label className="block text-base font-bold text-gray-900 mb-4">
                  What are your top priorities for the vibe? <span className="text-gray-400 font-normal text-sm">(select up to 4)</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  {themeStyles.map((style) => {
                    const isSelected = selectedThemeStyles.includes(style.id);
                    const isDisabled = !isSelected && selectedThemeStyles.length >= 4;
                    return (
                      <button
                        key={style.id}
                        type="button"
                        onClick={() => handleThemeStyleToggle(style.id)}
                        disabled={isDisabled}
                        className={`relative group p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 w-full flex-grow-0 flex-shrink-0 ${isSelected
                          ? 'border-primary bg-primary/10 shadow-md scale-105'
                          : isDisabled
                            ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                          }`}
                      >
                        <div className={`text-2xl mb-2 bg-gradient-to-br ${style.color} bg-clip-text text-transparent`}>{style.icon}</div>
                        <div className={`text-sm font-bold ${isSelected ? 'text-primary' : 'text-gray-900'}`}>{style.label}</div>
                        <div className="text-xs text-gray-600 mt-0.5">{style.description}</div>
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step: Vibe/Modifiers */}
            {getCurrentStepId() === STEP_IDS.THEME_DEAILS && (
              <div className={getStepAnimationClass(9)}>
                <label className={`block text-base font-bold text-gray-900 ${hasActivities ? 'mb-1' : 'mb-4'}`}>
                  What are your top priorities for this event?
                </label>
                {hasActivities && (
                  <p className="text-xs text-gray-500 mb-4 font-medium leading-relaxed">
                    Choose at least one to steer activity style.
                  </p>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  {themeModifiers.map((modifier) => {
                    const isSelected = selectedThemeModifiers.includes(modifier.id);
                    return (
                      <button
                        key={modifier.id}
                        type="button"
                        onClick={() => handleModifierToggle(modifier.id)}
                        className={`relative group p-4 sm:p-5 rounded-xl border-2 transition-all duration-200 overflow-hidden ${isSelected ? 'border-primary shadow-lg scale-105' : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                          }`}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-br ${modifier.color} opacity-10 ${isSelected ? 'opacity-20' : ''}`} />
                        <div className="relative z-10">
                          <div className="text-3xl mb-2">{modifier.icon}</div>
                          <div className="text-sm font-semibold text-gray-900">{modifier.label}</div>
                        </div>
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center z-10">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" /></svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}


            {/* Step: Additional Info */}
            {getCurrentStepId() === STEP_IDS.ADDITIONAL && (
              <div className={getStepAnimationClass(currentStep)}>
                <label className="block text-base font-bold text-gray-900 mb-1">Anything else we should know?</label>
                <p className="text-xs text-gray-500 mb-4 font-medium leading-relaxed">
                  {focusSelection.includes('food') || focusSelection.includes('everything')
                    ? "Have menu preferences? Add notes like 'more appetizer options,' 'kid-friendly desserts,' or 'include vegetarian mains' to guide your plan."
                    : "Add any special requests, constraints, or unique details you'd like us to consider."}
                </p>
                <div className="bg-white rounded-2xl border-2 border-primary/20 p-1 shadow-inner">
                  <textarea
                    value={additionalDetails}
                    onChange={(e) => setAdditionalDetails(e.target.value)}
                    placeholder="e.g., Surprise party, keep it quiet! Or 'No balloons please'..."
                    className="w-full h-40 p-5 bg-transparent text-gray-900 placeholder-gray-400 focus:outline-none transition resize-none text-base leading-relaxed"
                  />
                </div>
              </div>
            )}

            {/* Step: Theme Discovery Mode */}
            {getCurrentStepId() === STEP_IDS.THEME_MODE && (
              <div className={getStepAnimationClass(currentStep)}>
                <ThemeDiscoveryWizard
                  eventType={eventType === 'Other' ? customEventType : eventType}
                  ageRange={ageRange}
                  currentInput={themeInput}
                  onInputChange={(val) => setThemeInput(val)}
                  onNext={() => {
                    const val = 'Theme Mode: Surprise Me';
                    setThemeInput(val);
                    themeInputRef.current = val; // Update ref immediately for the timeout
                    setTimeout(() => handleNext(), 50);
                  }}
                  view="selection"
                  onViewChange={(view) => {
                    setThemeMode(view);
                    if (view === 'browse_categories') {
                      const val = 'Theme Mode: Browsing...';
                      setThemeInput(val);
                      themeInputRef.current = val;
                    } else if (view === 'custom') {
                      const val = 'Theme Mode: Custom Concept';
                      setThemeInput(val);
                      themeInputRef.current = val;
                    } else if (view === 'surprise') {
                      const val = 'Surprise me! Create something unique based on the event details.';
                      setThemeInput(val);
                      themeInputRef.current = val;
                    }
                  }}
                />
              </div>
            )}

            {/* Step: Theme Concept Input */}
            {getCurrentStepId() === STEP_IDS.THEME_CONCEPT && (
              <div className={getStepAnimationClass(currentStep)}>
                <ThemeDiscoveryWizard
                  eventType={eventType === 'Other' ? customEventType : eventType}
                  ageRange={ageRange}
                  currentInput={themeInput}
                  onInputChange={(val) => setThemeInput(val)}
                  onNext={() => handleNext()}
                  view="custom"
                  onViewChange={(v) => {}}
                />
              </div>
            )}

            {/* Step: Theme Browse Categories */}
            {getCurrentStepId() === STEP_IDS.THEME_BROWSE_CATS && (
              <div className={getStepAnimationClass(currentStep)}>
                <ThemeDiscoveryWizard
                  eventType={eventType === 'Other' ? customEventType : eventType}
                  ageRange={ageRange}
                  currentInput={themeInput}
                  onInputChange={(val) => setThemeInput(val)}
                  onNext={() => handleNext()}
                  view="browse_categories"
                  onViewChange={(view) => {
                    if (view === 'browse_templates') handleNext();
                  }}
                />
              </div>
            )}

            {/* Step: Theme Browse Templates */}
            {getCurrentStepId() === STEP_IDS.THEME_BROWSE_TEMPLATES && (
              <div className={getStepAnimationClass(currentStep)}>
                <ThemeDiscoveryWizard
                  eventType={eventType === 'Other' ? customEventType : eventType}
                  ageRange={ageRange}
                  currentInput={themeInput}
                  onInputChange={(val) => setThemeInput(val)}
                  onNext={() => handleNext()}
                  view="browse_templates"
                  onViewChange={(v) => {
                    if (v === 'browse_categories') handleBack();
                  }}
                />
              </div>
            )}


            {/* Step: Summary */}
            {getCurrentStepId() === STEP_IDS.SUMMARY && (
              <div className={getStepAnimationClass(currentStep)}>
                <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 shadow-sm relative">
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] lg:items-start gap-5 mb-5">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-stone-100 border border-stone-200 flex items-center justify-center text-lg flex-shrink-0">
                        ✨
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="text-xl sm:text-2xl font-bold text-gray-950 leading-tight m-0">
                            Review your event
                          </h3>
                          {(!isAuthenticated || (user?.email && !["sandheepr89@gmail.com", "sandheep.r.krishnan@gmail.com"].includes(user.email))) && (
                            <span className="text-[10px] bg-amber-50 text-amber-700 font-bold px-2 py-1 rounded-full uppercase tracking-wide border border-amber-100">
                              {isAuthenticated ? 'Free plan' : 'Demo mode'}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 leading-snug m-0">
                          {isThemeBased
                            ? "Confirm the details below before CraftMyParty creates your theme options."
                            : "Confirm the details below before CraftMyParty generates your custom plan."}
                        </p>
                      </div>
                    </div>
                    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-3.5">
                      {showDevBillingSimulator && (
                        <div className="mb-3 rounded-xl border border-dashed border-stone-200 bg-white/80 p-2.5">
                          <label className="block text-[10px] font-bold uppercase tracking-wide text-stone-600 mb-1">
                            Dev billing preview
                          </label>
                          <select
                            value={devBillingMode}
                            onChange={(e) => updateDevBillingMode(e.target.value)}
                            className="w-full h-9 rounded-lg border border-gray-200 bg-white px-2 text-xs font-semibold text-gray-800 outline-none focus:border-primary"
                          >
                            <option value="real">My real account</option>
                            <option value="new_free">New free user</option>
                            <option value="free_used_no_credits">Free used, no credits</option>
                            <option value="has_credit">Free used, has 1 credit</option>
                          </select>
                        </div>
                      )}
                      {hasExistingThemes ? (
                        <>
                          {hasExistingThemes && hasCorePlanInputChanges && (
                            <div className="mb-3 rounded-xl border border-blue-200 bg-blue-50 px-3 py-3">
                              <p className="text-[11px] font-bold uppercase tracking-wide text-blue-700">Update current plan</p>
                              <p className="mt-1 text-xs leading-relaxed text-blue-900">
                                Changing guests, budget, or date affects quantities, timeline, food, and budget suggestions. Re-estimate keeps your selected theme and refreshes the plan.
                              </p>
                              <p className="mt-2 text-[11px] leading-relaxed text-blue-800/90">
                                AI re-estimates are planning aids only. Review prices, availability, dietary needs, and venue rules before buying.
                              </p>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setForceRecalculate(true);
                                  handleSubmit(e);
                                }}
                                className="mt-3 h-10 w-full rounded-xl bg-blue-600 px-4 text-sm font-bold text-white transition hover:bg-blue-700"
                              >
                                Re-estimate this plan
                              </button>
                            </div>
                          )}
                          {!isThemeBased ? (
                            <>
                              <p className="text-xs font-bold uppercase tracking-wide text-gray-500 m-0 mb-1">
                                Plan Ready
                              </p>
                              <p className="text-sm text-gray-600 leading-snug mb-3">
                                This event already has a generated custom plan.
                              </p>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (onNavigateToThemes) onNavigateToThemes();
                                }}
                                className="h-11 px-5 bg-primary text-white font-bold text-sm rounded-xl shadow-sm hover:shadow-md hover:bg-primary-dark transition-all duration-200 flex items-center justify-center gap-2 w-full"
                              >
                                View plan
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (needsPaidFreshSet) {
                                    openBillingOptionsModal('fresh_set', {
                                      closeTarget: { screen: 'input', returnThemeKey: '' }
                                    });
                                  } else {
                                    setShowRegenerateModal(true);
                                  }
                                }}
                                className="mt-2 h-9 px-4 bg-white text-gray-700 border border-gray-200 font-semibold text-xs rounded-xl hover:bg-gray-100 transition w-full"
                              >
                                {needsPaidFreshSet
                                  ? (effectiveSubscriptionActive ? 'See extra credit options' : 'See credit & subscription options')
                                  : 'Generate a fresh plan'}
                              </button>
                            </>
                          ) : (
                            <>
                              <p className="text-xs font-bold uppercase tracking-wide text-gray-500 m-0 mb-1">
                                {focusSelection.length === 1 && focusSelection[0] === 'food' ? 'Menu Concepts Ready' : (focusSelection.length === 1 && focusSelection[0] === 'decor' ? 'Decor Concepts Ready' : 'Themes ready')}
                              </p>
                              <p className="text-sm text-gray-600 leading-snug mb-3">
                                This plan already has {existingThemes.length} generated {focusSelection.length === 1 && focusSelection[0] === 'food' ? 'menu concept' : (focusSelection.length === 1 && focusSelection[0] === 'decor' ? 'decor concept' : 'theme option')}{existingThemes.length === 1 ? '' : 's'}.
                              </p>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (onNavigateToThemes) onNavigateToThemes();
                                }}
                                className="h-11 px-5 bg-primary text-white font-bold text-sm rounded-xl shadow-sm hover:shadow-md hover:bg-primary-dark transition-all duration-200 flex items-center justify-center gap-2 w-full"
                              >
                                {focusSelection.length === 1 && focusSelection[0] === 'food' ? 'View Menu Concepts' : (focusSelection.length === 1 && focusSelection[0] === 'decor' ? 'View Decor Concepts' : (isThemeBased ? 'View Theme Options' : 'View Plan'))}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (needsPaidFreshSet) {
                                    openBillingOptionsModal('fresh_set', {
                                      closeTarget: { screen: 'input', returnThemeKey: '' }
                                    });
                                  } else {
                                    setShowRegenerateModal(true);
                                  }
                                }}
                                className="mt-2 h-9 px-4 bg-white text-gray-700 border border-gray-200 font-semibold text-xs rounded-xl hover:bg-gray-100 transition w-full"
                              >
                                {needsPaidFreshSet
                                  ? (effectiveSubscriptionActive ? 'See extra credit options' : 'See credit & subscription options')
                                  : (focusSelection.length === 1 && focusSelection[0] === 'food' ? 'Generate Fresh Menu Concepts' : (focusSelection.length === 1 && focusSelection[0] === 'decor' ? 'Generate Fresh Decor Concepts' : (isThemeBased ? 'Generate a fresh set' : 'Regenerate plan')))}
                              </button>
                            </>
                          )}
                          {needsPaidFreshSet && (
                            <p className="mt-2 text-[11px] text-gray-500 leading-snug">
                              One planning credit is charged for the fresh {getConceptLabelForIntent(focusSelection, 'concept-lower')} set. The matching event plan is included.
                            </p>
                          )}
                          {billingError && (
                            <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-[11px] font-medium leading-snug text-red-700">
                              {billingError}
                            </p>
                          )}
                        </>
                      ) : needsPaidGeneration ? (
                        <>
                          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 m-0 mb-1">More planning credits</p>
                          <p className="text-sm text-gray-600 leading-snug mb-3">
                            Your free planning credit has been used. Buy credits for extra runs or subscribe for ongoing access with monthly rollover.
                          </p>
                          <div className="grid gap-2">
                            {creditPackOffers.map((pkg) => (
                              <button
                                key={pkg.id}
                                type="button"
                                onClick={() => startCheckout(pkg.id)}
                                disabled={billingLoadingPackage !== null}
                                className="min-h-11 px-3 py-2 rounded-xl bg-white border border-gray-200 hover:border-primary hover:text-primary transition flex items-center justify-between gap-3 text-sm font-semibold disabled:opacity-60"
                              >
                                <span className="text-left">
                                  <span className="block text-gray-900">{pkg.name}</span>
                                  <span className="block text-[11px] font-medium text-gray-500">{getBillingOfferBadge(pkg)}</span>
                                  {getBillingOfferSavings(pkg) && (
                                    <span className="block text-[11px] font-semibold text-emerald-600">{getBillingOfferSavings(pkg)}</span>
                                  )}
                                </span>
                                <span className="text-right">
                                  <span className="block">{billingLoadingPackage === pkg.id ? 'Opening...' : formatBillingOfferPrice(pkg)}</span>
                                  {getBillingOfferFinePrint(pkg) && (
                                    <span className="block text-[10px] font-medium text-gray-400">{getBillingOfferFinePrint(pkg)}</span>
                                  )}
                                </span>
                              </button>
                            ))}
                          </div>
                          {visibleSubscriptionOffers.length > 0 && (
                            <div className="mt-3 grid gap-2">
                              <p className="text-[11px] font-bold uppercase tracking-wide text-stone-600 m-0">Subscriptions</p>
                              {visibleSubscriptionOffers.map((pkg) => (
                                <button
                                  key={pkg.id}
                                  type="button"
                                  onClick={() => startCheckout(pkg.id)}
                                  disabled={billingLoadingPackage !== null}
                                  className="min-h-11 px-3 py-2 rounded-xl bg-stone-50 border border-stone-200 hover:border-zinc-900 hover:bg-white transition flex items-center justify-between gap-3 text-sm font-semibold text-gray-900 disabled:opacity-60"
                                >
                                  <span className="text-left">
                                    <span className="block">{pkg.name}</span>
                                    <span className="block text-[11px] font-medium text-gray-500">{getBillingOfferBadge(pkg)}</span>
                                    {getBillingOfferSavings(pkg) && (
                                      <span className="block text-[11px] font-semibold text-emerald-600">{getBillingOfferSavings(pkg)}</span>
                                    )}
                                  </span>
                                  <span className="text-right">
                                    <span className="block">{billingLoadingPackage === pkg.id ? 'Opening...' : formatBillingOfferPrice(pkg)}</span>
                                    {getBillingOfferFinePrint(pkg) && (
                                      <span className="block text-[10px] font-medium text-gray-400">{getBillingOfferFinePrint(pkg)}</span>
                                    )}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                          {effectiveSubscriptionActive && (
                            <p className="mt-2 text-[11px] text-gray-500 leading-snug">
                              Your subscription is already active. Extra usage is handled with one-time credit packs, while your monthly credits continue to roll over.
                            </p>
                          )}
                          <p className="mt-2 text-[11px] text-gray-500 leading-snug">
                            Prices shown here follow your current event country when regional pricing is configured. Otherwise Stripe can localize one-time checkout pricing automatically.
                          </p>
                          {billingError && <p className="mt-2 text-[11px] text-red-600 text-center">{billingError}</p>}
                        </>
                      ) : isAuthenticated ? (
                        <>
                          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 m-0 mb-1">
                            {isThemeBased ? 'Ready to generate' : 'Ready to generate plan'}
                          </p>
                          <p className="text-sm text-gray-600 leading-snug mb-3">
                            {isThemeBased
                              ? `CraftMyParty will create ${themeGenerationCount} refined theme option${themeGenerationCount === 1 ? '' : 's'} from these details.`
                              : `CraftMyParty will create your custom event plan from these details.`}
                          </p>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              if (isFormValid()) {
                                handleSubmit(e);
                              } else {
                                validateForm();
                              }
                            }}
                            className="h-11 px-5 sm:px-6 bg-primary text-white font-bold text-sm rounded-xl shadow-sm hover:shadow-md hover:bg-primary-dark transition-all duration-200 flex items-center justify-center gap-2 w-full whitespace-normal break-words text-center leading-tight"
                          >
                            {getCtaLabel()}
                          </button>
                          <p className="mt-2 text-[11px] text-gray-500 text-center leading-snug">
                            {isThemeBased
                              ? 'One free generation is included for signed-in users. Extra fresh sets use planning credits.'
                              : 'One free generation is included for signed-in users.'}
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-3">
                            <p className="text-xs font-bold uppercase tracking-wide text-gray-500 m-0">Demo Account</p>
                            <span className="text-[10px] font-semibold px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                              {isThemeBased
                                ? (focusSelection.length === 1 && focusSelection[0] === 'food' ? '2 Concepts Selected' : '2 Themes Selected')
                                : 'Custom Plan'}
                            </span>
                          </div>

                          <div className="space-y-2 mb-4">
                            {/* Demo Tier - Selected */}
                            <div className="w-full relative flex items-center justify-between p-3.5 rounded-xl border text-left transition-all border-emerald-300 bg-emerald-50">
                              <span className="flex-1">
                                <span className="block text-sm font-bold text-gray-900">Demo Tier</span>
                                <span className="block text-[11px] font-medium text-gray-500">
                                  {isThemeBased
                                    ? (focusSelection.length === 1 && focusSelection[0] === 'food' ? '2 Basic Menu Concepts' : '2 Basic Theme Ideas')
                                    : 'Basic Custom Plan'}
                                </span>
                              </span>
                              <span className="text-right">
                                <span className="block font-bold text-gray-900">Free</span>
                                <span className="block text-[10px] font-medium text-emerald-600">Current</span>
                              </span>
                            </div>

                            {/* Full Access - Locked */}
                            <button
                              type="button"
                              onClick={() => setShowDemoUpgradeModal(true)}
                              className="w-full relative flex items-center justify-between p-3.5 rounded-xl border text-left transition-all border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 group"
                            >
                              <span className="flex-1 pr-2">
                                <span className="block text-sm font-bold text-gray-900 group-hover:text-zinc-900 transition-colors flex items-center gap-1">
                                  Full Access
                                  <svg className="w-3.5 h-3.5 text-zinc-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  </svg>
                                </span>
                                <span className="block text-[11px] font-medium text-gray-500 leading-tight">
                                  {isThemeBased
                                    ? (focusSelection.length === 1 && focusSelection[0] === 'food'
                                      ? '4 Concepts, Save & Share, Full Planning Tools'
                                      : '4 Themes, Save & Share, Full Planning Tools')
                                    : 'Full Custom Plan, Save & Share, Full Planning Tools'}
                                </span>
                              </span>
                              <span className="text-right shrink-0">
                                <span className="block text-[11px] font-bold text-zinc-900 bg-stone-100 px-2 py-1 rounded-md group-hover:bg-stone-200 transition-colors">
                                  Sign Up Free
                                </span>
                              </span>
                            </button>
                          </div>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              if (isFormValid()) {
                                handleSubmit(e);
                              } else {
                                validateForm();
                              }
                            }}
                            className="h-11 px-5 sm:px-6 bg-primary text-white font-bold text-sm rounded-xl shadow-sm hover:shadow-md hover:bg-primary-dark transition-all duration-200 flex items-center justify-center gap-2 w-full"
                          >
                            {isThemeBased ? 'Generate themes' : 'Generate custom plan'}
                          </button>
                          <p className="mt-2 text-[11px] text-gray-500 text-center leading-snug">
                            {isThemeBased
                              ? 'Demo creates 2 theme options. Sign up to unlock 4.'
                              : 'Demo creates a basic custom plan. Sign up to unlock full planning tools.'}
                          </p>
                        </>
                      )}
                    </div>
                  </div>


                  <div className="space-y-4">
                    {activeMacroSteps.map((macro, macroIdx) => {
                      if (macro.id === 'summary') return null;

                      // Icons for summary items
                      const icons = {
                        event_type: '🎈', event_details: '👋', logistics: '💰', age_range: '👥',
                        theme: '✨', venue: '🏠', time: '🌙', food: '🍕', dietary: '🌿', additional: '📝'
                      };

                      return (
                        <div key={macro.id} className={`flex items-start justify-between gap-3 p-4 bg-gray-50 rounded-xl border transition-all group sm:items-center sm:gap-4 ${
                          lastEditedMacroId === macro.id ? 'border-green-400 ring-2 ring-green-400/20 shadow-green-100 bg-green-50' : 'border-gray-200 hover:bg-white hover:shadow-sm'
                        }`}>
                          <div className="flex items-start gap-3 flex-1 min-w-0 sm:items-center sm:gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl shadow-sm flex-shrink-0 group-hover:scale-105 transition-transform ${
                              lastEditedMacroId === macro.id ? 'bg-green-100' : 'bg-white'
                            }`}>
                              {icons[macro.id] || '📝'}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-0.5">
                                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-0 leading-tight">Step {macroIdx + 1}: {macro.title}</p>
                                {lastEditedMacroId === macro.id && (
                                  <span className="text-[9px] bg-green-100 text-green-700 font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wider animate-pulse">Updated</span>
                                )}
                              </div>
                              <div className="text-sm font-semibold text-gray-900 break-words leading-snug">
                                {macro.id === 'event_type' && (eventType === 'Other' ? customEventType : eventType)}
                                {macro.id === 'event_details' && (eventSpecifics.honoreeRelation || 'Event Details')}
                                {macro.id === 'logistics' && `${guestCount} Guests • ${budget ? formatCurrency(budget, country) : 'No budget'}`}
                                {macro.id === 'age_range' && (ageRange.length > 0 ? ageRange.join(', ') : 'All Ages')}
                                {macro.id === 'theme' && (themeInput.includes('Theme Mode:') ? 'AI Selecting Theme...' : themeInput || 'AI Guided')}
                                {macro.id === 'venue' && (venue || 'Flexible')}
                                {macro.id === 'time' && (timeOfDay.length > 0 ? timeOfDay.join(', ') : 'Flexible')}
                                {macro.id === 'food' && (
                                  <div className="flex flex-col">
                                    {foodSource.length > 0 && (
                                      <span>{foodSource.map(id => FOOD_SOURCE_LABELS[id] || capitalize(id)).join(', ')}</span>
                                    )}
                                    {cuisineTypes.length > 0 && (
                                      <span className="text-[10px] text-stone-500 font-medium mt-0.5">
                                        Cuisines: {cuisineTypes.map(id => CUISINE_LABELS[id] || capitalize(id)).join(', ')}
                                      </span>
                                    )}
                                    {foodSource.length === 0 && cuisineTypes.length === 0 && <span>Food TBD</span>}
                                  </div>
                                )}
                                {macro.id === 'dietary' && (dietaryRestrictions.length > 0 && !dietaryRestrictions.includes('none') ? dietaryRestrictions.map(r => capitalize(r)).join(', ') : 'No restrictions')}
                                {macro.id === 'additional' && (additionalDetails ? additionalDetails : 'No extra notes')}
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const firstSubStep = macro.activeSubSteps[0];
                              const idx = activeStepIds.indexOf(firstSubStep);
                              if (idx !== -1) {
                                // Capture snapshot of current data before editing
                                setEditSnapshot(JSON.stringify(getFormSnapshot()));

	                                setReturnToSummaryMode(true);
	                                setLastEditedMacroId(macro.id); // Set as pending, we'll verify on return
	                                setStepDirection('backward');
	                                setCurrentStep(idx + 1);
                                scrollWizardToTop('auto');

                              }
                            }}
                            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 transition-colors hover:border-stone-300 hover:bg-stone-50 hover:text-zinc-900"
                            aria-label={`Edit ${macro.title}`}
                            title={`Edit ${macro.title}`}
                          >
                            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                              <path d="M11.667 4.167 15.833 8.333M5 15h3.526c.486 0 .729 0 .957-.055a2.5 2.5 0 0 0 .86-.363c.2-.123.372-.295.716-.64l4.775-4.775a1.768 1.768 0 0 0 0-2.5l-1.5-1.5a1.768 1.768 0 0 0-2.5 0L7.058 9.942c-.345.345-.517.517-.64.716-.155.251-.278.539-.363.86C6 11.746 6 11.989 6 12.475V16Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>

                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6 text-center text-xs text-gray-500">
                    Use Edit to adjust any section, then generate your themes.
                  </div>
                </div>
              </div>
            )}



          </form>
        </div >
      </div >

      {/* Bottom navigation - outside card, fixed to screen bottom (all screen sizes) */}
      {renderNavigationButtons()}
    </div>
    )}

      {/* Scroll-to-top: only visible on the summary/review step, positioned
          above the sticky mobile action dock so it never overlaps buttons */}
      {!billingOverlayOnly && currentStep === totalSteps && (
        <ScrollToTopButton
          targetSelector="main[data-app-main-scroll='true']"
          threshold={300}
          className={
            'fixed right-4 z-40 flex items-center justify-center w-12 h-12 rounded-full text-white transition-all duration-300 shadow-lg border focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:ring-offset-2 ' +
            'bottom-48 md:bottom-24 md:right-6 ' +
            'bg-zinc-900 hover:bg-zinc-800 border-zinc-700/20 hover:scale-110 active:scale-95'
          }
        />
      )}

      <BillingOptionsModal
        isOpen={showBillingOptionsModal}
        portalMounted={portalMounted}
        billingOverlayOnly={billingOverlayOnly}
        title={getBillingModalTitle()}
        description={getBillingModalDescription()}
        showSubscriptionTabs={true}
        billingModalTab={billingModalTab}
        onBillingTabChange={handleBillingTabChange}
        activeBillingOffers={activeBillingOffers}
        selectedBillingPackageId={selectedBillingPackageId}
        onSelectOffer={setSelectedBillingPackageId}
        getOfferTitle={getBillingOfferTitle}
        formatOfferPrice={formatBillingOfferPrice}
        getOfferBadge={getBillingOfferBadge}
        getOfferSavings={getBillingOfferSavings}
        getOfferFinePrint={getBillingOfferFinePrint}
        isRecommendedOffer={isRecommendedBillingOffer}
        creditPackOffers={creditPackOffers}
        selectedOffer={selectedBillingOffer}
        billingLoadingPackageId={billingLoadingPackage}
        billingError={billingError}
        isLaunching={isBillingLaunching}
        onClose={closeBillingOptionsModal}
        onContinue={() => selectedBillingOffer && startCheckout(selectedBillingOffer.id)}
        effectiveSubscriptionActive={effectiveSubscriptionActive}
        onManageSubscription={handleManageSubscription}
      />

      {!billingOverlayOnly && (
      <>
      <GlassModal
        isOpen={showDemoUpgradeModal}
        onClose={() => setShowDemoUpgradeModal(false)}
        title="Generate 4 themes?"
        footer={(
          <div className="grid grid-cols-1 gap-2">
            <button
              type="button"
              onClick={() => {
                setShowDemoUpgradeModal(false);
                onRequestAuth?.('signup');
              }}
              className="h-11 rounded-xl bg-primary px-4 text-sm font-bold text-white shadow-sm transition hover:bg-primary-dark"
            >
              Sign up for 4 themes
            </button>
            <button
              type="button"
              onClick={() => {
                setShowDemoUpgradeModal(false);
                onRequestAuth?.('signin');
              }}
              className="h-11 rounded-xl border border-gray-200 bg-white px-4 text-sm font-bold text-gray-800 transition hover:bg-gray-50"
            >
              I already have an account
            </button>
            <button
              type="button"
              onClick={() => setShowDemoUpgradeModal(false)}
              className="h-10 rounded-xl px-4 text-sm font-semibold text-gray-500 transition hover:text-gray-800"
            >
              Continue demo with 2 themes
            </button>
          </div>
        )}
      >
        <p className="text-sm leading-relaxed text-gray-600">
          Demo mode creates 2 theme options. A free account unlocks 4 refined themes, saving, and the full planning workflow.
        </p>
      </GlassModal>

      {/* Fresh set modal — mirrors the visual design used on the themes
          screen (ThemeCarousel) so users see a consistent dialog whether
          they trigger it from the summary or from the carousel. Uses
          createPortal so the modal escapes the sidebar stacking context. */}
      {showRegenerateModal && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[100000] flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm sm:p-6"
          onClick={closeFreshSetModal}
        >
          <div
            className="flex max-h-[min(92vh,820px)] w-full max-w-xl flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-5 sm:px-6">
              <div>
                <h3 className="text-xl font-extrabold tracking-tight text-gray-950">
                  {getConceptLabelForIntent(focusSelection, 'concepts') === 'Themes' ? 'Generate a fresh theme set' : 'Generate Fresh Menu Concepts'}
                </h3>
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  Create a new set from your current event details. Your existing {getConceptLabelForIntent(focusSelection, 'concepts-lower')} stay available until the new set is ready.
                </p>
              </div>
              <button
                type="button"
                onClick={closeFreshSetModal}
                className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-3 text-sm font-bold text-gray-600 transition hover:bg-gray-50 hover:text-gray-950"
              >
                <span aria-hidden="true">x</span>
                Close
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
              {hasExistingThemes && (
                <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-4">
                  <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-primary">What should happen to existing {getConceptLabelForIntent(focusSelection, 'concepts-lower')}?</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {[
                      { id: 'append', title: 'Keep existing', body: `Add a new set alongside your current ${getConceptLabelForIntent(focusSelection, 'concepts-lower')}.` },
                      { id: 'replace', title: 'Start fresh', body: `Replace the current ${getConceptLabelForIntent(focusSelection, 'concepts-lower')} with the new set.` }
                    ].map((option) => {
                      const isActive = regenerateMode === option.id;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setRegenerateMode(option.id)}
                          className={`rounded-2xl border p-3 text-left transition ${
                            isActive
                              ? 'border-primary bg-white text-primary shadow-sm ring-2 ring-primary'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-primary/40 hover:bg-primary/5'
                          }`}
                        >
                          <span className="block text-sm font-extrabold">{option.title}</span>
                          <span className="mt-1 block text-xs leading-5 text-gray-500">{option.body}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-[11px] leading-4 text-gray-500">
                    Tip: you can delete any older set later from the set pills above the {getConceptLabelForIntent(focusSelection, 'concept-lower')} card.
                  </p>
                </div>
              )}

              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-gray-500">Event summary</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="block min-w-0">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Event type</span>
                    <p className="mt-1 rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm font-semibold text-gray-900">
                      {(eventType === 'Other' ? customEventType : eventType) || '—'}
                    </p>
                  </div>
                  <div className="block min-w-0">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Guests</span>
                    <div className="relative mt-1">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-gray-400" aria-hidden="true">👥</span>
                      <p className="h-[2.75rem] rounded-xl border border-gray-200 bg-white pl-10 pr-3 pt-[0.65rem] text-sm font-semibold text-gray-900">
                        {guestCount || '—'}
                      </p>
                    </div>
                  </div>
                  <div className="block min-w-0">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Budget</span>
                    <div className="relative mt-1">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base font-bold text-gray-400" aria-hidden="true">{getCurrencySymbol(LAUNCH_COUNTRY)}</span>
                      <p className="h-[2.75rem] rounded-xl border border-gray-200 bg-white pl-8 pr-3 pt-[0.65rem] text-sm font-semibold text-gray-900">
                        {budget || '—'}
                      </p>
                    </div>
                  </div>
                  <div className="block min-w-0">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Date</span>
                    <p className="mt-1 rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm font-semibold text-gray-900">
                      {date || '—'}
                    </p>
                  </div>
                </div>
                <p className="mt-3 text-[11px] leading-4 text-gray-500">
                  Close this dialog and edit any field above to change what the generator uses.
                </p>
              </div>

              {isThemeBased ? (
                <div>
                  <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-gray-500">{getConceptLabelForIntent(focusSelection, 'concept')} options</p>
                  <div className="grid grid-cols-2 gap-3">
                    {[2, 4].map((count) => {
                      const isLocked = !isAuthenticated && count === 4;
                      const isActive = isAuthenticated ? themeGenerationCount === count : count === 2;
                      return (
                        <button
                          key={count}
                          type="button"
                          disabled={isLocked}
                          onClick={() => {
                            if (!isLocked) setThemeGenerationCount(count);
                          }}
                          className={`min-h-14 rounded-2xl border px-4 text-base font-extrabold transition ${
                            isActive
                              ? 'border-primary bg-primary text-white shadow-md'
                              : isLocked
                                ? 'border-gray-200 bg-white text-gray-400 cursor-not-allowed'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-primary/40 hover:bg-primary/5'
                          }`}
                        >
                          {count} {getConceptLabelForIntent(focusSelection, 'concepts')}{isLocked ? ' · sign up' : ''}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-3 rounded-xl bg-gray-50 border border-gray-100 p-3 text-xs text-gray-600 leading-relaxed">
                    {isAuthenticated
                      ? `CraftMyParty will create ${themeGenerationCount} fresh refined ${getConceptLabelForIntent(focusSelection, 'concepts-lower')} from the current event details. ${effectiveUsedFreeGeneration ? 'This uses 1 planning credit and reserves the included matching plan.' : 'This uses your free theme-generation run and includes the matching plan.'}`
                      : `Demo mode can regenerate 2 ${getConceptLabelForIntent(focusSelection, 'concepts-lower')}. Sign up to unlock 4-${getConceptLabelForIntent(focusSelection, 'concept-lower')} sets and planning.`}
                  </div>
                </div>
              ) : (
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 text-xs text-gray-600 leading-relaxed">
                  {isAuthenticated
                    ? 'One planning credit is charged to regenerate your custom plan.'
                    : 'Demo mode will regenerate your custom plan. Sign up to unlock full planning workflows.'}
                </div>
              )}
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-gray-100 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
              <button
                type="button"
                onClick={closeFreshSetModal}
                className="h-12 rounded-2xl bg-gray-100 px-5 text-sm font-bold text-gray-800 transition hover:bg-gray-200"
              >
                Not now
              </button>
              <button
                type="button"
                disabled={!isFormValid()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowRegenerateModal(false);
                  if (!isFormValid()) {
                    validateForm();
                    return;
                  }
                  const shouldAppend = hasExistingThemes && regenerateMode === 'append';
                  const setId = shouldAppend ? `theme-set-${Date.now()}-${Math.random().toString(36).slice(2)}` : '';
                  const setLabel = shouldAppend
                    ? `Set ${(existingThemes?.filter((t) => t?.theme_set_id).map((t) => t.theme_set_id).reduce((acc, id) => acc.add(id), new Set()).size || 0) + 2}`
                    : '';
                  if (onRegenerateThemes) {
                    onRegenerateThemes({
                      ...getFormSnapshot(),
                      __appendThemes: shouldAppend,
                      __themeSetId: setId,
                      __themeSetLabel: setLabel,
                      __openFreshSetWorkflow: false
                    });
                  } else {
                    handleSubmit(e);
                  }
                  setRegenerateMode('append');
                }}
                className="h-12 rounded-2xl bg-primary px-6 text-sm font-extrabold text-white shadow-sm transition hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isThemeBased ? `Generate ${isAuthenticated ? themeGenerationCount : 2} ${getConceptLabelForIntent(focusSelection, 'concepts')}` : 'Regenerate plan'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {showRecalculatePrompt && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-3xl border border-gray-200 bg-white p-5 shadow-2xl">
            <h3 className="text-xl font-extrabold text-gray-950">Re-estimate your plan?</h3>
            <p className="mt-2 text-sm leading-6 text-gray-600">
              You changed guests, budget, or date. That can affect shopping quantities, timeline, food, and budget suggestions for your selected theme.
            </p>
            <p className="mt-3 text-xs leading-relaxed text-amber-800">
              Re-estimating is a planning aid only. Review current prices, availability, dietary needs, and venue rules before purchasing.
            </p>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowRecalculatePrompt(false)}
                className="h-11 rounded-2xl bg-gray-100 px-5 text-sm font-bold text-gray-800 transition hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowRecalculatePrompt(false);
                  if (onNavigateToThemes) onNavigateToThemes();
                }}
                className="h-11 rounded-2xl bg-white px-5 text-sm font-bold text-gray-700 ring-1 ring-gray-200 transition hover:bg-gray-50"
              >
                Save details only
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowRecalculatePrompt(false);
                  setForceRecalculate(true);
                  handleSubmit();
                }}
                className="h-11 rounded-2xl bg-primary px-5 text-sm font-extrabold text-white shadow-sm transition hover:bg-primary-dark"
              >
                Re-estimate plan
              </button>
            </div>
          </div>
        </div>
      )}
      {/* AI Express Fast-Track Modal */}
      <ExpressPlanModal
        isOpen={showExpressModal}
        onClose={() => setShowExpressModal(false)}
        initialCountry={country}
        initialLocation={zipCode}
        onApplyPlan={(parsedData) => {
          if (!parsedData) return;
          if (parsedData.event_type) {
            setEventType(parsedData.event_type);
            if (parsedData.custom_event_type) setCustomEventType(parsedData.custom_event_type);
          }
          if (Array.isArray(parsedData.focus) && parsedData.focus.length > 0) {
            setFocusSelection(parsedData.focus);
          }
          if (parsedData.event_specifics) {
            setEventSpecifics(prev => ({
              ...prev,
              ...parsedData.event_specifics
            }));
          }
          if (typeof parsedData.guest_count === 'number') {
            setGuestCount(String(parsedData.guest_count));
          }
          if (typeof parsedData.budget === 'number') {
            setBudget(parsedData.budget);
          }
          if (parsedData.country) {
            setCountry(parsedData.country);
          }
          if (parsedData.zip_code) {
            setZipCode(parsedData.zip_code);
          }
          if (parsedData.venue) {
            setVenue(parsedData.venue);
          }
          if (parsedData.venue_type) {
            setVenueType(parsedData.venue_type);
            if (parsedData.custom_venue_type) setCustomVenueType(parsedData.custom_venue_type);
          }
          if (Array.isArray(parsedData.time_of_day) && parsedData.time_of_day.length > 0) {
            setTimeOfDay(parsedData.time_of_day);
          }
          if (Array.isArray(parsedData.meals) && parsedData.meals.length > 0) {
            setSelectedMeals(parsedData.meals);
            const struct = {};
            parsedData.meals.forEach(m => { struct[m] = 1; });
            setFoodMenuStructure(struct);
          }
          if (Array.isArray(parsedData.dietary_restrictions)) {
            setDietaryRestrictions(parsedData.dietary_restrictions);
          }
          if (parsedData.theme_input) {
            setThemeInput(parsedData.theme_input);
            themeInputRef.current = parsedData.theme_input;
          }
          if (parsedData.additional_details) {
            setAdditionalDetails(parsedData.additional_details);
          }
          setCurrentStep(totalSteps);
          scrollWizardToTop('auto');
        }}
        onGenerateDirectly={(parsedData) => {
          if (!parsedData) return;
          if (parsedData.event_type) setEventType(parsedData.event_type);
          if (typeof parsedData.budget === 'number') setBudget(parsedData.budget);
          if (typeof parsedData.guest_count === 'number') setGuestCount(String(parsedData.guest_count));
          if (parsedData.country) setCountry(parsedData.country);
          if (parsedData.zip_code) setZipCode(parsedData.zip_code);
          
          const snapshot = getFormSnapshot();
          const directPayload = {
            ...snapshot,
            event_type: parsedData.event_type || snapshot.event_type,
            custom_event_type: parsedData.custom_event_type || snapshot.custom_event_type,
            guest_count: parsedData.guest_count || snapshot.guest_count,
            budget: parsedData.budget || snapshot.budget,
            country: parsedData.country || snapshot.country || country || 'US',
            zip_code: parsedData.zip_code || snapshot.zip_code,
            venue: parsedData.venue || snapshot.venue,
            venue_type: parsedData.venue_type || snapshot.venue_type,
            time_of_day: parsedData.time_of_day || snapshot.time_of_day,
            meals: parsedData.meals || snapshot.meals,
            dietary_restrictions: parsedData.dietary_restrictions || snapshot.dietary_restrictions,
            theme_input: parsedData.theme_input || snapshot.theme_input,
            additional_details: parsedData.additional_details || snapshot.additional_details,
            budget_tolerance_percent: 10
          };
          onSubmit(directPayload);
        }}
      />
      </>
      )}
    </>
  );
});

EventInputScreen.displayName = 'EventInputScreen';

export default EventInputScreen;
